// FullScreenPages.jsx - iPhone-stijl full-screen pagina's voor elke categorie
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimation } from 'framer-motion';
import axios from 'axios';

// Re-export gemigreerde pagina's vanuit hun eigen bestanden
export { default as DagbestedingPage } from '../../pages/dashboard/DagbestedingPage';
export { default as EetmomentenPage } from '../../pages/dashboard/EetmomentenPage';
export { default as VoiceRapportagePage } from '../../pages/dashboard/VoiceRapportagePage';
export { default as RapporteerHelperPage } from '../../pages/dashboard/RapporteerHelperPage';
export { default as RapportageControlePage } from '../../pages/dashboard/RapportageControlePage';

// Swipe-to-go-back wrapper component
const SwipeBackWrapper = ({ children, onBack }) => {
  const x = useMotionValue(0);
  const controls = useAnimation();
  const [isDragging, setIsDragging] = useState(false);
  
  // Opacity based on drag position
  const opacity = useTransform(x, [0, 150], [1, 0.5]);
  
  const handleDragEnd = (event, info) => {
    setIsDragging(false);
    // If dragged more than 100px to the right with enough velocity, go back
    if (info.offset.x > 100 || (info.offset.x > 50 && info.velocity.x > 500)) {
      controls.start({ x: window.innerWidth, opacity: 0 }).then(() => {
        onBack && onBack();
      });
    } else {
      controls.start({ x: 0, opacity: 1 });
    }
  };

  return (
    <motion.div
      style={{ x, opacity }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={{ left: 0, right: 0.5 }}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={handleDragEnd}
      animate={controls}
      className="min-h-screen"
    >
      {/* Swipe indicator on left edge */}
      <div className="fixed left-0 top-1/2 -translate-y-1/2 w-1 h-20 bg-slate-300/50 rounded-r-full z-50 pointer-events-none" />
      {children}
    </motion.div>
  );
};

import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  Clock,
  User,
  Users,
  Edit2,
  Save,
  Utensils,
  ShoppingCart,
  Check,
  CheckCircle,
  CheckSquare,
  Star,
  PartyPopper,
  ClipboardList,
  FileText,
  Brush,
  Loader2,
  MapPin,
  AlertTriangle,
  Trash2,
  Wifi,
  Copy,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Heart,
  Activity,
  Thermometer,
  Scale,
  Droplets,
  Minus,
  History,
  Phone,
  Mail,
  Home,
  Search,
  Users2,
  Sun,
  Moon,
  Shield,
  Edit,
  X,
  Sparkles,
  Pill,
  CalendarDays,
  ListTodo,
  MessageSquare,
  Briefcase,
  Gift,
  Dumbbell,
  Coffee,
  TreePine,
  Mic,
  MicOff,
  Send,
  Volume2,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// PREMIUM PAGE WRAPPER - iPhone-stijl container
// ============================================
const PageWrapper = ({ children, title, subtitle, onBack, gradient = 'from-slate-50 via-purple-50/30 to-pink-50/20', icon: Icon, iconGradient = 'from-violet-500 to-purple-600' }) => (
  <SwipeBackWrapper onBack={onBack}>
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`min-h-screen bg-gradient-to-br ${gradient}`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-72 h-72 bg-purple-300/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-pink-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-violet-300/15 rounded-full blur-3xl" />
      </div>
      
      {/* Sticky Header with safe area */}
      <div className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/50 shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="p-2.5 rounded-xl bg-white/80 hover:bg-white shadow-sm border border-slate-200/50 transition-all"
              data-testid="back-button"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </motion.button>
            
            <div className="flex items-center gap-3 flex-1">
              {Icon && (
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${iconGradient} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold text-slate-800">{title}</h1>
                {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-6">
        {children}
      </div>
    </motion.div>
  </SwipeBackWrapper>
);

// Premium Card Component
const PremiumCard = ({ children, className = '', hover = true, padding = 'p-4' }) => (
  <motion.div
    whileHover={hover ? { y: -2, scale: 1.01 } : {}}
    className={`bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 ${padding} ${className} ${hover ? 'hover:shadow-md transition-all duration-300' : ''}`}
  >
    {children}
  </motion.div>
);

// Premium List Item
const PremiumListItem = ({ children, onClick, className = '' }) => (
  <motion.div
    whileHover={{ x: 4 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`p-4 bg-white/60 hover:bg-white/90 rounded-xl border border-slate-100 cursor-pointer transition-all ${className}`}
  >
    {children}
  </motion.div>
);

// Premium Badge
const PremiumBadge = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-slate-100 text-slate-700',
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    danger: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${variants[variant]}`}>
      {children}
    </span>
  );
};

// ============================================
// ROBUST API HELPER - Centrale error handling met retry
// ============================================
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const apiRequest = async (method, endpoint, data = null, options = {}, retries = 2) => {
  const token = localStorage.getItem('token');
  const config = {
    method,
    url: `${API_URL}${endpoint}`,
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    timeout: 15000, // 15 seconden timeout
    ...options
  };
  
  if (data && (method === 'post' || method === 'put')) {
    config.data = data;
  }
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await axios(config);
      return { success: true, data: response.data };
    } catch (error) {
      // Don't retry on client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        const message = error.response?.data?.detail || 
                        error.response?.data?.message || 
                        error.message || 
                        'Er ging iets mis';
        
        if (error.response?.status === 401) {
          toast.error('Sessie verlopen - log opnieuw in');
          localStorage.removeItem('token');
          window.location.href = '/';
          return { success: false, error: 'Unauthorized' };
        }
        
        if (error.response?.status === 403) {
          toast.error('Geen toegang tot deze functie');
          return { success: false, error: 'Forbidden' };
        }
        
        if (error.response?.status === 404) {
          return { success: false, error: 'Niet gevonden' };
        }
        
        return { success: false, error: message };
      }
      
      // Retry on server errors (5xx) or network issues
      if (attempt < retries) {
        await sleep(1000 * (attempt + 1)); // Exponential backoff: 1s, 2s
        continue;
      }
      
      // Final attempt failed
      if (error.response?.status >= 500) {
        toast.error('Server tijdelijk niet beschikbaar');
        return { success: false, error: 'Server error' };
      }
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Verbinding timeout - probeer het opnieuw');
        return { success: false, error: 'Timeout' };
      }
      
      toast.error('Netwerkfout - controleer je internetverbinding');
      return { success: false, error: error.message || 'Network error' };
    }
  }
};

// Simplified API methods
const api = {
  get: (endpoint, retries = 2) => apiRequest('get', endpoint, null, {}, retries),
  post: (endpoint, data, retries = 1) => apiRequest('post', endpoint, data, {}, retries),
  put: (endpoint, data, retries = 1) => apiRequest('put', endpoint, data, {}, retries),
  delete: (endpoint, retries = 1) => apiRequest('delete', endpoint, null, {}, retries)
};

// Safe data access helper - voorkomt "Cannot read property of undefined" errors
const safeGet = (obj, path, defaultValue = null) => {
  try {
    return path.split('.').reduce((o, k) => (o || {})[k], obj) ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

// ============================================
// SHARED COMPONENTS
// ============================================

const PageHeader = ({ title, subtitle, onBack, rightAction, icon: Icon, iconGradient = 'from-violet-500 to-purple-600' }) => (
  <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/50 shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="p-2.5 rounded-xl bg-white/80 hover:bg-white shadow-sm border border-slate-200/50 transition-all"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5 text-slate-700" />
          </motion.button>
          
          <div className="flex items-center gap-3">
            {Icon && (
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${iconGradient} flex items-center justify-center shadow-lg`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-slate-800">{title}</h1>
              {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            </div>
          </div>
        </div>
        {rightAction}
      </div>
    </div>
  </header>
);

const EmptyState = ({ icon: Icon, title, subtitle, action }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col items-center justify-center py-16 px-6"
  >
    <div className="relative mb-6">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl blur-xl opacity-30" />
      <div className="relative w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center shadow-lg border border-slate-200/50">
        <Icon className="w-12 h-12 text-slate-400" />
      </div>
    </div>
    <h3 className="text-xl font-bold text-slate-800 mb-2">{title}</h3>
    <p className="text-sm text-slate-500 text-center mb-6 max-w-xs">{subtitle}</p>
    {action}
  </motion.div>
);

const LoadingSpinner = ({ color = 'purple' }) => (
  <div className="flex flex-col items-center justify-center py-16">
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full blur-xl opacity-30 animate-pulse" />
      <div className="relative w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center">
        <Loader2 className={`w-8 h-8 animate-spin text-${color}-500`} />
      </div>
    </div>
    <p className="mt-4 text-sm text-slate-500 font-medium">Laden...</p>
  </div>
);

// ============================================
// PB GESPREKKEN PAGE
// ============================================
export const PBGesprekkenPage = ({ onBack, user, isAdmin, selectedLocation }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGesprek, setEditingGesprek] = useState(null);
  const [form, setForm] = useState({ client: '', begeleider: '', datum: '', tijd: '', type: 'Maandelijks PB gesprek' });
  const [useFallback, setUseFallback] = useState(false);
  
  // Koppelingen state
  const [showKoppelingModal, setShowKoppelingModal] = useState(false);
  const [editingKoppeling, setEditingKoppeling] = useState(null);
  const [koppelingForm, setKoppelingForm] = useState({ client: '', begeleider: '' });
  
  // Fallback PB gesprekken data
  const FALLBACK_PB = {
    'groep-zwaluw': {
      pb_gesprekken: [
        { id: '1', client: 'Yassin', begeleider: 'Serkan', datum: '2026-02-15', tijd: '14:00', type: 'Maandelijks PB gesprek' },
        { id: '2', client: 'Sietse', begeleider: 'Karim', datum: '2026-02-16', tijd: '10:00', type: 'Maandelijks PB gesprek' },
        { id: '3', client: 'Richard', begeleider: 'Redouan', datum: '2026-02-17', tijd: '11:00', type: 'Maandelijks PB gesprek' }
      ],
      pb_overzicht: {
        alle_koppelingen: [
          { begeleider: 'Serkan', client: 'Yassin & Ricardo' },
          { begeleider: 'Karim', client: 'Sietse, Axcel & Mahad' },
          { begeleider: 'Redouan', client: 'Richard' }
        ]
      }
    },
    'groep-adelaars': {
      pb_gesprekken: [
        { id: '1', client: 'Rinaldo', begeleider: 'Ilias', datum: '2026-02-15', tijd: '14:00', type: 'Maandelijks PB gesprek' },
        { id: '2', client: 'Mike', begeleider: 'Redouan', datum: '2026-02-16', tijd: '10:00', type: 'Maandelijks PB gesprek' }
      ],
      pb_overzicht: {
        alle_koppelingen: [
          { begeleider: 'Ilias', client: 'Rinaldo & Faraah' },
          { begeleider: 'Redouan', client: 'Mike & Joey' },
          { begeleider: 'Nassim', client: 'Melvin' }
        ]
      }
    }
  };

  const fetchData = async () => {
    const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
    try {
      const token = localStorage.getItem('token');
      const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
      const response = await axios.get(`${API_URL}/api/begeleider/overzicht${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      if (response.data?.pb_overzicht || response.data?.pb_gesprekken) {
        setData(response.data);
        setUseFallback(false);
      } else {
        console.warn('API returned empty PB data, using fallback');
        setUseFallback(true);
      }
    } catch (error) {
      console.error('API Error:', error.response?.status, error.message);
      setUseFallback(true);
      if (error.response?.status === 401) {
        toast.error('Sessie verlopen, log opnieuw in');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedLocation]);
  
  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
  const fallbackData = FALLBACK_PB[locationSlug] || FALLBACK_PB['groep-zwaluw'];

  // === GESPREKKEN FUNCTIES ===
  const openNewGesprek = () => {
    setEditingGesprek(null);
    setForm({ client: '', begeleider: '', datum: '', tijd: '', type: 'Maandelijks PB gesprek' });
    setShowModal(true);
  };

  const openEditGesprek = (gesprek) => {
    setEditingGesprek(gesprek);
    setForm({
      client: gesprek.client || '',
      begeleider: gesprek.begeleider || gesprek.pb_er || '',
      datum: gesprek.datum || '',
      tijd: gesprek.tijd || '',
      type: gesprek.type || 'Maandelijks PB gesprek'
    });
    setShowModal(true);
  };

  const saveGesprek = async () => {
    if (!form.client || !form.begeleider || !form.datum || !form.tijd) {
      toast.error('Vul alle velden in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingGesprek) {
        await axios.put(`${API_URL}/api/admin/pb-gesprekken/${editingGesprek.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Gesprek bijgewerkt');
      } else {
        await axios.post(`${API_URL}/api/admin/pb-gesprekken`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Gesprek ingepland');
      }
      setShowModal(false);
      setEditingGesprek(null);
      setForm({ client: '', begeleider: '', datum: '', tijd: '', type: 'Maandelijks PB gesprek' });
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan: ' + (error.response?.data?.detail || 'Onbekende fout'));
    }
  };

  const deleteGesprek = async (gesprekId) => {
    if (!window.confirm('Weet je zeker dat je dit gesprek wilt verwijderen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/pb-gesprekken/${gesprekId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gesprek verwijderd');
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen: ' + (error.response?.data?.detail || 'Onbekende fout'));
    }
  };

  // === KOPPELINGEN FUNCTIES ===
  const openNewKoppeling = () => {
    setEditingKoppeling(null);
    setKoppelingForm({ client: '', begeleider: '' });
    setShowKoppelingModal(true);
  };

  const openEditKoppeling = (koppeling) => {
    setEditingKoppeling(koppeling);
    setKoppelingForm({
      client: koppeling.client || '',
      begeleider: koppeling.begeleider || ''
    });
    setShowKoppelingModal(true);
  };

  const saveKoppeling = async () => {
    if (!koppelingForm.client || !koppelingForm.begeleider) {
      toast.error('Vul alle velden in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingKoppeling) {
        // Update bestaande koppeling
        await axios.put(`${API_URL}/api/admin/pb-koppelingen`, {
          oude_begeleider: editingKoppeling.begeleider,
          oude_client: editingKoppeling.client,
          nieuwe_begeleider: koppelingForm.begeleider,
          nieuwe_client: koppelingForm.client
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Koppeling bijgewerkt');
      } else {
        // Nieuwe koppeling
        await axios.post(`${API_URL}/api/admin/pb-koppelingen`, koppelingForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Koppeling toegevoegd');
      }
      setShowKoppelingModal(false);
      setEditingKoppeling(null);
      setKoppelingForm({ client: '', begeleider: '' });
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan: ' + (error.response?.data?.detail || 'Onbekende fout'));
    }
  };

  const deleteKoppeling = async (koppeling) => {
    if (!window.confirm(`Weet je zeker dat je de koppeling ${koppeling.client} - ${koppeling.begeleider} wilt verwijderen?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/pb-koppelingen?begeleider=${encodeURIComponent(koppeling.begeleider)}&client=${encodeURIComponent(koppeling.client)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Koppeling verwijderd');
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen: ' + (error.response?.data?.detail || 'Onbekende fout'));
    }
  };

  const gesprekken = useFallback 
    ? (fallbackData?.pb_gesprekken || [])
    : (data?.pb_overzicht?.alle_gesprekken || []);
  const koppelingen = useFallback
    ? (fallbackData?.pb_overzicht?.alle_koppelingen || [])
    : (data?.pb_overzicht?.alle_koppelingen || []);
  // alle_clienten is array of objects with {voornaam, volledige_naam, initials}
  const clienten = data?.alle_clienten || [];
  const clientNamen = clienten.map(c => c.voornaam || c.volledige_naam);

  // Functie om gekoppelde PB'ers voor een specifieke client te vinden
  const getPBsForClient = (clientNaam) => {
    if (!clientNaam) return [];
    const clientLower = clientNaam.toLowerCase();
    const matchedPBs = [];
    
    koppelingen.forEach(k => {
      // De client veld kan meerdere clienten bevatten (bv. "Yassin & Ricardo")
      const clientsInKoppeling = (k.client || '').toLowerCase();
      if (clientsInKoppeling.includes(clientLower) || clientLower.includes(clientsInKoppeling.split('&')[0]?.trim() || '')) {
        if (k.begeleider && !matchedPBs.includes(k.begeleider)) {
          matchedPBs.push(k.begeleider);
        }
      }
    });
    
    return matchedPBs;
  };

  // Haal alle unieke begeleiders op voor de dropdown
  const alleBegeleiders = [...new Set(koppelingen.map(k => k.begeleider).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-teal-50/30" data-testid="pb-gesprekken-page">
      <PageHeader
        title="PB Gesprekken"
        subtitle={`${gesprekken.length} gepland`}
        onBack={onBack}
        icon={ClipboardList}
        iconGradient="from-cyan-500 to-cyan-600"
        rightAction={
          <Button onClick={openNewGesprek} className="h-10 px-4 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white shadow-lg" data-testid="new-gesprek-btn">
            <Plus className="w-4 h-4 mr-1" />Nieuw
          </Button>
        }
      />

      <div className="px-4 py-6 pb-32">
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
            <p className="text-3xl font-bold">{gesprekken.length}</p>
            <p className="text-sm text-white/80">Gepland</p>
          </div>
          <div className="p-4 rounded-2xl bg-white border border-slate-200">
            <p className="text-3xl font-bold text-slate-800">{koppelingen.length}</p>
            <p className="text-sm text-slate-500">Koppelingen</p>
          </div>
        </div>

        <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Komende gesprekken</h2>
        
        {loading ? (
          <LoadingSpinner color="cyan" />
        ) : gesprekken.length === 0 ? (
          <EmptyState icon={ClipboardList} title="Geen gesprekken gepland" subtitle="Plan een nieuw PB gesprek in" />
        ) : (
          <div className="space-y-3">
            {gesprekken.map((gesprek, index) => (
              <motion.div key={gesprek.id || index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm" data-testid={`gesprek-item-${index}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-100 to-teal-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{gesprek.client}</h3>
                    <p className="text-sm text-slate-500">pb'er: {gesprek.begeleider || gesprek.pb_er}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar className="w-3.5 h-3.5" />{gesprek.datum}
                      </span>
                      {gesprek.tijd && <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="w-3.5 h-3.5" />{gesprek.tijd}
                      </span>}
                    </div>
                  </div>
                  {/* Edit en Delete knoppen */}
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => openEditGesprek(gesprek)}
                      className="p-2 rounded-lg bg-cyan-100 hover:bg-cyan-200 text-cyan-600 transition-colors"
                      data-testid={`edit-gesprek-${index}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => deleteGesprek(gesprek.id)}
                      className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
                      data-testid={`delete-gesprek-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* PB Koppelingen sectie */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider">PB Koppelingen ({koppelingen.length})</h2>
            <button 
              onClick={openNewKoppeling}
              className="px-3 py-1.5 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-700 text-sm font-medium flex items-center gap-1 transition-colors"
              data-testid="new-koppeling-btn"
            >
              <Plus className="w-4 h-4" />Nieuwe koppeling
            </button>
          </div>
          {koppelingen.length === 0 ? (
            <div className="p-4 rounded-xl bg-slate-50 text-center text-slate-500">
              Geen koppelingen gevonden
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {koppelingen.map((k) => (
                <div key={`${k.client}-${k.begeleider}`} className="p-3 rounded-xl bg-white border border-slate-200/50 flex items-center justify-between" data-testid={`koppeling-item-${k.client}`}>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{k.client}</p>
                    <p className="text-xs text-slate-500">PB'er: {k.begeleider}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => openEditKoppeling(k)}
                      className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors"
                      data-testid={`edit-koppeling-${k.client}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => deleteKoppeling(k)}
                      className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
                      data-testid={`delete-koppeling-${k.client}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Gesprek Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editingGesprek ? 'Gesprek Bewerken' : 'Nieuw PB Gesprek'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Cliënt</label>
              <select value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value, begeleider: '' })}
                className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200" data-testid="gesprek-client-select">
                <option value="">Selecteer cliënt</option>
                {clientNamen.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Persoonlijk Begeleider (PB)</label>
              {form.client ? (
                <>
                  <select 
                    value={form.begeleider} 
                    onChange={(e) => setForm({ ...form, begeleider: e.target.value })}
                    className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200"
                    data-testid="gesprek-begeleider-select"
                  >
                    <option value="">Selecteer PB'er</option>
                    {/* Toon eerst de gekoppelde PB'ers */}
                    {getPBsForClient(form.client).length > 0 && (
                      <optgroup label="Gekoppelde PB'ers">
                        {getPBsForClient(form.client).map((pb, i) => (
                          <option key={`pb-${i}`} value={pb}>✓ {pb}</option>
                        ))}
                      </optgroup>
                    )}
                    {/* Toon alle andere begeleiders */}
                    <optgroup label="Alle begeleiders">
                      {alleBegeleiders.filter(b => !getPBsForClient(form.client).includes(b)).map((b, i) => (
                        <option key={`all-${i}`} value={b}>{b}</option>
                      ))}
                    </optgroup>
                  </select>
                  {getPBsForClient(form.client).length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">⚠️ Geen PB gekoppeld aan deze cliënt. Voeg eerst een koppeling toe.</p>
                  )}
                </>
              ) : (
                <Input 
                  value={form.begeleider} 
                  disabled 
                  placeholder="Selecteer eerst een cliënt" 
                  className="h-12 rounded-xl bg-slate-50" 
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Datum</label>
                <Input type="date" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} className="h-12 rounded-xl" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tijd</label>
                <Input type="time" value={form.tijd} onChange={(e) => setForm({ ...form, tijd: e.target.value })} className="h-12 rounded-xl" />
              </div>
            </div>
            <Button onClick={saveGesprek} className="w-full h-12 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500" data-testid="save-gesprek-btn">
              <Save className="w-4 h-4 mr-2" />{editingGesprek ? 'Bijwerken' : 'Inplannen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Koppeling Modal */}
      <Dialog open={showKoppelingModal} onOpenChange={setShowKoppelingModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editingKoppeling ? 'Koppeling Bewerken' : 'Nieuwe PB Koppeling'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Cliënt</label>
              <select value={koppelingForm.client} onChange={(e) => setKoppelingForm({ ...koppelingForm, client: e.target.value })}
                className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200" data-testid="koppeling-client-select">
                <option value="">Selecteer cliënt</option>
                {clientNamen.map((c, i) => <option key={i} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Persoonlijk Begeleider (PB)</label>
              <select 
                value={koppelingForm.begeleider} 
                onChange={(e) => setKoppelingForm({ ...koppelingForm, begeleider: e.target.value })}
                className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200"
                data-testid="koppeling-begeleider-select"
              >
                <option value="">Selecteer begeleider</option>
                {alleBegeleiders.map((b, i) => (
                  <option key={i} value={b}>{b}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Of typ een nieuwe naam:</p>
              <Input 
                value={koppelingForm.begeleider} 
                onChange={(e) => setKoppelingForm({ ...koppelingForm, begeleider: e.target.value })} 
                placeholder="Nieuwe begeleider naam" 
                className="h-10 rounded-xl mt-1" 
                data-testid="koppeling-begeleider-input"
              />
            </div>
            <Button onClick={saveKoppeling} className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500" data-testid="save-koppeling-btn">
              <Save className="w-4 h-4 mr-2" />{editingKoppeling ? 'Bijwerken' : 'Koppeling Toevoegen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// WEEKMENU PAGE
// ============================================
export const WeekmenuPage = ({ onBack, user, isAdmin, selectedLocation }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingDay, setEditingDay] = useState(null);
  const [menuForm, setMenuForm] = useState({ gerecht: '', kookt_mee: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDayForm, setNewDayForm] = useState({ dag: '', gerecht: '', kookt_mee: '' });
  const [useFallback, setUseFallback] = useState(false);

  const DAGEN = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
  
  // Fallback weekmenu data
  const FALLBACK_WEEKMENU = {
    'groep-zwaluw': [
      { dag: 'Maandag', gerecht: 'Spaghetti Bolognese', kookt_mee: 'Yassin' },
      { dag: 'Dinsdag', gerecht: 'Kip met rijst', kookt_mee: 'Sietse' },
      { dag: 'Woensdag', gerecht: 'Stamppot boerenkool', kookt_mee: 'Richard' },
      { dag: 'Donderdag', gerecht: 'Nasi goreng', kookt_mee: 'Axcel' },
      { dag: 'Vrijdag', gerecht: 'Pizza', kookt_mee: 'Mahad' },
      { dag: 'Zaterdag', gerecht: 'Pannenkoeken', kookt_mee: 'Team' },
      { dag: 'Zondag', gerecht: 'Gehaktballen met groenten', kookt_mee: 'Team' }
    ],
    'groep-adelaars': [
      { dag: 'Maandag', gerecht: 'Macaroni met kaas', kookt_mee: 'Rinaldo' },
      { dag: 'Dinsdag', gerecht: 'Shoarma met friet', kookt_mee: 'Faraah' },
      { dag: 'Woensdag', gerecht: 'Erwtensoep', kookt_mee: 'Mike' },
      { dag: 'Donderdag', gerecht: 'Lasagne', kookt_mee: 'Melvin' },
      { dag: 'Vrijdag', gerecht: 'Vis met aardappelen', kookt_mee: 'Julian' },
      { dag: 'Zaterdag', gerecht: 'BBQ', kookt_mee: 'Team' },
      { dag: 'Zondag', gerecht: 'Stoofvlees', kookt_mee: 'Team' }
    ]
  };

  const fetchData = async () => {
    const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
    try {
      const token = localStorage.getItem('token');
      const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
      const response = await axios.get(`${API_URL}/api/begeleider/overzicht${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      if (response.data?.weekmenu && response.data.weekmenu.length > 0) {
        setData(response.data);
        setUseFallback(false);
      } else {
        console.warn('API returned empty weekmenu, using fallback');
        setUseFallback(true);
      }
    } catch (error) {
      console.error('Weekmenu API Error:', error.response?.status, error.message);
      setUseFallback(true);
      if (error.response?.status === 401) {
        toast.error('Sessie verlopen, log opnieuw in');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedLocation]);

  const saveMenu = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Niet ingelogd - log opnieuw in');
        return;
      }
      
      // Stuur locatie mee in request body
      const response = await axios.post(`${API_URL}/api/begeleider/weekmenu/dag`, {
        dag: editingDay,
        gerecht: menuForm.gerecht,
        kookt_mee: menuForm.kookt_mee,
        locatie: selectedLocation || user?.selected_location || 'groep-zwaluw'
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      
      toast.success('Menu opgeslagen');
      setEditingDay(null);
      setMenuForm({ gerecht: '', kookt_mee: '' });
      setUseFallback(false);
      setLoading(true);
      fetchData();
    } catch (error) {
      console.error('Save menu error:', error);
      if (error.response?.status === 401) {
        toast.error('Sessie verlopen - log opnieuw in');
      } else if (error.response?.status === 403) {
        toast.error('Geen rechten om wijzigingen te maken');
      } else {
        toast.error('Opslaan mislukt - probeer later opnieuw');
      }
    }
  };

  const addNewDay = async () => {
    if (!newDayForm.dag || !newDayForm.gerecht) {
      toast.error('Selecteer een dag en vul een gerecht in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/begeleider/weekmenu/dag`, {
        dag: newDayForm.dag.toLowerCase(),
        gerecht: newDayForm.gerecht,
        kookt_mee: newDayForm.kookt_mee
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Menu toegevoegd');
      setShowAddModal(false);
      setNewDayForm({ dag: '', gerecht: '', kookt_mee: '' });
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error('Fout bij toevoegen: ' + (error.response?.data?.detail || 'Onbekende fout'));
    }
  };

  const deleteMenu = async (dag) => {
    if (!window.confirm(`Weet je zeker dat je het menu voor ${dag} wilt verwijderen?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/begeleider/weekmenu/dag/${dag.toLowerCase()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Menu verwijderd');
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen: ' + (error.response?.data?.detail || 'Onbekende fout'));
    }
  };

  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
  
  // weekmenu is an array of { dag, gerecht, kookt_mee }
  const weekmenu = useFallback 
    ? (FALLBACK_WEEKMENU[locationSlug] || FALLBACK_WEEKMENU['groep-zwaluw'])
    : (data?.weekmenu || []);
  const clienten = data?.alle_clienten || [];
  const clientNamen = clienten.map(c => c.voornaam || c.volledige_naam);
  
  // Vind dagen die nog niet in het menu staan
  const bestaandeDagen = weekmenu.map(m => m.dag?.toLowerCase());
  const beschikbareDagen = DAGEN.filter(d => !bestaandeDagen.includes(d.toLowerCase()));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/30" data-testid="weekmenu-page">
      <PageHeader 
        title="Weekmenu" 
        subtitle="Maaltijdplanning" 
        onBack={onBack}
        icon={Utensils}
        iconGradient="from-orange-500 to-orange-600"
        rightAction={
          <Button onClick={() => setShowAddModal(true)} className="h-10 px-4 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg" data-testid="add-menu-btn">
            <Plus className="w-4 h-4 mr-1" />Toevoegen
          </Button>
        }
      />

      <div className="px-4 py-6 pb-32">
        {loading ? (
          <LoadingSpinner color="orange" />
        ) : weekmenu.length === 0 ? (
          <EmptyState icon={Utensils} title="Geen weekmenu" subtitle="Er is nog geen weekmenu ingesteld" action={
            <Button onClick={() => setShowAddModal(true)} className="rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <Plus className="w-4 h-4 mr-1" />Menu toevoegen
            </Button>
          } />
        ) : (
          <div className="space-y-3">
            {weekmenu.map((item, index) => {
              const isEditing = editingDay === item.dag?.toLowerCase();
              
              return (
                <motion.div key={item.dag || index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-2xl bg-white border shadow-sm ${isEditing ? 'border-orange-300 ring-2 ring-orange-100' : 'border-slate-200/50'}`}
                  data-testid={`weekmenu-item-${index}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        item.gerecht && item.gerecht !== 'Nog niet ingevuld' 
                          ? 'bg-gradient-to-br from-orange-500 to-orange-600' : 'bg-slate-100'
                      }`}>
                        <Utensils className={`w-6 h-6 ${item.gerecht && item.gerecht !== 'Nog niet ingevuld' ? 'text-white' : 'text-slate-400'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{item.dag}</h3>
                        {isEditing ? (
                          <div className="mt-2 space-y-2">
                            <Input placeholder="Gerecht" value={menuForm.gerecht} onChange={(e) => setMenuForm({ ...menuForm, gerecht: e.target.value })} className="h-10 rounded-xl text-sm" data-testid="edit-gerecht-input" />
                            <select value={menuForm.kookt_mee} onChange={(e) => setMenuForm({ ...menuForm, kookt_mee: e.target.value })}
                              className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm" data-testid="edit-kookt-mee-select">
                              <option value="">Wie kookt mee?</option>
                              {clientNamen.map((c, i) => <option key={i} value={c}>{c}</option>)}
                            </select>
                            <div className="flex gap-2">
                              <Button onClick={saveMenu} size="sm" className="rounded-lg bg-rose-500 hover:bg-rose-600" data-testid="save-menu-btn">
                                <Save className="w-3.5 h-3.5 mr-1" />Opslaan
                              </Button>
                              <Button onClick={() => setEditingDay(null)} size="sm" variant="ghost" className="rounded-lg">Annuleer</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className={`text-sm ${item.gerecht && item.gerecht !== 'Nog niet ingevuld' ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                              {item.gerecht || 'Nog geen menu'}
                            </p>
                            {item.kookt_mee && (
                              <span className="inline-flex items-center gap-1 mt-2 text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                                <User className="w-3 h-3" />{item.kookt_mee} kookt mee
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {/* Edit en Delete knoppen - zichtbaar voor alle gebruikers */}
                    {!isEditing && (
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => { setEditingDay(item.dag?.toLowerCase()); setMenuForm({ gerecht: item.gerecht || '', kookt_mee: item.kookt_mee || '' }); }}
                          className="p-2 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-600 transition-colors" 
                          data-testid={`edit-menu-${index}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => deleteMenu(item.dag)}
                          className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors" 
                          data-testid={`delete-menu-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Menu Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Menu Toevoegen</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Dag</label>
              <select 
                value={newDayForm.dag} 
                onChange={(e) => setNewDayForm({ ...newDayForm, dag: e.target.value })}
                className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200"
                data-testid="new-dag-select"
              >
                <option value="">Selecteer dag</option>
                {beschikbareDagen.map(dag => <option key={dag} value={dag}>{dag}</option>)}
                {DAGEN.filter(d => bestaandeDagen.includes(d.toLowerCase())).map(dag => (
                  <option key={dag} value={dag}>{dag} (overschrijven)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Gerecht</label>
              <Input 
                value={newDayForm.gerecht} 
                onChange={(e) => setNewDayForm({ ...newDayForm, gerecht: e.target.value })} 
                placeholder="bijv. Spaghetti Bolognese, Stamppot, Pizza..." 
                className="h-12 rounded-xl mt-1" 
                data-testid="new-gerecht-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Wie kookt mee?</label>
              <select 
                value={newDayForm.kookt_mee} 
                onChange={(e) => setNewDayForm({ ...newDayForm, kookt_mee: e.target.value })}
                className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200"
                data-testid="new-kookt-mee-select"
              >
                <option value="">Selecteer cliënt (optioneel)</option>
                {clientNamen.map((c, i) => <option key={i} value={c}>{c}</option>)}
              </select>
            </div>
            <Button onClick={addNewDay} className="w-full h-12 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500" data-testid="save-new-menu-btn">
              <Plus className="w-4 h-4 mr-2" />Menu Toevoegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// BOODSCHAPPEN PAGE
// ============================================
export const BoodschappenPage = ({ onBack, user, isAdmin, selectedLocation }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedItems, setCheckedItems] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ item: '', categorie: 'Overig', hoeveelheid: '' });
  const [useFallback, setUseFallback] = useState(false);

  const CATEGORIES = ['Groenten', 'Fruit', 'Vlees', 'Zuivel', 'Brood', 'Dranken', 'Schoonmaak', 'Overig'];
  
  // Fallback boodschappen data
  const FALLBACK_BOODSCHAPPEN = {
    'groep-zwaluw': {
      items: [
        { item: 'Melk', categorie: 'Zuivel', hoeveelheid: '2 pakken' },
        { item: 'Brood', categorie: 'Brood', hoeveelheid: '2 stuks' },
        { item: 'Kaas', categorie: 'Zuivel', hoeveelheid: '1 pak' },
        { item: 'Appels', categorie: 'Fruit', hoeveelheid: '1 kilo' },
        { item: 'Tomaten', categorie: 'Groenten', hoeveelheid: '500 gram' },
        { item: 'Kipfilet', categorie: 'Vlees', hoeveelheid: '500 gram' },
        { item: 'Afwasmiddel', categorie: 'Schoonmaak', hoeveelheid: '1 fles' }
      ]
    },
    'groep-adelaars': {
      items: [
        { item: 'Eieren', categorie: 'Zuivel', hoeveelheid: '1 doos' },
        { item: 'Boter', categorie: 'Zuivel', hoeveelheid: '1 pak' },
        { item: 'Bananen', categorie: 'Fruit', hoeveelheid: '1 tros' },
        { item: 'Gehakt', categorie: 'Vlees', hoeveelheid: '500 gram' },
        { item: 'Sla', categorie: 'Groenten', hoeveelheid: '1 krop' },
        { item: 'Toiletpapier', categorie: 'Schoonmaak', hoeveelheid: '1 pak' }
      ]
    }
  };

  const fetchData = async () => {
    const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
    try {
      const token = localStorage.getItem('token');
      const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
      const response = await axios.get(`${API_URL}/api/begeleider/overzicht${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      // Backend returns boodschappenlijst, not items
      const boodschappenData = response.data?.boodschappen?.boodschappenlijst || response.data?.boodschappen?.items || [];
      if (boodschappenData.length > 0) {
        // Normalize the data structure for frontend
        const normalizedData = {
          ...response.data,
          boodschappen: {
            ...response.data.boodschappen,
            items: boodschappenData
          }
        };
        setData(normalizedData);
        setUseFallback(false);
        const status = response.data?.boodschappen?.item_status || {};
        const checked = {};
        Object.entries(status).forEach(([item, s]) => { if (s.afgevinkt) checked[item] = true; });
        setCheckedItems(checked);
      } else {
        console.warn('API returned empty boodschappen, using fallback');
        setUseFallback(true);
      }
    } catch (error) {
      console.error('Boodschappen API Error:', error.response?.status, error.message);
      setUseFallback(true);
      if (error.response?.status === 401) {
        toast.error('Sessie verlopen, log opnieuw in');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedLocation]);

  const toggleItem = async (itemName, itemId) => {
    const newChecked = !checkedItems[itemName];
    setCheckedItems(prev => ({ ...prev, [itemName]: newChecked }));
    try {
      const token = localStorage.getItem('token');
      const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
      await axios.put(`${API_URL}/api/begeleider/boodschappen/item`, { 
        item: itemName, 
        afgevinkt: newChecked, 
        besteld: false,
        besteld_via: '',
        besteld_datum: null,
        locatie: locationSlug
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
    } catch (error) {
      console.error('Toggle item error:', error);
      setCheckedItems(prev => ({ ...prev, [itemName]: !newChecked }));
      if (error.response?.status === 401) {
        toast.error('Sessie verlopen');
      } else {
        toast.error('Opslaan mislukt');
      }
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setNewItem({ item: '', categorie: 'Overig', hoeveelheid: '' });
    setShowAddModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setNewItem({
      item: item.item || '',
      categorie: item.categorie || 'Overig',
      hoeveelheid: item.hoeveelheid || ''
    });
    setShowAddModal(true);
  };

  const addItem = async () => {
    if (!newItem.item.trim()) {
      toast.error('Vul een item in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
      
      if (editingItem) {
        // Update bestaand item
        await axios.put(`${API_URL}/api/boodschappen/items/${editingItem.id}`, {
          item: newItem.item,
          categorie: newItem.categorie,
          hoeveelheid: newItem.hoeveelheid || '1',
          locatie: locationSlug
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        });
        toast.success('Item bijgewerkt');
      } else {
        // Nieuw item
        await axios.post(`${API_URL}/api/boodschappen/items`, {
          item: newItem.item,
          categorie: newItem.categorie,
          hoeveelheid: newItem.hoeveelheid || '1',
          toegevoegd_door: user?.name || 'Onbekend',
          locatie: locationSlug
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        });
        toast.success('Item toegevoegd');
      }
      setNewItem({ item: '', categorie: 'Overig', hoeveelheid: '' });
      setEditingItem(null);
      setShowAddModal(false);
      // Refresh data
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const deleteItem = async (itemId, itemName) => {
    if (!itemId) {
      toast.error('Kan item niet verwijderen: geen ID gevonden');
      return;
    }
    if (!window.confirm(`Weet je zeker dat je "${itemName}" wilt verwijderen?`)) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Je bent niet ingelogd');
        return;
      }
      await axios.delete(`${API_URL}/api/boodschappen/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item verwijderd');
      setLoading(true);
      fetchData();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
      toast.error(`Fout bij verwijderen: ${errorMsg}`);
    }
  };

  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
  const fallbackData = FALLBACK_BOODSCHAPPEN[locationSlug] || FALLBACK_BOODSCHAPPEN['groep-zwaluw'];
  
  const boodschappenlijst = useFallback 
    ? fallbackData.items 
    : (data?.boodschappen?.boodschappenlijst || []);
  
  const categories = {};
  boodschappenlijst.forEach(item => {
    const cat = item.categorie || 'Overig';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(item);
  });
  
  const categoryNames = Object.keys(categories);
  const totalItems = boodschappenlijst.length;
  const checkedCount = Object.keys(checkedItems).filter(k => checkedItems[k]).length;
  const progress = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-fuchsia-50/30" data-testid="boodschappen-page">
      {/* Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-72 h-72 bg-purple-300/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-fuchsia-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-violet-300/15 rounded-full blur-3xl" />
      </div>
      
      <PageHeader 
        title="Boodschappen" 
        subtitle={`${checkedCount}/${totalItems} afgevinkt`} 
        onBack={onBack}
        icon={ShoppingCart}
        iconGradient="from-purple-500 to-purple-600"
        rightAction={
          <Button onClick={openAddModal} className="h-10 px-4 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg" data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-1" />Toevoegen
          </Button>
        }
      />

      <div className="px-4 py-6 pb-32">
        <div className="mb-6 p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Voortgang</span>
            <span className="text-sm font-bold text-purple-600">{progress}%</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" />
          </div>
          {data?.boodschappen?.boodschappendag && (
            <p className="text-xs text-slate-500 mt-2">Boodschappendag: {data.boodschappen.boodschappendag}</p>
          )}
        </div>

        {loading ? (
          <LoadingSpinner color="purple" />
        ) : categoryNames.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="Geen boodschappen" subtitle="De boodschappenlijst is leeg" action={
            <Button onClick={openAddModal} className="rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 text-white">
              <Plus className="w-4 h-4 mr-1" />Item toevoegen
            </Button>
          } />
        ) : (
          <div className="space-y-4">
            {categoryNames.map((category, catIndex) => (
              <motion.div key={category} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: catIndex * 0.05 }}>
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 px-1">{category}</h2>
                <div className="bg-white rounded-2xl border border-slate-200/50 shadow-sm divide-y divide-slate-100">
                  {categories[category].map((item, index) => (
                    <div key={item.id || index} className="flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`boodschap-item-${catIndex}-${index}`}>
                      {/* Checkbox */}
                      <button 
                        onClick={() => toggleItem(item.item, item.id)} 
                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${checkedItems[item.item] ? 'bg-purple-500 border-purple-500' : 'border-slate-300 hover:border-purple-400'}`}
                      >
                        {checkedItems[item.item] && <Check className="w-4 h-4 text-white" />}
                      </button>
                      {/* Item naam */}
                      <div className="flex-1">
                        <span className={`${checkedItems[item.item] ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{item.item}</span>
                        {item.hoeveelheid && <span className="text-xs text-slate-400 ml-2">({item.hoeveelheid})</span>}
                      </div>
                      {/* Edit en Delete knoppen */}
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => openEditItem(item)}
                          className="p-2.5 rounded-xl bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors"
                          data-testid={`edit-item-${catIndex}-${index}`}
                          title="Bewerken"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => deleteItem(item.id, item.item)}
                          className="p-2.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
                          data-testid={`delete-item-${catIndex}-${index}`}
                          title="Verwijderen"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Item Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editingItem ? 'Item Bewerken' : 'Item Toevoegen'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Item</label>
              <Input 
                value={newItem.item} 
                onChange={(e) => setNewItem({ ...newItem, item: e.target.value })} 
                placeholder="bijv. Melk, Brood, Tomaten..." 
                className="h-12 rounded-xl mt-1" 
                data-testid="item-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Categorie</label>
              <select 
                value={newItem.categorie} 
                onChange={(e) => setNewItem({ ...newItem, categorie: e.target.value })}
                className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200"
                data-testid="category-select"
              >
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Hoeveelheid</label>
              <Input 
                value={newItem.hoeveelheid} 
                onChange={(e) => setNewItem({ ...newItem, hoeveelheid: e.target.value })} 
                placeholder="bijv. 2 stuks, 500g, 1 pak..." 
                className="h-12 rounded-xl mt-1" 
              />
            </div>
            <Button onClick={addItem} className="w-full h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500" data-testid="save-item-btn">
              {editingItem ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingItem ? 'Bijwerken' : 'Item Toevoegen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// CORVEE PAGE - Met CRUD functionaliteit
// ============================================
export const CorveePage = ({ onBack, user, isAdmin, selectedLocation }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBewoner, setEditingBewoner] = useState(null);
  const [editingDag, setEditingDag] = useState(null);
  const [selectedTaak, setSelectedTaak] = useState('');
  const [useFallback, setUseFallback] = useState(false);
  
  // Fallback corvee data
  const FALLBACK_CORVEE = {
    'groep-zwaluw': {
      naam: 'Corvee Schema Groep Zwaluw',
      bewoners: ['Yassin', 'Sietse', 'Richard', 'Axcel'],
      taken_definitie: {
        poetsdag: 'Grote schoonmaak',
        huishoudelijk: 'Dagelijkse taken',
        keuken: 'Keuken schoonmaken',
        badkamer: 'Badkamer schoonmaken'
      },
      weekschema: {
        maandag: { Yassin: 'keuken', Sietse: 'badkamer' },
        dinsdag: { Richard: 'huishoudelijk', Axcel: 'keuken' },
        woensdag: { Axcel: 'keuken', Yassin: 'huishoudelijk' },
        donderdag: { Sietse: 'keuken', Richard: 'badkamer' },
        vrijdag: { Richard: 'huishoudelijk', Axcel: 'badkamer' },
        zaterdag: { Yassin: 'poetsdag' },
        zondag: {}
      }
    },
    'groep-adelaars': {
      naam: 'Corvee Schema Groep Adelaars',
      bewoners: ['Rinaldo', 'Faraah', 'Mike', 'Melvin', 'Julian'],
      taken_definitie: {
        poetsdag: 'Grote schoonmaak',
        huishoudelijk: 'Dagelijkse taken',
        keuken: 'Keuken schoonmaken',
        badkamer: 'Badkamer schoonmaken'
      },
      weekschema: {
        maandag: { Rinaldo: 'keuken', Faraah: 'badkamer' },
        dinsdag: { Mike: 'huishoudelijk', Melvin: 'keuken' },
        woensdag: { Julian: 'keuken', Rinaldo: 'huishoudelijk' },
        donderdag: { Faraah: 'keuken', Mike: 'badkamer' },
        vrijdag: { Melvin: 'huishoudelijk', Julian: 'badkamer' },
        zaterdag: { Rinaldo: 'poetsdag', Julian: 'huishoudelijk' },
        zondag: { Julian: 'keuken' }
      }
    }
  };

  const fetchData = async () => {
    const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
    try {
      const token = localStorage.getItem('token');
      const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
      const response = await axios.get(`${API_URL}/api/begeleider/overzicht${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      if (response.data?.corvee_schema?.bewoners && response.data.corvee_schema.bewoners.length > 0) {
        setData(response.data);
        setUseFallback(false);
      } else {
        console.warn('API returned empty corvee, using fallback');
        setUseFallback(true);
      }
    } catch (error) {
      console.error('Corvee API Error:', error.response?.status, error.message);
      setUseFallback(true);
      if (error.response?.status === 401) {
        toast.error('Sessie verlopen, log opnieuw in');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedLocation]);

  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
  const fallbackData = FALLBACK_CORVEE[locationSlug] || FALLBACK_CORVEE['groep-zwaluw'];
  
  // corvee_schema has: naam, beschrijving, bewoners[], taken_definitie{}, weekschema{}
  const corveeSchema = useFallback ? fallbackData : (data?.corvee_schema || {});
  const weekschema = corveeSchema?.weekschema || {};
  const bewoners = corveeSchema?.bewoners || [];
  const takenDef = corveeSchema?.taken_definitie || {};
  
  const dagen = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'];
  const dagLabels = {
    maandag: 'Ma', dinsdag: 'Di', woensdag: 'Wo', donderdag: 'Do',
    vrijdag: 'Vr', zaterdag: 'Za', zondag: 'Zo'
  };
  
  const taakKleuren = {
    poetsdag: 'from-blue-500 to-blue-600',
    huishoudelijk: 'from-emerald-500 to-emerald-600',
    wasdag: 'from-orange-500 to-orange-600',
    controle: 'from-purple-500 to-purple-600',
    vrij: 'from-slate-300 to-slate-400',
  };

  const openEditModal = (bewoner, dag) => {
    setEditingBewoner(bewoner);
    setEditingDag(dag);
    const currentTaak = weekschema[dag]?.[bewoner]?.taak || '';
    setSelectedTaak(currentTaak);
    setShowModal(true);
  };

  const saveTaak = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/begeleider/corvee`, {
        dag: editingDag,
        bewoner_naam: editingBewoner,
        taak_type: selectedTaak || 'vrij'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Corvee taak bijgewerkt voor ${editingBewoner}`);
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Corvee update error:', error);
      toast.error('Fout bij opslaan');
    }
  };

  const deleteTaak = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/begeleider/corvee`, {
        dag: editingDag,
        bewoner_naam: editingBewoner,
        taak_type: 'vrij'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Corvee taak verwijderd voor ${editingBewoner}`);
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-yellow-50/30 to-amber-50/30" data-testid="corvee-page">
      <PageHeader 
        title="Corvee Schema" 
        subtitle={corveeSchema?.naam || "Huishoudelijke taken"} 
        onBack={onBack}
        icon={Brush}
        iconGradient="from-yellow-400 to-yellow-500"
      />

      <div className="px-4 py-6 pb-32">
        {/* Info banner */}
        {corveeSchema?.beschrijving && (
          <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-yellow-400 to-yellow-500 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Brush className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Corvee Planning</h3>
                <p className="text-sm text-white/80">{bewoners.length} bewoners • Klik op een dag om te bewerken</p>
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        {Object.keys(takenDef).length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Legenda</h2>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(takenDef).map(([key, taak]) => (
                <div key={key} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-200/50">
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${taakKleuren[key] || 'from-slate-400 to-slate-500'}`} />
                  <span className="text-sm text-slate-700">{taak.naam || key}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <LoadingSpinner color="yellow" />
        ) : bewoners.length === 0 ? (
          <EmptyState icon={Brush} title="Geen corvee schema" subtitle="Er is nog geen corvee schema ingesteld" />
        ) : (
          <div className="space-y-4">
            {bewoners.map((bewoner, bIndex) => (
              <motion.div key={bewoner} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: bIndex * 0.1 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm" data-testid={`corvee-bewoner-${bIndex}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center">
                    <span className="text-lg font-bold text-white">{bewoner.charAt(0)}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900">{bewoner}</h3>
                </div>
                
                <div className="grid grid-cols-7 gap-1">
                  {dagen.map(dag => {
                    const taakInfo = weekschema[dag]?.[bewoner];
                    const taakKey = taakInfo?.taak;
                    const label = taakInfo?.label || taakKey || '-';
                    
                    return (
                      <button
                        key={dag}
                        onClick={() => openEditModal(bewoner, dag)}
                        className="text-center group"
                        data-testid={`corvee-${bewoner}-${dag}`}
                      >
                        <div className="text-xs text-slate-400 mb-1">{dagLabels[dag]}</div>
                        <div className={`h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-all group-hover:ring-2 group-hover:ring-yellow-300 ${
                          taakKey ? `bg-gradient-to-br ${taakKleuren[taakKey] || 'from-slate-400 to-slate-500'} text-white` : 'bg-slate-100 text-slate-400'
                        }`}>
                          {label.slice(0, 3)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Taken details */}
        {Object.keys(takenDef).length > 0 && (
          <div className="mt-8">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Taken Overzicht</h2>
            <div className="space-y-3">
              {Object.entries(takenDef).map(([key, taak]) => (
                <div key={key} className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${taakKleuren[key] || 'from-slate-400 to-slate-500'}`} />
                    <h3 className="font-semibold text-slate-800">{taak.naam || key}</h3>
                  </div>
                  {taak.taken && (
                    <ul className="ml-6 space-y-1">
                      {taak.taken.map((t, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          {t}
                        </li>
                      ))}
                    </ul>
                  )}
                  {taak.note && <p className="text-xs text-slate-500 mt-2 italic">{taak.note}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Corvee Bewerken</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-xl bg-slate-50">
              <p className="text-sm text-slate-500">Bewoner</p>
              <p className="font-semibold text-slate-800">{editingBewoner}</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-50">
              <p className="text-sm text-slate-500">Dag</p>
              <p className="font-semibold text-slate-800 capitalize">{editingDag}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Taak</label>
              <select 
                value={selectedTaak} 
                onChange={(e) => setSelectedTaak(e.target.value)}
                className="mt-1 w-full p-3 rounded-xl border border-slate-200 focus:border-indigo-400"
                data-testid="corvee-taak-select"
              >
                <option value="">Geen taak</option>
                {Object.entries(takenDef).map(([key, taak]) => (
                  <option key={key} value={key}>{taak.naam || key}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3 justify-between">
            <Button variant="outline" onClick={deleteTaak} className="text-red-600 hover:bg-red-50" data-testid="delete-corvee-btn">
              <Trash2 className="w-4 h-4 mr-1" />Verwijderen
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Annuleren</Button>
              <Button onClick={saveTaak} className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white" data-testid="save-corvee-btn">
                <Save className="w-4 h-4 mr-1" />Opslaan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// HUISAFSPRAKEN PAGE - Regels voor bewoners (iPhone Style)
// ============================================
export const HuisafsprakenPage = ({ onBack, user }) => {
  const [activeCategory, setActiveCategory] = useState('algemeen');
  const [searchTerm, setSearchTerm] = useState('');

  // Huisafspraken data met juiste iconen
  const HUISAFSPRAKEN = {
    algemeen: {
      titel: 'Algemeen',
      icon: Home,
      gradient: 'from-emerald-400 to-teal-500',
      regels: [
        { titel: 'Veiligheid & Welzijn', beschrijving: 'Iedereen moet zich veilig en op zijn gemak voelen. Deze afspraken zorgen voor rust en structuur.', belangrijk: true },
        { titel: 'Vertrouwen & Respect', beschrijving: 'Afspraken nakomen leidt tot meer vrijheid en verantwoordelijkheid.', belangrijk: true },
        { titel: 'Spullen Uitlenen', beschrijving: 'Niet zonder overleg met begeleiding.', belangrijk: false },
        { titel: 'Agressie & Geweld', beschrijving: 'Resulteert in gesprek met zorgcoördinator. Wapens zijn altijd verboden.', belangrijk: true }
      ]
    },
    kamer: {
      titel: 'Jouw Kamer',
      icon: Home,
      gradient: 'from-blue-400 to-indigo-500',
      regels: [
        { titel: 'Netjes Houden', beschrijving: 'Je bent zelf verantwoordelijk. Wekelijkse controle door begeleiding.', belangrijk: true },
        { titel: 'Sleutels', beschrijving: 'Veilig bewaren en niet uitlenen aan anderen.', belangrijk: false },
        { titel: 'Kamer Inspectie', beschrijving: 'Bij verdenking van verboden items mag begeleiding de kamer inspecteren.', belangrijk: true }
      ]
    },
    middelen: {
      titel: 'Alcohol & Drugs',
      icon: AlertTriangle,
      gradient: 'from-red-400 to-rose-500',
      regels: [
        { titel: 'Alcohol & Drugs', beschrijving: 'NIET toegestaan binnen Zorg-Vleugel.', belangrijk: true },
        { titel: 'Roken', beschrijving: 'Alleen in aangewezen rookplekken buiten.', belangrijk: true },
        { titel: 'Blowen', beschrijving: 'Niet in woning, kamers of tuin. Ga naar nabijgelegen park.', belangrijk: true },
        { titel: 'Onder Invloed', beschrijving: 'Direct naar je kamer. Gesprek volgende dag.', belangrijk: true }
      ]
    },
    maaltijden: {
      titel: 'Maaltijden',
      icon: Utensils,
      gradient: 'from-amber-400 to-orange-500',
      regels: [
        { titel: 'Avondeten 17:00', beschrijving: 'Samen eten voor structuur. Via app-groep melden of je mee-eet.', belangrijk: true },
        { titel: 'Eigen Bestek', beschrijving: 'Meenemen naar eten en daarna zelf afwassen.', belangrijk: false },
        { titel: 'Keuken Schoon', beschrijving: 'Netjes achterlaten voor andere bewoners.', belangrijk: true }
      ]
    },
    schoonmaak: {
      titel: 'Schoonmaak',
      icon: Brush,
      gradient: 'from-purple-400 to-violet-500',
      regels: [
        { titel: 'Schoonmaakdagen', beschrijving: 'Woensdag en zondag. Hulp nodig? Vraag de begeleiding!', belangrijk: true },
        { titel: 'Beloningssysteem', beschrijving: 'Groen (2 ptn) = netjes | Oranje (1 pt) = gedeeltelijk | Rood (0 ptn) = niet', belangrijk: false }
      ]
    },
    bezoek: {
      titel: 'Bezoek & Verlof',
      icon: Users,
      gradient: 'from-cyan-400 to-blue-500',
      regels: [
        { titel: 'Bezoek Aanmelden', beschrijving: 'Minimaal 2 dagen van tevoren in overleg met begeleiding.', belangrijk: true },
        { titel: 'Bezoektijden', beschrijving: 'Doordeweeks 11:00-16:00 | Weekend 10:00-16:00', belangrijk: false },
        { titel: 'Weekend Verlof', beschrijving: 'Vrijdag/zaterdag mogelijk in overleg met mentor.', belangrijk: false }
      ]
    },
    samenleven: {
      titel: 'Samen Wonen',
      icon: Users,
      gradient: 'from-pink-400 to-rose-500',
      regels: [
        { titel: 'Nachtrust (23:00)', beschrijving: 'Na 23:00 op je kamer blijven voor rust in huis.', belangrijk: true },
        { titel: 'Muziek', beschrijving: 'Geen harde muziek na 23:00. Bij overlast: speakers in beslag.', belangrijk: true },
        { titel: 'Huisdieren', beschrijving: 'Niet toegestaan om hygiëne te waarborgen.', belangrijk: false }
      ]
    }
  };

  const categories = Object.keys(HUISAFSPRAKEN);
  const activeData = HUISAFSPRAKEN[activeCategory];

  // Filter alle regels op zoekterm (zoek in alle categorieën)
  const getFilteredResults = () => {
    if (!searchTerm.trim()) return null;
    
    const results = [];
    Object.entries(HUISAFSPRAKEN).forEach(([catKey, catData]) => {
      catData.regels.forEach(regel => {
        if (regel.titel.toLowerCase().includes(searchTerm.toLowerCase()) ||
            regel.beschrijving.toLowerCase().includes(searchTerm.toLowerCase())) {
          results.push({ ...regel, categorie: catData.titel, categorieKey: catKey, gradient: catData.gradient, icon: catData.icon });
        }
      });
    });
    return results;
  };

  const searchResults = getFilteredResults();
  const showSearchResults = searchTerm.trim().length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-blue-50/30" data-testid="huisafspraken-page">
      <PageHeader title="Huisafspraken" subtitle="Regels voor bewoners" onBack={onBack} icon={FileText} iconGradient="from-indigo-500 to-indigo-600" />

      <div className="px-4 py-4 pb-32">
        {/* Zoekbalk */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Zoek in huisafspraken..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-slate-800 placeholder-slate-400"
            data-testid="huisafspraken-search"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-100"
            >
              <span className="text-slate-400 text-sm">✕</span>
            </button>
          )}
        </div>

        {/* Quick Reference - Belangrijke Tijden */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl p-4 text-white shadow-lg mb-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold">Belangrijke Tijden</p>
                <p className="text-white/90 text-sm">Eten 17:00 • Rust 23:00 • Bezoek 11-16u</p>
              </div>
            </div>
          </div>
        </motion.div>

        {showSearchResults ? (
          /* Zoekresultaten */
          <div className="space-y-3">
            <p className="text-sm text-slate-500 mb-2">
              {searchResults.length} resultaat{searchResults.length !== 1 ? 'en' : ''} voor "{searchTerm}"
            </p>
            {searchResults.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-100">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Geen resultaten gevonden</p>
                <p className="text-sm text-slate-400 mt-1">Probeer een andere zoekterm</p>
              </div>
            ) : (
              searchResults.map((regel, index) => {
                const Icon = regel.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regel.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">{regel.categorie}</span>
                          {regel.belangrijk && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              Belangrijk
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-800">{regel.titel}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{regel.beschrijving}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        ) : (
          /* Normale weergave met categorieën */
          <>
            {/* Category Tabs - iPhone Style Segmented Control */}
            <div className="bg-white rounded-2xl p-1.5 shadow-sm border border-slate-200 mb-4">
              <div className="flex overflow-x-auto gap-1 scrollbar-hide">
                {categories.map((cat) => {
                  const Icon = HUISAFSPRAKEN[cat].icon;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`flex items-center gap-1.5 flex-1 min-w-fit px-3 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                        activeCategory === cat 
                          ? `bg-gradient-to-r ${HUISAFSPRAKEN[cat].gradient} text-white shadow-md` 
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`huisafspraken-tab-${cat}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{HUISAFSPRAKEN[cat].titel}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Rules List - iPhone Card Style */}
            <div className="space-y-3">
              {activeData.regels.map((regel, index) => {
                const Icon = activeData.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
                    data-testid={`huisafspraken-regel-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${activeData.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                        {regel.belangrijk ? (
                          <AlertTriangle className="w-5 h-5 text-white" />
                        ) : (
                          <Icon className="w-5 h-5 text-white" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-800">{regel.titel}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{regel.beschrijving}</p>
                      </div>

                      {/* Belangrijk Badge */}
                      {regel.belangrijk && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex-shrink-0">
                          Belangrijk
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Bottom Summary Card */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-6 bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="font-bold text-slate-800">Onthoud</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-slate-600">Respect & vertrouwen = meer vrijheid</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-slate-600">Hulp nodig? Vraag de begeleiding</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-slate-600">Agressie & drugs = directe consequenties</span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// TEAMUITJES PAGE
// ============================================
export const TeamuitjesPage = ({ onBack, user, isAdmin }) => {
  const [uitjes, setUitjes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUitje, setEditingUitje] = useState(null);
  const [form, setForm] = useState({ datum: '', activiteit: '', locatie: '', beschrijving: '' });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/teamuitjes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUitjes(response.data.uitjes || []);
    } catch (error) {
      setUitjes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const saveUitje = async () => {
    if (!form.datum || !form.activiteit) {
      toast.error('Vul datum en activiteit in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = { ...form, maand: '', groep: 'beide' };
      if (editingUitje) {
        await axios.put(`${API_URL}/api/teamuitjes/${editingUitje.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Uitje bijgewerkt');
      } else {
        await axios.post(`${API_URL}/api/teamuitjes`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Uitje toegevoegd');
      }
      setShowModal(false);
      setEditingUitje(null);
      setForm({ datum: '', activiteit: '', locatie: '', beschrijving: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const deleteUitje = async (id) => {
    if (!id) {
      toast.error('Kan uitje niet verwijderen: geen ID gevonden');
      return;
    }
    if (!window.confirm('Weet je zeker dat je dit uitje wilt verwijderen?')) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Je bent niet ingelogd');
        return;
      }
      const response = await axios.delete(`${API_URL}/api/teamuitjes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Uitje verwijderd');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
      toast.error(`Fout bij verwijderen: ${errorMsg}`);
    }
  };

  const openEdit = (uitje) => {
    setEditingUitje(uitje);
    setForm({
      datum: uitje.datum || '',
      activiteit: uitje.activiteit || '',
      locatie: uitje.locatie || '',
      beschrijving: uitje.beschrijving || ''
    });
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-lime-50/30 to-green-50/30" data-testid="teamuitjes-page">
      <PageHeader 
        title="Teamuitjes" 
        subtitle={`${uitjes.length} gepland`} 
        onBack={onBack}
        icon={PartyPopper}
        iconGradient="from-lime-500 to-lime-600"
        rightAction={
          <Button onClick={() => { setEditingUitje(null); setForm({ datum: '', activiteit: '', locatie: '', beschrijving: '' }); setShowModal(true); }} 
            className="h-10 px-4 rounded-xl bg-gradient-to-r from-lime-500 to-lime-600 text-white shadow-lg" data-testid="new-uitje-btn">
            <Plus className="w-4 h-4 mr-1" />Nieuw
          </Button>
        }
      />

      <div className="px-4 py-6 pb-32">
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-lime-500 to-lime-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <PartyPopper className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">Teamuitjes</h3>
              <p className="text-sm text-white/80">Activiteiten voor bewoners en begeleiders</p>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner color="lime" />
        ) : uitjes.length === 0 ? (
          <EmptyState 
            icon={PartyPopper} 
            title="Geen uitjes gepland" 
            subtitle="Plan een nieuw teamuitje in"
            action={
              <Button onClick={() => setShowModal(true)} className="rounded-xl bg-gradient-to-r from-lime-500 to-lime-600 text-white">
                <Plus className="w-4 h-4 mr-1" />Uitje Inplannen
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Aankomende uitjes</h2>
            {uitjes.map((uitje, index) => (
              <motion.div key={uitje.id || index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm" data-testid={`uitje-item-${index}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-lime-500 to-lime-600 flex items-center justify-center flex-shrink-0">
                    <PartyPopper className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900">{uitje.activiteit}</h3>
                    {uitje.beschrijving && <p className="text-sm text-slate-500 truncate">{uitje.beschrijving}</p>}
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar className="w-3.5 h-3.5" />{uitje.datum}
                      </span>
                      {uitje.locatie && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="w-3.5 h-3.5" />{uitje.locatie}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button 
                      onClick={() => openEdit(uitje)} 
                      className="p-2.5 rounded-xl bg-lime-100 hover:bg-lime-200 transition-colors" 
                      data-testid={`edit-uitje-${index}`}
                      title="Bewerken"
                    >
                      <Edit2 className="w-5 h-5 text-lime-700" />
                    </button>
                    <button 
                      onClick={() => deleteUitje(uitje.id)} 
                      className="p-2.5 rounded-xl bg-red-100 hover:bg-red-200 transition-colors" 
                      data-testid={`delete-uitje-${index}`}
                      title="Verwijderen"
                    >
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUitje ? 'Uitje Bewerken' : 'Nieuw Uitje'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Activiteit *</label>
              <Input value={form.activiteit} onChange={(e) => setForm({...form, activiteit: e.target.value})} placeholder="Bijv. Bowlen" className="mt-1" data-testid="uitje-activiteit-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Datum *</label>
              <Input type="date" value={form.datum} onChange={(e) => setForm({...form, datum: e.target.value})} className="mt-1" data-testid="uitje-datum-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Locatie</label>
              <Input value={form.locatie} onChange={(e) => setForm({...form, locatie: e.target.value})} placeholder="Bijv. Tilburg" className="mt-1" data-testid="uitje-locatie-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Beschrijving</label>
              <Input value={form.beschrijving} onChange={(e) => setForm({...form, beschrijving: e.target.value})} placeholder="Extra informatie..." className="mt-1" data-testid="uitje-beschrijving-input" />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuleren</Button>
            <Button onClick={saveUitje} className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white" data-testid="save-uitje-btn">
              <Save className="w-4 h-4 mr-1" />{editingUitje ? 'Opslaan' : 'Toevoegen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// VERGADERINGEN PAGE - Met CRUD voor alle begeleiders
// ============================================
export const VergaderingenPage = ({ onBack, user, isAdmin, selectedLocation }) => {
  const [vergaderingen, setVergaderingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVergadering, setEditingVergadering] = useState(null);
  const [form, setForm] = useState({ datum: '', tijd: '', type: 'Bewonersvergadering', voorzitter: '', beschrijving: '' });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
      const response = await axios.get(`${API_URL}/api/bewonersvergaderingen${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVergaderingen(response.data.vergaderingen || []);
    } catch (error) {
      setVergaderingen([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [selectedLocation]);

  const saveVergadering = async () => {
    if (!form.datum || !form.tijd) {
      toast.error('Vul datum en tijd in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingVergadering) {
        await axios.put(`${API_URL}/api/bewonersvergaderingen/${editingVergadering.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vergadering bijgewerkt');
      } else {
        await axios.post(`${API_URL}/api/bewonersvergaderingen`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vergadering ingepland');
      }
      setShowModal(false);
      setEditingVergadering(null);
      setForm({ datum: '', tijd: '', type: 'Bewonersvergadering', voorzitter: '', beschrijving: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const deleteVergadering = async (id) => {
    if (!id) {
      toast.error('Kan vergadering niet verwijderen: geen ID gevonden');
      return;
    }
    if (!window.confirm('Weet je zeker dat je deze vergadering wilt verwijderen?')) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Je bent niet ingelogd');
        return;
      }
      await axios.delete(`${API_URL}/api/bewonersvergaderingen/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vergadering verwijderd');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
      toast.error(`Fout bij verwijderen: ${errorMsg}`);
    }
  };

  const openEdit = (vergadering) => {
    setEditingVergadering(vergadering);
    setForm({
      datum: vergadering.datum || '',
      tijd: vergadering.tijd || '',
      type: vergadering.type || 'Bewonersvergadering',
      voorzitter: vergadering.voorzitter || '',
      beschrijving: vergadering.beschrijving || ''
    });
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/30 to-blue-50/30" data-testid="vergaderingen-page">
      <PageHeader 
        title="Bewonersvergaderingen" 
        subtitle={`${vergaderingen.length} gepland`}
        onBack={onBack}
        icon={Calendar}
        iconGradient="from-blue-500 to-blue-600"
        rightAction={
          <Button onClick={() => { setEditingVergadering(null); setForm({ datum: '', tijd: '', type: 'Bewonersvergadering', voorzitter: '', beschrijving: '' }); setShowModal(true); }} 
            className="h-10 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-500 text-white shadow-lg" data-testid="new-vergadering-btn">
            <Plus className="w-4 h-4 mr-1" />Nieuw
          </Button>
        }
      />

      <div className="px-4 py-6 pb-32">
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-500 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">Bewonersvergaderingen</h3>
              <p className="text-sm text-white/80">Vergaderingen met bewoners en begeleiding</p>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner color="sky" />
        ) : vergaderingen.length === 0 ? (
          <EmptyState 
            icon={Users} 
            title="Geen vergaderingen gepland" 
            subtitle="Plan een nieuwe bewonersvergadering in"
            action={
              <Button onClick={() => setShowModal(true)} className="rounded-xl bg-gradient-to-r from-sky-500 to-blue-500 text-white">
                <Plus className="w-4 h-4 mr-1" />Vergadering Inplannen
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Alle vergaderingen</h2>
            {vergaderingen.map((vergadering, index) => (
              <motion.div key={vergadering.id || index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm" data-testid={`vergadering-item-${index}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900">{vergadering.type || 'Bewonersvergadering'}</h3>
                    {vergadering.voorzitter && <p className="text-sm text-slate-500">Voorzitter: {vergadering.voorzitter}</p>}
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar className="w-3.5 h-3.5" />{vergadering.datum}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="w-3.5 h-3.5" />{vergadering.tijd}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button 
                      onClick={() => openEdit(vergadering)} 
                      className="p-2.5 rounded-xl bg-blue-100 hover:bg-blue-200 transition-colors" 
                      data-testid={`edit-vergadering-${index}`}
                      title="Bewerken"
                    >
                      <Edit2 className="w-5 h-5 text-blue-700" />
                    </button>
                    <button 
                      onClick={() => deleteVergadering(vergadering.id)} 
                      className="p-2.5 rounded-xl bg-red-100 hover:bg-red-200 transition-colors" 
                      data-testid={`delete-vergadering-${index}`}
                      title="Verwijderen"
                    >
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingVergadering ? 'Vergadering Bewerken' : 'Nieuwe Vergadering'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Type</label>
              <Input value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} placeholder="Bijv. Bewonersvergadering" className="mt-1" data-testid="vergadering-type-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Datum *</label>
                <Input type="date" value={form.datum} onChange={(e) => setForm({...form, datum: e.target.value})} className="mt-1" data-testid="vergadering-datum-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tijd *</label>
                <Input type="time" value={form.tijd} onChange={(e) => setForm({...form, tijd: e.target.value})} className="mt-1" data-testid="vergadering-tijd-input" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Voorzitter</label>
              <Input value={form.voorzitter} onChange={(e) => setForm({...form, voorzitter: e.target.value})} placeholder="Naam voorzitter" className="mt-1" data-testid="vergadering-voorzitter-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Beschrijving</label>
              <Input value={form.beschrijving} onChange={(e) => setForm({...form, beschrijving: e.target.value})} placeholder="Extra informatie..." className="mt-1" data-testid="vergadering-beschrijving-input" />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuleren</Button>
            <Button onClick={saveVergadering} className="bg-gradient-to-r from-sky-500 to-blue-500 text-white" data-testid="save-vergadering-btn">
              <Save className="w-4 h-4 mr-1" />{editingVergadering ? 'Opslaan' : 'Toevoegen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// FICHES PAGE - Met werkend fiches systeem
// ============================================
export const FichesPage = ({ onBack, user, selectedLocation }) => {
  const [fichesData, setFichesData] = useState({});
  const [loading, setLoading] = useState(true);
  const [clienten, setClienten] = useState([]);
  const [showVerzilverModal, setShowVerzilverModal] = useState(false);

  const MAX_FICHES_PER_DAG = 2;

  // Verzilver beloningen lijst
  const VERZILVER_BELONINGEN = [
    { fiches: 20, beloningen: ['Zakje chips', 'Snoepzak', 'Frisdrank'] },
    { fiches: 40, beloningen: ['Scheermes', 'Tandpasta', 'Tandenborstel', 'Shampoo of douchegel'] },
    { fiches: 60, beloningen: ['IJsje halen', 'Iets bestellen', 'Klein extra uitje'] },
    { fiches: 100, beloningen: ['Bioscoopkaartje'] },
    { fiches: 200, beloningen: ['Parfum (maximaal €50,-)'] },
  ];

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Haal fiches data op
      const fichesResponse = await axios.get(`${API_URL}/api/fiches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFichesData(fichesResponse.data.fiches || {});
      
      // Haal cliënten op voor deze locatie
      const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
      const overzichtResponse = await axios.get(`${API_URL}/api/begeleider/overzicht${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const alleClienten = overzichtResponse.data.alle_clienten || [];
      // Haal voornamen uit de cliënten data
      const clientNamen = alleClienten.map(c => c.voornaam || c.volledige_naam?.split(' ')[0] || 'Onbekend');
      setClienten(clientNamen);
      
    } catch (error) {
      console.error('Fout bij ophalen data:', error);
      setClienten([]);
      setFichesData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const giveFiche = async (client) => {
    const current = fichesData[client] || { vandaag: 0, totaal: 0 };
    if (current.vandaag >= MAX_FICHES_PER_DAG) {
      toast.error(`${client} heeft al ${MAX_FICHES_PER_DAG} fiches vandaag`);
      return;
    }
    
    // Optimistic update
    setFichesData(prev => ({
      ...prev,
      [client]: {
        vandaag: (prev[client]?.vandaag || 0) + 1,
        totaal: (prev[client]?.totaal || 0) + 1
      }
    }));

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/fiches/give`, {
        client: client,
        gegeven_door: user?.name || 'Begeleider'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Fiche gegeven aan ${client}!`);
    } catch (error) {
      // Revert
      setFichesData(prev => ({
        ...prev,
        [client]: {
          vandaag: Math.max(0, (prev[client]?.vandaag || 0) - 1),
          totaal: Math.max(0, (prev[client]?.totaal || 0) - 1)
        }
      }));
      toast.error('Fout bij opslaan');
    }
  };

  // Fiches intrekken functie
  const removeFiche = async (client, aantal = 1) => {
    const current = fichesData[client] || { vandaag: 0, totaal: 0 };
    if (current.totaal < aantal) {
      toast.error(`${client} heeft niet genoeg fiches om in te trekken`);
      return;
    }
    
    // Optimistic update
    setFichesData(prev => ({
      ...prev,
      [client]: {
        vandaag: prev[client]?.vandaag || 0,
        totaal: Math.max(0, (prev[client]?.totaal || 0) - aantal)
      }
    }));

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/fiches/remove`, {
        client: client,
        aantal: aantal,
        ingetrokken_door: user?.name || 'Begeleider'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${aantal} fiche(s) ingetrokken bij ${client}`);
    } catch (error) {
      // Revert
      setFichesData(prev => ({
        ...prev,
        [client]: {
          vandaag: prev[client]?.vandaag || 0,
          totaal: (prev[client]?.totaal || 0) + aantal
        }
      }));
      toast.error('Fout bij intrekken');
    }
  };

  const voorwaarden = [
    'Kamer opruimen',
    'Wasdag taken uitvoeren',
    'Corvee taken doen',
    'Doelen behaald'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-yellow-50/30 to-amber-50/30" data-testid="fiches-page">
      <PageHeader title="Fiches Systeem" subtitle="Beloningen" onBack={onBack} icon={Star} iconGradient="from-amber-400 to-amber-500" />

      <div className="px-4 py-6 pb-32">
        {/* Info banner */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">Beloningssysteem</h3>
              <p className="text-sm text-white/80">Max {MAX_FICHES_PER_DAG} fiches per dag • Geen max totaal</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-white/90">
            Klik op de sterren om een fiche te geven. Fiches kunnen verzilverd worden voor beloningen.
          </p>
        </div>

        {/* Verzilver knop */}
        <button
          onClick={() => setShowVerzilverModal(true)}
          className="w-full mb-6 p-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all"
          data-testid="verzilver-btn"
        >
          <Gift className="w-6 h-6" />
          <span className="font-semibold">Bekijk Verzilver Beloningen</span>
        </button>

        {/* Voorwaarden */}
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Wanneer een fiche geven?</h2>
          <div className="bg-white rounded-2xl border border-slate-200/50 shadow-sm p-4">
            <ul className="space-y-2">
              {voorwaarden.map((v, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  {v}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner color="yellow" />
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Cliënten</h2>
            {clienten.map((client, index) => {
              const clientData = fichesData[client] || { vandaag: 0, totaal: 0 };
              const fichesVandaag = clientData.vandaag || 0;
              const fichesTotaal = clientData.totaal || 0;
              
              return (
                <motion.div 
                  key={client} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: index * 0.05 }}
                  className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm" 
                  data-testid={`fiches-client-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">{client.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{client}</h3>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                            Vandaag: {fichesVandaag}/{MAX_FICHES_PER_DAG}
                          </span>
                          <span className="text-amber-600 font-medium">
                            Totaal: {fichesTotaal} fiches
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Klikbare sterren */}
                    <div className="flex items-center gap-1">
                      {[...Array(MAX_FICHES_PER_DAG)].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => i >= fichesVandaag && giveFiche(client)}
                          disabled={i < fichesVandaag}
                          className={`p-1 rounded-lg transition-all ${
                            i < fichesVandaag 
                              ? 'cursor-default' 
                              : 'hover:bg-amber-100 cursor-pointer hover:scale-110'
                          }`}
                          data-testid={`fiche-star-${client}-${i}`}
                        >
                          <Star 
                            className={`w-8 h-8 transition-all ${
                              i < fichesVandaag 
                                ? 'text-amber-400 fill-amber-400' 
                                : 'text-slate-300 hover:text-amber-300'
                            }`} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Fiches intrekken sectie */}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-sm text-slate-600">Fiches intrekken of verzilveren:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => removeFiche(client, 1)}
                        disabled={fichesTotaal < 1}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          fichesTotaal >= 1
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        }`}
                        data-testid={`remove-fiche-${client}`}
                      >
                        -1 Fiche
                      </button>
                      <button
                        onClick={() => {
                          const aantal = prompt(`Hoeveel fiches intrekken bij ${client}? (max: ${fichesTotaal})`);
                          if (aantal && !isNaN(aantal) && parseInt(aantal) > 0) {
                            removeFiche(client, parseInt(aantal));
                          }
                        }}
                        disabled={fichesTotaal < 1}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          fichesTotaal >= 1
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        Meerdere
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Verzilver Beloningen Modal */}
      <AnimatePresence>
        {showVerzilverModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowVerzilverModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl w-full max-w-md max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-amber-400 to-amber-500 p-4 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Fiches Verzilveren</h2>
                      <p className="text-sm text-white/80">Beloningssysteem</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowVerzilverModal(false)}
                    className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <p className="text-sm text-slate-600 text-center">
                  Per dag kan je <strong>2 fiches</strong> verdienen
                </p>
                
                {VERZILVER_BELONINGEN.map((tier, idx) => (
                  <div key={idx} className="p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-amber-600">{tier.fiches}</span>
                      <span className="text-amber-600 font-medium">fiches</span>
                    </div>
                    <ul className="space-y-1">
                      {tier.beloningen.map((beloning, bIdx) => (
                        <li key={bIdx} className="text-sm text-slate-700 flex items-center gap-2">
                          <Check className="w-4 h-4 text-amber-500" />
                          {beloning}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// TAKEN PAGE (Static task list for shifts)
// ============================================
export const TakenPage = ({ onBack, user }) => {
  const [completedTasks, setCompletedTasks] = useState([]);

  const shifts = {
    dagdienst: {
      name: 'Dagdienst',
      sections: [
        { name: 'Ochtend', timeRange: '07:45 - 12:00', tasks: [
          { time: '07:45', title: 'Aankomst & Overdracht lezen', important: true },
          { time: '07:45', title: 'Inloggen op groepsmail & groepstelefoon aanzetten', important: true },
          { time: '08:00', title: 'Ontbijt begeleiden' },
          { time: '08:00', title: 'Medicatie Ochtend', important: true },
          { time: '09:30', title: 'CONTACTMOMENT 1', important: true },
          { time: '12:00', title: 'Lunch' },
        ]},
        { name: 'Middag', timeRange: '13:00 - 17:30', tasks: [
          { time: '13:00', title: 'CONTACTMOMENT 2', important: true },
          { time: '15:00', title: 'Tussentijdse check' },
          { time: '17:00', title: 'Avondeten voorbereiden' },
        ]},
        { name: 'Avond', timeRange: '17:45 - 23:00', tasks: [
          { time: '17:45', title: 'CONTACTMOMENT 3', important: true },
          { time: '18:30', title: 'Medicatie Avond', important: true },
          { time: '20:00', title: 'Overdracht schrijven (deadline!)', important: true },
          { time: '23:00', title: 'Rapportage Zilliz (deadline!)', important: true },
        ]}
      ]
    }
  };

  const toggleTask = (taskId) => {
    setCompletedTasks(prev => prev.includes(taskId) ? prev.filter(t => t !== taskId) : [...prev, taskId]);
  };

  const currentShift = shifts.dagdienst;
  const totalTasks = currentShift.sections.reduce((acc, s) => acc + s.tasks.length, 0);
  const progress = Math.round((completedTasks.length / totalTasks) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30" data-testid="taken-page">
      <PageHeader title="Taken Vandaag" subtitle={`${completedTasks.length}/${totalTasks} voltooid`} onBack={onBack} icon={CheckSquare} iconGradient="from-emerald-500 to-emerald-600" />

      <div className="px-4 py-6 pb-32">
        <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-white/80">Voortgang</span>
            <span className="text-2xl font-bold">{progress}%</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} className="h-full bg-white rounded-full" />
          </div>
        </div>

        <div className="space-y-4">
          {currentShift.sections.map((section, sectionIndex) => (
            <motion.div key={section.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: sectionIndex * 0.1 }}>
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center justify-between">
                <span>{section.name}</span>
                <span className="text-xs font-normal text-slate-400">{section.timeRange}</span>
              </h2>
              <div className="bg-white rounded-2xl border border-slate-200/50 shadow-sm divide-y divide-slate-100">
                {section.tasks.map((task, taskIndex) => {
                  const taskId = `${sectionIndex}-${taskIndex}`;
                  const isCompleted = completedTasks.includes(taskId);
                  
                  return (
                    <button key={taskIndex} onClick={() => toggleTask(taskId)} 
                      className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left"
                      data-testid={`task-item-${sectionIndex}-${taskIndex}`}>
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                        isCompleted ? 'bg-emerald-500 border-emerald-500' : task.important ? 'border-amber-400' : 'border-slate-300'
                      }`}>
                        {isCompleted && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <span className={`font-medium ${isCompleted ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{task.title}</span>
                        {task.important && !isCompleted && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Belangrijk</span>}
                      </div>
                      <span className="text-xs text-slate-400">{task.time}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// CLIENTEN PAGE - Met klikbare cliënt details
// ============================================
export const ClientenPage = ({ onBack, user, selectedLocation }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [useFallback, setUseFallback] = useState(false);

  // Debug logging
  // DEBUG: console.log('ClientenPage rendered with selectedLocation:', selectedLocation);
  
  // Fallback client lijst voor als API faalt - ZONDER pb_er (die komt uit database koppelingen)
  // Ricardo van den Bergh en Joey Goorden verwijderd - niet meer in zorg
  const FALLBACK_CLIENTEN = {
    'groep-zwaluw': [
      { voornaam: 'Yassin', achternaam: 'Houba', kamer: 'Kamer 1', adres: 'Enschotsestraat 98' },
      { voornaam: 'Sietse', achternaam: 'Bouwman', kamer: 'Kamer 1', adres: 'Enschotsestraat 100' },
      { voornaam: 'Axcel', achternaam: 'Trinidad', kamer: 'Kamer 4', adres: 'Enschotsestraat 100' },
      { voornaam: 'Mahad', achternaam: 'Abib', kamer: 'Appartement', adres: 'Piusplein 62-31 (Ambulant)' }
    ],
    'groep-adelaars': [
      { voornaam: 'Rinaldo', achternaam: 'Bon', kamer: 'Kamer 1', adres: 'Ardennenlaan 68' },
      { voornaam: 'Faraah', achternaam: 'Omari', kamer: 'Kamer 2', adres: 'Ardennenlaan 68' },
      { voornaam: 'Mike', achternaam: 'Reenalda', kamer: 'Kamer 3', adres: 'Ardennenlaan 68' },
      { voornaam: 'Melvin', achternaam: 'Hartman', kamer: 'Kamer 4', adres: 'Ardennenlaan 68' },
      { voornaam: 'Julian', achternaam: 'Bovenberg', kamer: 'Kamer 6', adres: 'Ardennenlaan 68' }
    ]
  };

  // Client profielen met extra info
  const CLIENT_PROFILES = {
    // =====================================================
    // GROEP ZWALUW - Enschotsestraat 98 & 100
    // =====================================================
    'Yassin': {
      volledige_naam: 'Yassin Houba',
      initials: 'Y.H.',
      geboortedatum: '15-03-1998',
      kamer: 'Kamer 1',
      locatie: 'Enschotsestraat 98',
      pb_er: 'Serkan',
      diagnose: 'ASS',
      bijzonderheden: 'Houdt van structuur en voorspelbaarheid. Heeft soms moeite met prikkels.',
      interesses: 'Gaming, muziek, wandelen',
      aandachtspunten: ['Medicatie avond', 'Structuur is belangrijk', 'Korte duidelijke instructies'],
      netwerk: [
        { naam: 'Moeder Houba', relatie: 'Moeder (Wettelijk vertegenwoordiger)', telefoon: '06-12345678', email: 'moeder.houba@email.nl' },
        { naam: 'Vader Houba', relatie: 'Vader', telefoon: '06-87654321', email: '' },
        { naam: 'Huisarts Tilburg', relatie: 'Huisarts', telefoon: '013-5432100', email: 'huisarts@praktijktilburg.nl' }
      ],
      signaleringsplan: {
        groen: 'Rustig, maakt contact, volgt dagstructuur, slaapt goed',
        oranje: 'Terugtrekken, minder eten, korter van stof, slechter slapen',
        rood: 'Agressief gedrag, schreeuwen, spullen kapot maken, niet meer communiceren',
        interventies: ['Rust en ruimte geven', 'Prikkelarme omgeving', 'Niet forceren', 'Korte zinnen gebruiken']
      },
      financiele_afspraken: {
        leefgeld: '€50 per week',
        beheer: 'Zelf beheer met ondersteuning',
        pinpas: 'Ja, eigen beheer',
        sparen: '€25 per maand naar spaarrekening'
      }
    },
    // Ricardo van den Bergh verwijderd - niet meer in zorg
    'Sietse': {
      volledige_naam: 'Sietse Bouwmans',
      initials: 'S.B.',
      geboortedatum: '08-11-1997',
      kamer: 'Kamer 1',
      locatie: 'Enschotsestraat 100',
      pb_er: 'Karim',
      diagnose: 'ASS + ADHD',
      bijzonderheden: 'Energiek en enthousiast. Heeft baat bij beweging en buitenactiviteiten.',
      interesses: 'Fietsen, dieren, tuinieren',
      aandachtspunten: ['Zo nodig medicatie', 'Beweging inbouwen', 'Duidelijke dagstructuur'],
      netwerk: [
        { naam: 'Dhr. Bouwmans', relatie: 'Vader (Wettelijk vertegenwoordiger)', telefoon: '06-99887766', email: 'bouwmans@email.nl' },
        { naam: 'Mw. Bouwmans', relatie: 'Moeder', telefoon: '06-77889900', email: '' },
        { naam: 'Huisarts Goirle', relatie: 'Huisarts', telefoon: '013-5341234', email: '' }
      ],
      signaleringsplan: {
        groen: 'Actief, positief, helpt mee, slaapt goed',
        oranje: 'Onrustig, veel praten, moeite met concentratie, piekeren',
        rood: 'Hyperactief, niet te stoppen, impulsief gedrag, slapeloosheid',
        interventies: ['Beweging/sport aanbieden', 'Zo nodig medicatie', 'Rustige omgeving', 'Duidelijke grenzen']
      },
      financiele_afspraken: {
        leefgeld: '€45 per week',
        beheer: 'Volledig onder beheer begeleiding',
        pinpas: 'Nee, contant geld',
        sparen: '€20 per maand naar spaarrekening'
      }
    },
    'Richard': {
      initials: 'R.D.',
      geboortedatum: '30-05-1996',
      kamer: 'Kamer 2',
      locatie: 'Enschotsestraat 100',
      pb_er: 'Karim',
      diagnose: 'LVB',
      bijzonderheden: 'Rustig en vriendelijk. Houdt van vaste routines.',
      interesses: 'Lezen, puzzelen, tv kijken',
      aandachtspunten: ['Geen medicatie', 'Houdt van rust', 'Geduldig benaderen'],
      netwerk: [
        { naam: 'Broer Peter', relatie: 'Broer', telefoon: '06-22334455', email: '' },
        { naam: 'Huisarts Tilburg', relatie: 'Huisarts', telefoon: '013-5432100', email: '' }
      ],
      signaleringsplan: {
        groen: 'Rustig, tevreden, doet mee met activiteiten, slaapt goed',
        oranje: 'Stiller dan normaal, trekt zich terug, eet minder',
        rood: 'Huilen, niet meer praten, weigert te eten, sluit zich op',
        interventies: ['Rustig aanspreken', 'Niet teveel vragen stellen', 'Samen wandelen', 'Favoriete activiteit aanbieden']
      },
      financiele_afspraken: {
        leefgeld: '€40 per week',
        beheer: 'Volledig onder beheer begeleiding',
        pinpas: 'Nee, contant geld via begeleiding',
        sparen: '€15 per maand naar spaarrekening'
      }
    },
    'Axcel': {
      volledige_naam: 'Axcel Trinidad',
      initials: 'A.T.',
      geboortedatum: '14-09-2000',
      kamer: 'Kamer 4',
      locatie: 'Enschotsestraat 100',
      pb_er: 'Karim',
      diagnose: 'ASS',
      bijzonderheden: 'Creatief en nieuwsgierig. Kan goed zelfstandig werken aan taken.',
      interesses: 'Tekenen, gamen, YouTube',
      aandachtspunten: ['Medicatie ochtend en avond', 'Visuele ondersteuning helpt', 'Tijd geven om te schakelen'],
      netwerk: [
        { naam: 'Mw. Trinidad', relatie: 'Moeder (Wettelijk vertegenwoordiger)', telefoon: '06-44556677', email: 'trinidad@email.nl' },
        { naam: 'Dhr. Trinidad', relatie: 'Vader', telefoon: '06-77665544', email: '' },
        { naam: 'Huisarts Tilburg', relatie: 'Huisarts', telefoon: '013-5432100', email: '' }
      ],
      signaleringsplan: {
        groen: 'Creatief bezig, maakt oogcontact, volgt schema, slaapt door',
        oranje: 'Moeite met overgangen, gefixeerd op één ding, minder eten',
        rood: 'Overload, handen voor oren, weglopen, schreeuwen',
        interventies: ['Vooraankondigen bij overgangen', 'Rustige ruimte aanbieden', 'Visueel schema gebruiken', 'Niet aanraken']
      },
      financiele_afspraken: {
        leefgeld: '€55 per week',
        beheer: 'Gedeeld beheer met ouders',
        pinpas: 'Ja, ouders hebben inzage',
        sparen: '€25 per maand naar spaarrekening'
      }
    },
    // Ambulant cliënten onder verantwoordelijkheid Groep Zwaluw
    'Rudrix': {
      volledige_naam: 'Rudrix Fecunda',
      initials: 'R.F.',
      geboortedatum: '12-05-1995',
      kamer: 'Ambulant',
      locatie: 'Statenlaan 59 (Ambulant Roodborst)',
      pb_er: 'Karim',
      diagnose: 'LVB',
      bijzonderheden: 'Woont zelfstandig met ambulante ondersteuning vanuit Groep Zwaluw.',
      interesses: 'Koken, wandelen, sociale contacten',
      aandachtspunten: ['Zelfstandigheid stimuleren', 'Administratie ondersteuning', 'Sociale vaardigheden'],
      signaleringsplan: {
        groen: 'Zelfstandig, houdt afspraken na, positieve stemming',
        oranje: 'Vergeet afspraken, rommelig huis, verminderd contact',
        rood: 'Financiële problemen, isolement, verwaarlozing',
        interventies: ['Extra huisbezoek', 'Samen plannen maken', 'Contact met netwerk']
      },
      financiele_afspraken: {
        leefgeld: 'Zelf beheer',
        beheer: 'Zelfstandig met ondersteuning bij administratie',
        pinpas: 'Ja, eigen beheer',
        sparen: 'Eigen verantwoordelijkheid'
      }
    },
    'Mahad': {
      volledige_naam: 'Mahad Abib',
      initials: 'M.A.',
      geboortedatum: '22-07-1999',
      kamer: 'Ambulant',
      locatie: 'Piusplein 62-31 (Ambulant IJsvogel)',
      pb_er: 'Karim',
      diagnose: 'LVB',
      bijzonderheden: 'Sociaal en behulpzaam. Woont zelfstandig met ambulante ondersteuning vanuit Groep Zwaluw.',
      interesses: 'Voetbal, koken, films kijken',
      aandachtspunten: ['Dagstructuur ondersteuning', 'Herinneren aan afspraken', 'Positieve bekrachtiging'],
      signaleringsplan: {
        groen: 'Vrolijk, helpt anderen, praat veel, eet goed, houdt huis schoon',
        oranje: 'Stil, kijkt veel TV, reageert traag, eet minder, vergeet afspraken',
        rood: 'Huilen, boos worden, dingen vergeten, nachtmerries, isolement',
        interventies: ['Rustig gesprek', 'Afleiding bieden', 'Samen activiteit doen', 'Contact met netwerk']
      },
      financiele_afspraken: {
        leefgeld: '€60 per week',
        beheer: 'Gedeeld beheer met begeleiding',
        pinpas: 'Ja, met daglimiet van €50',
        sparen: '€30 per maand naar spaarrekening'
      }
    },
    // =====================================================
    // GROEP ADELAARS - Ardennenlaan 68
    // =====================================================
    'Melvin': {
      volledige_naam: 'Melvin Hartman',
      initials: 'M.H.',
      geboortedatum: '12-04-1998',
      kamer: 'Kamer 2',
      locatie: 'Ardennenlaan 68',
      pb_er: 'Nassim',
      diagnose: 'LVB',
      bijzonderheden: 'Vriendelijk en sociaal. Houdt van gezelschap en activiteiten.',
      interesses: 'Voetbal kijken, muziek, spelletjes',
      aandachtspunten: ['Structuur behouden', 'Positieve benadering', 'Sociale contacten stimuleren'],
      netwerk: [
        { naam: 'Jeroen Cloudt', relatie: 'Bewindvoerder', telefoon: '0318-745132', email: '' },
        { naam: 'Apotheek Piushaven', relatie: 'Apotheek', telefoon: '013-5358933', email: '' },
        { naam: 'Huisarts Poyraz', relatie: 'Huisartsenpraktijk', telefoon: '013-5422722', email: '' },
        { naam: 'Astrid Toonen', relatie: 'Mentor', telefoon: '06-18724093', email: '' },
        { naam: 'Sanne Tuijl', relatie: 'Moeder van Ex vriendin', telefoon: '06-51308820', email: '' }
      ],
      signaleringsplan: {
        groen: 'Vrolijk, actief, sociaal, slaapt goed',
        oranje: 'Stiller, trekt zich terug, minder eetlust',
        rood: 'Verdrietig, huilen, niet communiceren',
        interventies: ['Rustig gesprek', 'Activiteit aanbieden', 'Contact met netwerk']
      },
      financiele_afspraken: {
        leefgeld: '€50 per week',
        beheer: 'Gedeeld beheer',
        pinpas: 'Ja, met limiet',
        sparen: '€20 per maand'
      }
    },
    'Mike': {
      volledige_naam: 'Mike Reenalda',
      initials: 'M.R.',
      geboortedatum: '25-08-1997',
      kamer: 'Kamer 3',
      locatie: 'Ardennenlaan 68',
      pb_er: 'Redouan',
      diagnose: 'ASS',
      bijzonderheden: 'Rustig en precies. Houdt van vaste patronen.',
      interesses: 'Computers, lezen, wandelen',
      aandachtspunten: ['Vaste routine', 'Voorspelbaarheid', 'Duidelijke communicatie'],
      netwerk: [
        { naam: 'Anna de Jong', relatie: 'Moeder', telefoon: '06-21169389', email: '' },
        { naam: 'Nathalie Kirac', relatie: 'Curator', telefoon: '071-5820026', email: '' },
        { naam: 'Apotheek Piushaven', relatie: 'Apotheek', telefoon: '013-5358933', email: '' },
        { naam: 'Huisarts Poyraz', relatie: 'Huisarts', telefoon: '013-5422722', email: '' }
      ],
      signaleringsplan: {
        groen: 'Rustig, volgt routine, maakt contact',
        oranje: 'Onrustig bij verandering, herhalend gedrag',
        rood: 'Overprikkeld, terugtrekken, niet communiceren',
        interventies: ['Rust en ruimte', 'Schema volgen', 'Prikkelarme omgeving']
      },
      financiele_afspraken: {
        leefgeld: '€45 per week',
        beheer: 'Onder beheer',
        pinpas: 'Nee, contant',
        sparen: '€25 per maand'
      }
    },
    'Rinaldo': {
      volledige_naam: 'Rinaldo Bon',
      initials: 'R.B.',
      geboortedatum: '03-02-1999',
      kamer: 'Kamer 4',
      locatie: 'Ardennenlaan 68',
      pb_er: 'Ilias',
      diagnose: 'LVB + ADHD',
      bijzonderheden: 'Energiek en spontaan. Heeft baat bij beweging.',
      interesses: 'Sport, muziek, koken',
      aandachtspunten: ['Beweging inbouwen', 'Korte taken', 'Positieve bekrachtiging'],
      netwerk: [
        { naam: 'Maria', relatie: 'Moeder (Bewind/Mentor)', telefoon: '06-28251777', email: '' },
        { naam: 'Apotheek Piushaven', relatie: 'Apotheek', telefoon: '013-5358933', email: '' },
        { naam: 'Huisarts Poyraz', relatie: 'Huisarts', telefoon: '013-5422722', email: '' }
      ],
      signaleringsplan: {
        groen: 'Actief, vrolijk, helpt mee',
        oranje: 'Onrustig, moeite met concentratie',
        rood: 'Impulsief, boos, niet te stoppen',
        interventies: ['Sport/beweging', 'Afleiding', 'Rustige toon']
      },
      financiele_afspraken: {
        leefgeld: '€55 per week',
        beheer: 'Gedeeld beheer',
        pinpas: 'Ja, met daglimiet',
        sparen: '€30 per maand'
      }
    },
    'Joey': {
      volledige_naam: 'Joey Goordem',
      initials: 'J.G.',
      geboortedatum: '19-11-1998',
      kamer: 'Kamer 5',
      locatie: 'Ardennenlaan 68',
      pb_er: 'Ilias',
      diagnose: 'ASS',
      bijzonderheden: 'Creatief en artistiek. Houdt van tekenen en muziek.',
      interesses: 'Tekenen, gitaar spelen, films',
      aandachtspunten: ['Creatieve uitlaatklep', 'Rust momenten', 'Niet teveel prikkels'],
      netwerk: [
        { naam: 'Dhr. Goordem', relatie: 'Vader (Wettelijk vertegenwoordiger)', telefoon: '06-88776655', email: 'goordem@email.nl' },
        { naam: 'Zus Lisa', relatie: 'Zus', telefoon: '06-11224433', email: 'lisagoordem@email.nl' },
        { naam: 'Muziektherapeut', relatie: 'Therapeut', telefoon: '06-99001122', email: '' },
        { naam: 'Huisarts Tilburg West', relatie: 'Huisarts', telefoon: '013-5441234', email: '' }
      ],
      signaleringsplan: {
        groen: 'Creatief bezig, ontspannen, communiceert goed',
        oranje: 'Stil, terugtrekken, minder creatief',
        rood: 'Overload, handen oren, schreeuwen',
        interventies: ['Rustige ruimte', 'Creatieve activiteit', 'Muziek luisteren']
      },
      financiele_afspraken: {
        leefgeld: '€50 per week',
        beheer: 'Zelf beheer met ondersteuning',
        pinpas: 'Ja',
        sparen: '€25 per maand'
      }
    },
    'Faraah': {
      volledige_naam: 'Faraah Ibrahim',
      initials: 'F.I.',
      geboortedatum: '07-06-2000',
      kamer: 'Kamer 7',
      locatie: 'Ardennenlaan 68',
      pb_er: 'Ilias',
      diagnose: 'LVB',
      bijzonderheden: 'Zorgzaam en behulpzaam. Houdt van gezelligheid.',
      interesses: 'Koken, winkelen, series kijken',
      aandachtspunten: ['Sociale contacten', 'Zelfvertrouwen', 'Positieve feedback'],
      netwerk: [
        { naam: 'Apotheek Piushaven', relatie: 'Apotheek', telefoon: '013-5358933', email: '' },
        { naam: 'Advocaat Nunnikhoven', relatie: 'Advocaat', telefoon: '013-5351098', email: '' },
        { naam: 'Thijs Pellis-Klein', relatie: 'Bewindvoering', telefoon: '06-41844091', email: '' },
        { naam: 'Huisartsenpraktijk Tilburg-West', relatie: 'Huisarts', telefoon: '013-4682984', email: '' },
        { naam: 'Michiel van Genugten', relatie: 'Tandarts 013', telefoon: '013-5424562', email: '' }
      ],
      signaleringsplan: {
        groen: 'Vrolijk, helpt anderen, praat veel',
        oranje: 'Onzeker, vraagt veel bevestiging',
        rood: 'Huilen, terugtrekken, niet eten',
        interventies: ['Geruststelling', 'Complimenten', 'Samen activiteit']
      },
      financiele_afspraken: {
        leefgeld: '€45 per week',
        beheer: 'Gedeeld beheer',
        pinpas: 'Ja, met limiet',
        sparen: '€20 per maand'
      }
    },
    'Julian': {
      volledige_naam: 'Julian Bovenberg',
      initials: 'J.B.',
      geboortedatum: '15-03-1999',
      kamer: 'Kamer 6',
      locatie: 'Ardennenlaan 68',
      pb_er: 'Ilias',
      diagnose: 'ASS',
      bijzonderheden: 'Rustige cliënt. Houdt van structuur en duidelijkheid.',
      interesses: 'Gamen, muziek luisteren, wandelen',
      aandachtspunten: ['Dagstructuur', 'Sociale interactie', 'Zelfstandigheid vergroten'],
      netwerk: [
        { naam: 'Mw. Bovenberg', relatie: 'Moeder', telefoon: '06-12345678', email: 'bovenberg@email.nl' },
        { naam: 'Huisarts Tilburg West', relatie: 'Huisarts', telefoon: '013-5441234', email: '' }
      ],
      signaleringsplan: {
        groen: 'Rustig, volgt dagritme, communiceert goed',
        oranje: 'Terugtrekken, minder communicatie, onrustig',
        rood: 'Agressief, niet aanspreekbaar, zelfverwaarlozing',
        interventies: ['Rustige omgeving', 'Duidelijke structuur bieden', 'Een-op-een gesprek']
      },
      financiele_afspraken: {
        leefgeld: '€40 per week',
        beheer: 'Bewindvoerder',
        pinpas: 'Ja, met limiet',
        sparen: '€25 per maand'
      }
    },
    // =====================================================
    // AMBULANT ROODBORST - Statenlaan 59
    // =====================================================
    'Rudrix': {
      volledige_naam: 'Rudrix Fecunda',
      initials: 'R.F.',
      geboortedatum: '12-05-1995',
      kamer: 'Appartement',
      locatie: 'Statenlaan 59',
      pb_er: 'Karim',
      diagnose: 'LVB',
      bijzonderheden: 'Woont zelfstandig met ambulante ondersteuning.',
      interesses: 'Koken, wandelen, sociale contacten',
      aandachtspunten: ['Zelfstandigheid stimuleren', 'Administratie ondersteuning', 'Sociale vaardigheden'],
      signaleringsplan: {
        groen: 'Zelfstandig, houdt afspraken na, positieve stemming',
        oranje: 'Vergeet afspraken, rommelig huis, verminderd contact',
        rood: 'Financiële problemen, isolement, verwaarlozing',
        interventies: ['Extra huisbezoek', 'Samen plannen maken', 'Contact met netwerk']
      },
      financiele_afspraken: {
        leefgeld: 'Zelf beheer',
        beheer: 'Zelfstandig met ondersteuning bij administratie',
        pinpas: 'Ja, eigen beheer',
        sparen: 'Eigen verantwoordelijkheid'
      }
    },
    // =====================================================
    // AMBULANT IJSVOGEL - Piusplein 62-31
    // =====================================================
    'Mahad': {
      volledige_naam: 'Mahad Abib',
      initials: 'M.A.',
      geboortedatum: '22-07-1999',
      kamer: 'Appartement',
      locatie: 'Piusplein 62-31',
      pb_er: 'Karim',
      diagnose: 'LVB',
      bijzonderheden: 'Sociaal en behulpzaam. Woont zelfstandig met ambulante ondersteuning.',
      interesses: 'Voetbal, koken, films kijken',
      aandachtspunten: ['Dagstructuur ondersteuning', 'Herinneren aan afspraken', 'Positieve bekrachtiging'],
      signaleringsplan: {
        groen: 'Vrolijk, helpt anderen, praat veel, eet goed, houdt huis schoon',
        oranje: 'Stil, kijkt veel TV, reageert traag, eet minder, vergeet afspraken',
        rood: 'Huilen, boos worden, dingen vergeten, nachtmerries, isolement',
        interventies: ['Rustig gesprek', 'Afleiding bieden', 'Samen activiteit doen', 'Contact met netwerk']
      },
      financiele_afspraken: {
        leefgeld: '€60 per week',
        beheer: 'Gedeeld beheer met begeleiding',
        pinpas: 'Ja, met daglimiet van €50',
        sparen: '€30 per maand naar spaarrekening'
      }
    },
  };

  useEffect(() => {
    // Reset loading state when location changes
    setLoading(true);
    setData(null);
    setUseFallback(false);
    
    const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
    
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        // Stuur selectedLocation mee als query parameter voor zorgmanager/admin
        const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
        // DEBUG: console.log('ClientenPage fetching data with URL:', `${API_URL}/api/begeleider/overzicht${locationParam}`);
        const response = await axios.get(`${API_URL}/api/begeleider/overzicht${locationParam}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        });
        // DEBUG: console.log('ClientenPage received locatie:', response.data?.locatie?.naam, 'clienten:', response.data?.alle_clienten?.map(c => c.voornaam));
        
        // Check if we got valid data
        if (response.data?.alle_clienten && response.data.alle_clienten.length > 0) {
          setData(response.data);
          setUseFallback(false);
        } else {
          console.warn('API returned empty clienten, using fallback');
          setUseFallback(true);
        }
      } catch (error) {
        console.error('ClientenPage fetch error:', error);
        // Gebruik fallback data bij fout
        setUseFallback(true);
        if (error.response?.status === 401) {
          toast.error('Sessie verlopen, log opnieuw in');
        } else {
          // Toon geen error toast, gebruik gewoon fallback
          // DEBUG: console.log('Using fallback client data for location:', locationSlug);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedLocation, user?.selected_location]);

  // Bepaal welke locatie we gebruiken
  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
  
  // Gebruik API data of fallback data
  const clienten = useFallback 
    ? (FALLBACK_CLIENTEN[locationSlug] || FALLBACK_CLIENTEN['groep-zwaluw'])
    : (data?.alle_clienten || []);
    
  // Haal de dynamische koppelingen op van de API
  const koppelingen = data?.pb_overzicht?.alle_koppelingen || [];

  // Maak een lookup map van client naar begeleider
  // Ondersteunt ook gecombineerde namen zoals "Sietse & Mahad"
  const pbKoppelingMap = {};
  koppelingen.forEach(k => {
    if (k.client && k.begeleider) {
      // Split op " & " voor gecombineerde namen
      const clients = k.client.split(' & ').map(c => c.trim().toLowerCase());
      clients.forEach(clientName => {
        if (clientName) {
          pbKoppelingMap[clientName] = k.begeleider;
        }
      });
    }
  });

  const getClientProfile = (voornaam) => {
    const profile = CLIENT_PROFILES[voornaam] || null;
    if (profile) {
      // BELANGRIJK: Alleen de pb_er tonen als er een ECHTE koppeling in de database staat.
      // Als er geen koppeling is, toon dan GEEN pb_er (niet de hardgecodeerde waarde).
      const dbKoppeling = pbKoppelingMap[voornaam?.toLowerCase()];
      if (dbKoppeling) {
        // Er is een koppeling in de database - toon deze
        return { ...profile, pb_er: dbKoppeling };
      } else {
        // Geen koppeling in de database - verwijder de pb_er
        const { pb_er, ...profileWithoutPbEr } = profile;
        return profileWithoutPbEr;
      }
    }
    return profile;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-purple-50/30" data-testid="clienten-page">
      {/* Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-72 h-72 bg-violet-300/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-purple-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-fuchsia-300/15 rounded-full blur-3xl" />
      </div>
      
      <PageHeader title="Cliënten" subtitle={`${clienten.length} cliënten`} onBack={onBack} icon={Users} iconGradient="from-violet-500 to-violet-600" />

      <div className="px-4 py-6 pb-32 relative z-10">
        {loading ? (
          <LoadingSpinner color="violet" />
        ) : clienten.length === 0 ? (
          <EmptyState icon={Users} title="Geen cliënten" subtitle="Er zijn geen cliënten gevonden" />
        ) : (
          <div className="space-y-3">
            {clienten.map((client, index) => {
              const profile = getClientProfile(client.voornaam);
              return (
                <motion.button 
                  key={client.voornaam || index} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 4, scale: 1.01 }}
                  onClick={() => setSelectedClient(profile || { volledige_naam: client.voornaam, initials: client.initials })}
                  className="w-full p-4 rounded-2xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-sm hover:shadow-lg hover:border-violet-200 transition-all text-left" 
                  data-testid={`client-item-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl blur-md opacity-30" />
                      <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                        <span className="text-xl font-bold text-white">{client.voornaam?.charAt(0) || '?'}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900">{client.voornaam || client.volledige_naam}</h3>
                      <p className="text-sm text-slate-500">{profile?.kamer || ''} • {profile?.locatie || ''}</p>
                      {profile?.pb_er && (
                        <span className="inline-flex items-center mt-1 text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                          pb'er: {profile.pb_er}
                        </span>
                      )}
                    </div>
                    <ChevronLeft className="w-5 h-5 text-slate-400 rotate-180" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      {/* Client Detail Modal */}
      <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
          {selectedClient && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{selectedClient.volledige_naam?.charAt(0) || '?'}</span>
                  </div>
                  <div>
                    <DialogTitle className="text-xl">{selectedClient.volledige_naam}</DialogTitle>
                    <p className="text-sm text-slate-500">{selectedClient.initials}</p>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="mt-4 space-y-4">
                {/* Basis Info */}
                <div className="grid grid-cols-2 gap-3">
                  {selectedClient.geboortedatum && (
                    <div className="p-3 rounded-xl bg-slate-50">
                      <p className="text-xs text-slate-500">Geboortedatum</p>
                      <p className="font-medium text-slate-800">{selectedClient.geboortedatum}</p>
                    </div>
                  )}
                  {selectedClient.kamer && (
                    <div className="p-3 rounded-xl bg-slate-50">
                      <p className="text-xs text-slate-500">Kamer</p>
                      <p className="font-medium text-slate-800">{selectedClient.kamer}</p>
                    </div>
                  )}
                  {selectedClient.locatie && (
                    <div className="p-3 rounded-xl bg-slate-50">
                      <p className="text-xs text-slate-500">Locatie</p>
                      <p className="font-medium text-slate-800">{selectedClient.locatie}</p>
                    </div>
                  )}
                  {selectedClient.pb_er && (
                    <div className="p-3 rounded-xl bg-violet-50">
                      <p className="text-xs text-violet-600">PB'er</p>
                      <p className="font-medium text-violet-800">{selectedClient.pb_er}</p>
                    </div>
                  )}
                </div>

                {/* Diagnose */}
                {selectedClient.diagnose && (
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium">Diagnose</p>
                    <p className="text-slate-800">{selectedClient.diagnose}</p>
                  </div>
                )}

                {/* Bijzonderheden */}
                {selectedClient.bijzonderheden && (
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500 font-medium mb-1">Bijzonderheden</p>
                    <p className="text-sm text-slate-700">{selectedClient.bijzonderheden}</p>
                  </div>
                )}

                {/* Interesses */}
                {selectedClient.interesses && (
                  <div className="p-3 rounded-xl bg-emerald-50">
                    <p className="text-xs text-emerald-600 font-medium mb-1">Interesses</p>
                    <p className="text-sm text-slate-700">{selectedClient.interesses}</p>
                  </div>
                )}

                {/* Aandachtspunten */}
                {selectedClient.aandachtspunten && selectedClient.aandachtspunten.length > 0 && (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200">
                    <p className="text-xs text-rose-600 font-medium mb-2">Aandachtspunten</p>
                    <ul className="space-y-1">
                      {selectedClient.aandachtspunten.map((punt, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-rose-500 mt-0.5">•</span>
                          {punt}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Signaleringsplan */}
                {selectedClient.signaleringsplan && (
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-600 font-bold mb-3 uppercase">Signaleringsplan</p>
                    <div className="space-y-2">
                      <div className="p-2 rounded-lg bg-emerald-50 border-l-4 border-emerald-500">
                        <p className="text-xs font-bold text-emerald-700">GROEN - Stabiel</p>
                        <p className="text-xs text-slate-600">{selectedClient.signaleringsplan.groen}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-orange-50 border-l-4 border-orange-500">
                        <p className="text-xs font-bold text-orange-700">ORANJE - Aandacht</p>
                        <p className="text-xs text-slate-600">{selectedClient.signaleringsplan.oranje}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-red-50 border-l-4 border-red-500">
                        <p className="text-xs font-bold text-red-700">ROOD - Actie nodig</p>
                        <p className="text-xs text-slate-600">{selectedClient.signaleringsplan.rood}</p>
                      </div>
                      {selectedClient.signaleringsplan.interventies && (
                        <div className="mt-2 p-2 rounded-lg bg-violet-50">
                          <p className="text-xs font-bold text-violet-700 mb-1">Interventies</p>
                          <ul className="text-xs text-slate-600 space-y-0.5">
                            {selectedClient.signaleringsplan.interventies.map((int, i) => (
                              <li key={i}>• {int}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Netwerk / Contacten */}
                {selectedClient.netwerk && selectedClient.netwerk.length > 0 && (
                  <div className="p-3 rounded-xl bg-cyan-50 border border-cyan-200">
                    <p className="text-xs text-cyan-600 font-bold mb-3 uppercase flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Netwerk & Contacten
                    </p>
                    <div className="space-y-2">
                      {selectedClient.netwerk.map((contact, i) => (
                        <div key={i} className="p-2 rounded-lg bg-white border border-cyan-100">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-slate-800">{contact.naam}</p>
                              <p className="text-xs text-cyan-600">{contact.relatie}</p>
                            </div>
                          </div>
                          <div className="mt-2 space-y-1">
                            {contact.telefoon && (
                              <a 
                                href={`tel:${contact.telefoon}`} 
                                className="flex items-center gap-2 text-xs text-slate-600 hover:text-cyan-600"
                              >
                                <Phone className="w-3 h-3" />
                                {contact.telefoon}
                              </a>
                            )}
                            {contact.email && (
                              <a 
                                href={`mailto:${contact.email}`}
                                className="flex items-center gap-2 text-xs text-slate-600 hover:text-cyan-600"
                              >
                                <Mail className="w-3 h-3" />
                                {contact.email}
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Financiële Afspraken */}
                {selectedClient.financiele_afspraken && (
                  <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-200">
                    <p className="text-xs text-indigo-600 font-bold mb-3 uppercase">Financiële Afspraken</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 rounded-lg bg-white">
                        <p className="text-xs text-slate-500">Leefgeld</p>
                        <p className="text-sm font-medium text-slate-800">{selectedClient.financiele_afspraken.leefgeld}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-white">
                        <p className="text-xs text-slate-500">Beheer</p>
                        <p className="text-sm font-medium text-slate-800">{selectedClient.financiele_afspraken.beheer}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-white">
                        <p className="text-xs text-slate-500">Pinpas</p>
                        <p className="text-sm font-medium text-slate-800">{selectedClient.financiele_afspraken.pinpas}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-white">
                        <p className="text-xs text-slate-500">Sparen</p>
                        <p className="text-sm font-medium text-slate-800">{selectedClient.financiele_afspraken.sparen}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// TEAMOVERLEG PAGE - Alleen zorgmanager kan inplannen
// ============================================
export const TeamoverlegPage = ({ onBack, user, isAdmin }) => {
  const [overleggen, setOverleggen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOverleg, setEditingOverleg] = useState(null);
  const [form, setForm] = useState({ datum: '', tijd: '', locatie: '', agendapunten: '' });

  // Check if user is zorgmanager
  const isZorgmanager = user?.role === 'zorgmanager' || user?.email === 'zorgmanager@zorgvleugel.nl' || isAdmin;

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/begeleidingsoverleg`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverleggen(response.data.overleggen || []);
    } catch (error) {
      setOverleggen([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const saveOverleg = async () => {
    if (!form.datum || !form.tijd) {
      toast.error('Vul datum en tijd in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingOverleg) {
        await axios.put(`${API_URL}/api/begeleidingsoverleg/${editingOverleg.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Overleg bijgewerkt');
      } else {
        await axios.post(`${API_URL}/api/begeleidingsoverleg`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Overleg ingepland');
      }
      setShowModal(false);
      setEditingOverleg(null);
      setForm({ datum: '', tijd: '', locatie: '', agendapunten: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const deleteOverleg = async (id) => {
    if (!id) {
      toast.error('Kan overleg niet verwijderen: geen ID gevonden');
      return;
    }
    if (!window.confirm('Weet je zeker dat je dit overleg wilt verwijderen?')) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Je bent niet ingelogd');
        return;
      }
      await axios.delete(`${API_URL}/api/begeleidingsoverleg/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Overleg verwijderd');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
      toast.error(`Fout bij verwijderen: ${errorMsg}`);
    }
  };

  const openEdit = (overleg) => {
    setEditingOverleg(overleg);
    setForm({
      datum: overleg.datum || '',
      tijd: overleg.tijd || '',
      locatie: overleg.locatie || '',
      agendapunten: overleg.agendapunten || ''
    });
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100/50 to-gray-100/50" data-testid="teamoverleg-page">
      <PageHeader 
        title="Begeleidingsoverleg" 
        subtitle="Intern overleg" 
        onBack={onBack}
        icon={Users}
        iconGradient="from-slate-400 to-slate-500"
        rightAction={isZorgmanager && (
          <Button onClick={() => { setEditingOverleg(null); setForm({ datum: '', tijd: '', locatie: '', agendapunten: '' }); setShowModal(true); }} 
            className="h-10 px-4 rounded-xl bg-gradient-to-r from-slate-600 to-gray-700 text-white shadow-lg" data-testid="new-overleg-btn">
            <Plus className="w-4 h-4 mr-1" />Nieuw
          </Button>
        )}
      />

      <div className="px-4 py-6 pb-32">
        {/* Info banner */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-slate-600 to-gray-700 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">Begeleidingsoverleg</h3>
              <p className="text-sm text-white/70">Maandelijkse besprekingen met het team</p>
            </div>
          </div>
          {!isZorgmanager && (
            <p className="mt-3 text-xs text-white/60 bg-white/10 rounded-lg p-2">
              ℹ️ Alleen de zorgmanager kan overleggen inplannen
            </p>
          )}
        </div>

        {loading ? (
          <LoadingSpinner color="slate" />
        ) : overleggen.length === 0 ? (
          <EmptyState 
            icon={Users} 
            title="Geen begeleidingsoverleg gepland" 
            subtitle={isZorgmanager ? "Plan een nieuw overleg in" : "Er zijn nog geen overleggen ingepland door de zorgmanager"}
            action={isZorgmanager && (
              <Button onClick={() => setShowModal(true)} className="rounded-xl bg-gradient-to-r from-slate-600 to-gray-700 text-white">
                <Plus className="w-4 h-4 mr-1" />Overleg Inplannen
              </Button>
            )}
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Geplande Overleggen</h2>
            {overleggen.map((overleg, index) => (
              <motion.div 
                key={overleg.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm"
                data-testid={`overleg-item-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-500 to-gray-600 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Begeleidingsoverleg</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-sm text-slate-500">
                          <Calendar className="w-3.5 h-3.5" />{overleg.datum}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-slate-500">
                          <Clock className="w-3.5 h-3.5" />{overleg.tijd}
                        </span>
                      </div>
                      {overleg.locatie && (
                        <p className="text-sm text-slate-500 mt-1">
                          <MapPin className="w-3.5 h-3.5 inline mr-1" />{overleg.locatie}
                        </p>
                      )}
                      {overleg.agendapunten && (
                        <p className="text-xs text-slate-400 mt-2 whitespace-pre-wrap">{overleg.agendapunten}</p>
                      )}
                    </div>
                  </div>
                  {isZorgmanager && (
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(overleg)} className="p-2 rounded-lg hover:bg-slate-100">
                        <Edit2 className="w-4 h-4 text-slate-500" />
                      </button>
                      <button onClick={() => deleteOverleg(overleg.id)} className="p-2 rounded-lg hover:bg-red-100">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal voor nieuw/bewerken */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>{editingOverleg ? 'Overleg Bewerken' : 'Nieuw Begeleidingsoverleg'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Datum</label>
                <Input type="date" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} className="h-12 rounded-xl mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tijd</label>
                <Input type="time" value={form.tijd} onChange={(e) => setForm({ ...form, tijd: e.target.value })} className="h-12 rounded-xl mt-1" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Locatie</label>
              <Input value={form.locatie} onChange={(e) => setForm({ ...form, locatie: e.target.value })} placeholder="bijv. Kantoor, Teams" className="h-12 rounded-xl mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Agendapunten</label>
              <textarea value={form.agendapunten} onChange={(e) => setForm({ ...form, agendapunten: e.target.value })} placeholder="Onderwerpen voor dit overleg..." className="w-full h-24 p-3 rounded-xl border border-slate-200 mt-1 text-sm resize-none" />
            </div>
            <Button onClick={saveOverleg} className="w-full h-12 rounded-xl bg-gradient-to-r from-slate-600 to-gray-700">
              <Save className="w-4 h-4 mr-2" />{editingOverleg ? 'Opslaan' : 'Inplannen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// MDO PAGE - Alleen zorgmanager kan inplannen
// ============================================
export const MDOPage = ({ onBack, user, isAdmin }) => {
  const [mdos, setMdos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMdo, setEditingMdo] = useState(null);
  const [form, setForm] = useState({ client: '', datum: '', tijd: '', locatie: '', bespreekpunten: '' });
  const [clienten, setClienten] = useState([]);

  const isZorgmanager = user?.role === 'zorgmanager' || user?.email === 'zorgmanager@zorgvleugel.nl' || isAdmin;
  
  // Alle clienten van beide groepen voor MDO (gedeeld overzicht)
  const ALLE_CLIENTEN = [
    // Groep Zwaluw
    'Yassin', 'Mahad', 'Sietse', 'Richard', 'Axcel', 'Ricardo',
    // Groep Adelaars
    'Melvin', 'Mike', 'Rinaldo', 'Joey', 'Faraah'
  ];

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/mdo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMdos(response.data.mdos || []);
    } catch (error) {
      setMdos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const saveMdo = async () => {
    if (!form.client || !form.datum) {
      toast.error('Selecteer een cliënt en datum');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingMdo) {
        await axios.put(`${API_URL}/api/mdo/${editingMdo.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('MDO bijgewerkt');
      } else {
        await axios.post(`${API_URL}/api/mdo`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('MDO ingepland');
      }
      setShowModal(false);
      setEditingMdo(null);
      setForm({ client: '', datum: '', tijd: '', locatie: '', bespreekpunten: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const deleteMdo = async (id) => {
    if (!id) {
      toast.error('Kan MDO niet verwijderen: geen ID gevonden');
      return;
    }
    if (!window.confirm('Weet je zeker dat je dit MDO wilt verwijderen?')) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Je bent niet ingelogd');
        return;
      }
      await axios.delete(`${API_URL}/api/mdo/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('MDO verwijderd');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
      toast.error(`Fout bij verwijderen: ${errorMsg}`);
    }
  };

  const openEdit = (mdo) => {
    setEditingMdo(mdo);
    setForm({
      client: mdo.client || '',
      datum: mdo.datum || '',
      tijd: mdo.tijd || '',
      locatie: mdo.locatie || '',
      bespreekpunten: mdo.bespreekpunten || ''
    });
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/30" data-testid="mdo-page">
      <PageHeader 
        title="MDO" 
        subtitle="Multidisciplinair Overleg" 
        onBack={onBack}
        icon={FileText}
        iconGradient="from-green-600 to-green-700"
        rightAction={isZorgmanager && (
          <Button onClick={() => { setEditingMdo(null); setForm({ client: '', datum: '', tijd: '', locatie: '', bespreekpunten: '' }); setShowModal(true); }} 
            className="h-10 px-4 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg" data-testid="new-mdo-btn">
            <Plus className="w-4 h-4 mr-1" />Nieuw
          </Button>
        )}
      />

      <div className="px-4 py-6 pb-32">
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-green-600 to-green-700 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">Multidisciplinair Overleg</h3>
              <p className="text-sm text-white/70">Cliëntbesprekingen met het zorgteam</p>
            </div>
          </div>
          {!isZorgmanager && (
            <p className="mt-3 text-xs text-white/60 bg-white/10 rounded-lg p-2">
              ℹ️ Alleen de zorgmanager kan MDO's inplannen
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm">
            <p className="text-3xl font-bold text-green-600">{mdos.length}</p>
            <p className="text-sm text-slate-500">Gepland</p>
          </div>
          <div className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm">
            <p className="text-3xl font-bold text-slate-800">{ALLE_CLIENTEN.length}</p>
            <p className="text-sm text-slate-500">Cliënten</p>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner color="green" />
        ) : mdos.length === 0 ? (
          <EmptyState 
            icon={FileText} 
            title="Geen MDO gepland" 
            subtitle={isZorgmanager ? "Plan een nieuw MDO in" : "Er zijn nog geen MDO's ingepland door de zorgmanager"}
            action={isZorgmanager && (
              <Button onClick={() => setShowModal(true)} className="rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white">
                <Plus className="w-4 h-4 mr-1" />MDO Inplannen
              </Button>
            )}
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Geplande MDO's</h2>
            {mdos.map((mdo, index) => (
              <motion.div 
                key={mdo.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm"
                data-testid={`mdo-item-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">MDO: {mdo.client}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-sm text-slate-500">
                          <Calendar className="w-3.5 h-3.5" />{mdo.datum}
                        </span>
                        {mdo.tijd && <span className="flex items-center gap-1 text-sm text-slate-500">
                          <Clock className="w-3.5 h-3.5" />{mdo.tijd}
                        </span>}
                      </div>
                      {mdo.locatie && (
                        <p className="text-sm text-slate-500 mt-1">
                          <MapPin className="w-3.5 h-3.5 inline mr-1" />{mdo.locatie}
                        </p>
                      )}
                      {mdo.bespreekpunten && (
                        <p className="text-xs text-slate-400 mt-2 whitespace-pre-wrap">{mdo.bespreekpunten}</p>
                      )}
                    </div>
                  </div>
                  {isZorgmanager && (
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(mdo)} className="p-2 rounded-lg hover:bg-slate-100">
                        <Edit2 className="w-4 h-4 text-slate-500" />
                      </button>
                      <button onClick={() => deleteMdo(mdo.id)} className="p-2 rounded-lg hover:bg-red-100">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal voor nieuw/bewerken */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>{editingMdo ? 'MDO Bewerken' : 'Nieuw MDO'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Cliënt</label>
              <select value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}
                className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200">
                <option value="">Selecteer cliënt</option>
                {ALLE_CLIENTEN.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Datum</label>
                <Input type="date" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} className="h-12 rounded-xl mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tijd</label>
                <Input type="time" value={form.tijd} onChange={(e) => setForm({ ...form, tijd: e.target.value })} className="h-12 rounded-xl mt-1" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Locatie</label>
              <Input value={form.locatie} onChange={(e) => setForm({ ...form, locatie: e.target.value })} placeholder="bijv. Kantoor" className="h-12 rounded-xl mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Bespreekpunten</label>
              <textarea value={form.bespreekpunten} onChange={(e) => setForm({ ...form, bespreekpunten: e.target.value })} placeholder="Wat moet besproken worden..." className="w-full h-24 p-3 rounded-xl border border-slate-200 mt-1 text-sm resize-none" />
            </div>
            <Button onClick={saveMdo} className="w-full h-12 rounded-xl bg-gradient-to-r from-green-600 to-green-700">
              <Save className="w-4 h-4 mr-2" />{editingMdo ? 'Opslaan' : 'Inplannen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// MEDICATIE PAGE
// ============================================
export const MedicatiePage = ({ onBack, user, selectedLocation }) => {
  // Bepaal locatie - gebruik selectedLocation prop (voor zorgmanager) of user's locatie
  const userLocation = selectedLocation || user?.selected_location || user?.location_id || 'groep-zwaluw';
  const isAdelaars = userLocation === 'groep-adelaars';
  const isDemo = userLocation === 'groep-demo' || user?.email === 'demo@zorg-vleugel.nl';
  
  // Demo medicatie schema (fictieve data)
  const MEDICATION_SCHEDULE_DEMO = [
    {
      time: '08:00',
      label: 'Ochtend',
      clients: [
        { name: 'Jan', initials: 'J.J.', room: 'Kamer 1', location: 'Voorbeeldstraat 1', medication: 'Vitaminen', dosage: '1 tablet', type: 'begeleider', note: 'Demo - Begeleider geeft medicatie', instructions: 'Na het ontbijt innemen' },
        { name: 'Karel', initials: 'K.K.', room: 'Kamer 3', location: 'Voorbeeldstraat 1', medication: 'Paracetamol', dosage: '1 tablet', type: 'begeleider', note: 'Demo - Alleen bij hoofdpijn', instructions: 'Zo nodig innemen' },
      ]
    },
    {
      time: '18:00',
      label: 'Avond',
      clients: [
        { name: 'Petra', initials: 'P.P.', room: 'Kamer 2', location: 'Voorbeeldstraat 1', medication: 'Avondmedicatie', dosage: '1 tablet', type: 'eigen_beheer', note: 'Demo - Eigen beheer', instructions: 'Zelf innemen voor het slapen' },
      ]
    },
    {
      time: 'ZO NODIG',
      label: 'Zo Nodig (PRN)',
      isOnDemand: true,
      clients: [
        { name: 'Jan', initials: 'J.J.', room: 'Kamer 1', location: 'Voorbeeldstraat 1', medication: 'Zo nodig medicatie', dosage: 'Volgens voorschrift', type: 'begeleider', note: 'Demo - Alleen op verzoek', instructions: 'Alleen geven wanneer Jan erom vraagt' },
      ]
    }
  ];

  // Medicatie schema per locatie
  const MEDICATION_SCHEDULE_ZWALUW = [
    {
      time: '08:00',
      label: 'Ochtend',
      clients: [
        { name: 'Axcel', initials: 'A.T.', room: 'Kamer 4', location: 'Enschotsestraat 100', medication: 'Ochtendmedicatie', dosage: '1 tablet', type: 'begeleider', note: 'Begeleider geeft medicatie', instructions: 'Na het ontbijt innemen' },
      ]
    },
    {
      time: '18:00',
      label: 'Avond',
      clients: [
        { name: 'Yassin', initials: 'Y.H.', room: 'Kamer 1', location: 'Enschotsestraat 98', medication: 'Aripiprazol', dosage: 'Volgens voorschrift', type: 'begeleider', note: 'Begeleider geeft medicatie', instructions: 'NA het avondeten innemen' },
        { name: 'Mahad', initials: 'M.A.', room: 'Appartement', location: 'Piusplein 62-31', medication: 'Paliperidone 3mg', dosage: '1 tablet', type: 'eigen_beheer', note: 'Eigen beheer - herinneren indien nodig', instructions: 'Zelf innemen, begeleider checkt of het is ingenomen' },
        { name: 'Axcel', initials: 'A.T.', room: 'Kamer 4', location: 'Enschotsestraat 100', medication: 'Avondmedicatie', dosage: '1 tablet', type: 'begeleider', note: 'Begeleider geeft medicatie', instructions: 'Na het avondeten innemen' },
      ]
    },
    {
      time: 'ZO NODIG',
      label: 'Zo Nodig (PRN)',
      isOnDemand: true,
      clients: [
        { name: 'Sietse', initials: 'S.B.', room: 'Kamer 1', location: 'Enschotsestraat 100', medication: 'Zo nodig medicatie', dosage: 'Volgens voorschrift', type: 'begeleider', note: 'Alleen op verzoek van Sietse', instructions: 'Alleen geven wanneer Sietse erom vraagt. Altijd aftekenen!' },
      ]
    }
  ];

  const MEDICATION_SCHEDULE_ADELAARS = [
    {
      time: '08:00',
      label: 'Ochtend',
      clients: [
        { name: 'Faraah', initials: 'F.I.', room: 'K7', location: 'Ardennenlaan 68', medication: 'Thiamine HCL 25mg (Vitamine B1)', dosage: '1 tablet', type: 'begeleider', note: 'Baxterol - Zorginstelling beheer', instructions: 'Na het ontbijt innemen' },
      ]
    },
    {
      time: '10:00',
      label: 'Ochtend',
      clients: [
        { name: 'Melvin', initials: 'M.H.', room: 'K2', location: 'Ardennenlaan 68', medication: 'Haloperidol 1 mg', dosage: '1 tablet', type: 'begeleider', note: 'Baxterol - Zorginstelling beheer', instructions: 'Na het ontbijt innemen' },
        { name: 'Melvin', initials: 'M.H.', room: 'K2', location: 'Ardennenlaan 68', medication: 'Metformine 500 mg', dosage: '1 tablet', type: 'begeleider', note: 'Baxterol - Zorginstelling beheer', instructions: 'Na het ontbijt innemen' },
      ]
    },
    {
      time: '17:00',
      label: 'Avond',
      clients: [
        { name: 'Mike', initials: 'M.R.', room: 'K3', location: 'Ardennenlaan 68', medication: 'Aripiprazol 10MG + 5MG + 2,5MG TABL', dosage: 'Volgens voorschrift', type: 'begeleider', note: 'Baxterol - Zorginstelling beheer', instructions: 'Na het avondeten innemen' },
        { name: 'Mike', initials: 'M.R.', room: 'K3', location: 'Ardennenlaan 68', medication: 'OMEPRAZOL MSR 20MG CAPS', dosage: '1 capsule', type: 'begeleider', note: 'Baxterol - Zorginstelling beheer', instructions: 'Na het avondeten innemen' },
      ]
    },
    {
      time: '18:00',
      label: 'Avond',
      clients: [
        { name: 'Melvin', initials: 'M.H.', room: 'K2', location: 'Ardennenlaan 68', medication: 'Promethazine 20 mg', dosage: '1 tablet', type: 'begeleider', note: 'Baxterol - Zorginstelling beheer', instructions: 'Na het avondeten innemen' },
        { name: 'Faraah', initials: 'F.I.', room: 'K7', location: 'Ardennenlaan 68', medication: 'Thiamine HCL 25mg (Vitamine B1)', dosage: '1 tablet', type: 'begeleider', note: 'Baxterol - Zorginstelling beheer', instructions: 'Na het avondeten innemen' },
      ]
    },
    {
      time: '21:00',
      label: 'Slaapmedicatie',
      isOnDemand: false,
      clients: [
        { name: 'Rinaldo', initials: 'R.B.', room: 'K4', location: 'Ardennenlaan 68', medication: 'Quetiapine (slaapmedicatie)', dosage: 'Volgens voorschrift', type: 'eigen_beheer', note: 'Eigen beheer door Rinaldo', instructions: 'Rinaldo neemt zelf in voor het slapen' },
        { name: 'Rinaldo', initials: 'R.B.', room: 'K4', location: 'Ardennenlaan 68', medication: 'Diazepam (slaapmedicatie)', dosage: 'Volgens voorschrift', type: 'eigen_beheer', note: 'Eigen beheer door Rinaldo', instructions: 'Rinaldo neemt zelf in voor het slapen' },
      ]
    }
  ];

  // Selecteer juiste schema op basis van locatie
  const MEDICATION_SCHEDULE = isDemo ? MEDICATION_SCHEDULE_DEMO : (isAdelaars ? MEDICATION_SCHEDULE_ADELAARS : MEDICATION_SCHEDULE_ZWALUW);

  const fiveRs = [
    { r: 'Juiste Cliënt', desc: 'Controleer naam en foto' },
    { r: 'Juiste Medicatie', desc: 'Check medicijnnaam' },
    { r: 'Juiste Dosis', desc: 'Controleer hoeveelheid' },
    { r: 'Juiste Tijd', desc: 'Is dit het juiste moment?' },
    { r: 'Juiste Toedieningsweg', desc: 'Oraal, injectie, etc.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-pink-50/30 to-rose-50/30" data-testid="medicatie-page">
      {/* Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-72 h-72 bg-pink-300/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-rose-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-pink-300/15 rounded-full blur-3xl" />
      </div>
      
      <PageHeader title="Medicatie" subtitle="Overzicht & Richtlijnen" onBack={onBack} icon={Pill} iconGradient="from-pink-400 to-pink-500" />

      <div className="px-4 py-6 pb-32 relative z-10">
        {/* Disclaimer */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-red-50 to-rose-50 border border-red-200/50 shadow-sm"
        >
          <h3 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            Belangrijk
          </h3>
          <p className="text-sm text-red-700">
            Dit is een <strong>overzicht en richtlijn</strong>. Medicaties dienen altijd te worden afgetekend via de <strong>officiële aftekenlijst in het medicatiemapje</strong>.
          </p>
        </motion.div>

        {/* 5 R's Card - Premium */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-pink-400 via-pink-500 to-rose-500 text-white shadow-xl shadow-pink-500/20"
        >
          <h3 className="font-bold mb-4 flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5" />
            </div>
            De 5 Juistes (5 R's)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {fiveRs.map((item, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.05 }}
                className="flex items-center gap-3 p-2 rounded-xl bg-white/10"
              >
                <span className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center text-sm font-bold">{i + 1}</span>
                <div>
                  <span className="font-semibold text-sm">{item.r}</span>
                  <span className="text-white/70 text-xs block">{item.desc}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Medicatie Schema */}
        <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 px-1">Cliënten met Medicatie</h2>
        
        <div className="space-y-4">
          {MEDICATION_SCHEDULE.map((schedule, scheduleIndex) => (
            <motion.div 
              key={schedule.time} 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: scheduleIndex * 0.1 }}
              className="rounded-2xl bg-white border border-slate-200/50 shadow-sm overflow-hidden"
              data-testid={`medicatie-schedule-${scheduleIndex}`}
            >
              {/* Schedule Header */}
              <div className="p-4" style={{ backgroundColor: schedule.isOnDemand ? '#fef3c7' : '#fce7f3' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: schedule.isOnDemand ? '#f59e0b' : '#ec4899' }}>
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{schedule.time}</h3>
                    <p className="text-sm text-slate-600">{schedule.label}</p>
                  </div>
                </div>
              </div>
              
              {/* Clients */}
              <div className="divide-y divide-slate-100">
                {schedule.clients.map((client, clientIndex) => (
                  <div key={clientIndex} className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, #ec4899, #db2777)' }}>
                        <span className="text-sm font-bold text-white">{client.initials}</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{client.name}</h4>
                        <p className="text-sm font-medium" style={{ color: '#ec4899' }}>{client.medication}</p>
                        <p className="text-xs text-slate-500 mt-1">{client.dosage} • {client.room}</p>
                        <p className="text-xs text-slate-400">{client.location}</p>
                        {client.instructions && (
                          <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded-lg">
                            💡 {client.instructions}
                          </p>
                        )}
                        <span className="inline-flex items-center mt-2 text-xs px-2 py-1 rounded-full" style={{ 
                          backgroundColor: client.type === 'eigen_beheer' ? '#d1fae5' : '#fce7f3',
                          color: client.type === 'eigen_beheer' ? '#047857' : '#be185d'
                        }}>
                          {client.type === 'eigen_beheer' ? 'Eigen beheer' : 'Begeleider geeft'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Belangrijke Info */}
        <div className="mt-6 p-4 rounded-2xl border" style={{ backgroundColor: '#fce7f3', borderColor: '#fbcfe8' }}>
          <h3 className="font-semibold mb-2" style={{ color: '#9d174d' }}>⚠️ Belangrijk</h3>
          <ul className="text-sm space-y-1" style={{ color: '#be185d' }}>
            {isDemo ? (
              <>
                <li>• Dit is een demo-omgeving met fictieve cliëntgegevens</li>
                <li>• Jan, Petra en Karel zijn geen echte cliënten</li>
                <li>• Deze data is alleen bedoeld voor demonstratiedoeleinden</li>
              </>
            ) : isAdelaars ? (
              <>
                <li>• Rinaldo (K4) beheert zijn slaapmedicatie zelf</li>
                <li>• Baxterol medicatie wordt beheerd door zorginstelling</li>
              </>
            ) : (
              <>
                <li>• Altijd dubbel controleren met medicatielijst</li>
              </>
            )}
            <li>• Bij twijfel: bel de apotheek of achterwacht</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// ============================================
// CHECKLIST PAGE - Dagelijkse dienst-taken met overdracht
// ============================================
export const ChecklistPage = ({ onBack, user }) => {
  const [selectedShift, setSelectedShift] = useState(() => {
    const hour = new Date().getHours();
    if (hour >= 7 && hour < 15) return 'dagdienst';
    if (hour >= 15 && hour < 23) return 'avonddienst';
    return 'nachtdienst';
  });
  const [completedTasks, setCompletedTasks] = useState({});
  const [previousOverdracht, setPreviousOverdracht] = useState(null);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  // Dienst configuratie met taken
  const SHIFTS = {
    dagdienst: {
      name: 'Dagdienst',
      timeRange: '07:00 - 15:00',
      gradient: 'from-amber-500 to-orange-500',
      sections: [
        {
          name: 'Ochtend (07:00 - 12:00)',
          tasks: [
            { id: 'dag-1', time: '07:00', title: 'Overdracht lezen van nachtdienst', important: true },
            { id: 'dag-2', time: '07:30', title: 'Bewoners wekken en begroeten' },
            { id: 'dag-3', time: '08:00', title: 'Ontbijt begeleiden' },
            { id: 'dag-4', time: '08:00', title: 'Medicatie ochtend toedienen', important: true },
            { id: 'dag-5', time: '09:00', title: 'Dagbesteding starten / afspraken checken' },
            { id: 'dag-6', time: '09:30', title: 'Contactmoment 1 - Individuele aandacht', important: true },
            { id: 'dag-7', time: '10:30', title: 'Huishoudelijke taken begeleiden' },
            { id: 'dag-8', time: '12:00', title: 'Lunch voorbereiden en begeleiden' },
          ]
        },
        {
          name: 'Middag (12:00 - 15:00)',
          tasks: [
            { id: 'dag-9', time: '13:00', title: 'Contactmoment 2 - Check welzijn', important: true },
            { id: 'dag-10', time: '13:30', title: 'Administratie bijwerken' },
            { id: 'dag-11', time: '14:00', title: 'Voorbereiden overdracht avonddienst' },
            { id: 'dag-12', time: '14:30', title: 'Overdracht schrijven', important: true },
          ]
        }
      ]
    },
    avonddienst: {
      name: 'Avonddienst',
      timeRange: '15:00 - 23:00',
      gradient: 'from-indigo-500 to-purple-500',
      sections: [
        {
          name: 'Middag (15:00 - 18:00)',
          tasks: [
            { id: 'avond-1', time: '15:00', title: 'Overdracht lezen van dagdienst', important: true },
            { id: 'avond-2', time: '15:30', title: 'Bewoners begroeten en check-in' },
            { id: 'avond-3', time: '16:00', title: 'Activiteiten begeleiden' },
            { id: 'avond-4', time: '17:00', title: 'Avondeten voorbereiden' },
            { id: 'avond-5', time: '17:45', title: 'Contactmoment 3 - Dagbespreking', important: true },
          ]
        },
        {
          name: 'Avond (18:00 - 23:00)',
          tasks: [
            { id: 'avond-6', time: '18:00', title: 'Avondeten begeleiden' },
            { id: 'avond-7', time: '18:30', title: 'Medicatie avond toedienen', important: true },
            { id: 'avond-8', time: '19:30', title: 'Vrije tijd / activiteiten' },
            { id: 'avond-9', time: '21:00', title: 'Avondronde - welzijnscheck' },
            { id: 'avond-10', time: '22:00', title: 'Bewoners naar bed begeleiden' },
            { id: 'avond-11', time: '22:30', title: 'Overdracht schrijven voor nachtdienst', important: true },
          ]
        }
      ]
    },
    nachtdienst: {
      name: 'Nachtdienst',
      timeRange: '23:00 - 07:00',
      gradient: 'from-slate-600 to-slate-800',
      sections: [
        {
          name: 'Nacht (23:00 - 07:00)',
          tasks: [
            { id: 'nacht-1', time: '23:00', title: 'Overdracht lezen van avonddienst', important: true },
            { id: 'nacht-2', time: '23:30', title: 'Nachtronde 1 - Alle kamers checken' },
            { id: 'nacht-3', time: '02:00', title: 'Nachtronde 2' },
            { id: 'nacht-4', time: '05:00', title: 'Nachtronde 3' },
            { id: 'nacht-5', time: '06:00', title: 'Voorbereiden ontbijt' },
            { id: 'nacht-6', time: '06:30', title: 'Overdracht schrijven voor dagdienst', important: true },
          ]
        }
      ]
    }
  };

  // Load saved tasks and previous overdracht
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/checklist/${today}/${selectedShift}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setCompletedTasks(response.data.completed_tasks || {});
          setPreviousOverdracht(response.data.previous_overdracht);
        }
      } catch (error) {
        // No saved data yet, use empty state
        setCompletedTasks({});
        setPreviousOverdracht(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [today, selectedShift]);

  const toggleTask = async (taskId) => {
    const newCompleted = { ...completedTasks };
    if (newCompleted[taskId]) {
      delete newCompleted[taskId];
    } else {
      newCompleted[taskId] = {
        completed_at: new Date().toISOString(),
        completed_by: user?.name || 'Onbekend'
      };
    }
    setCompletedTasks(newCompleted);

    // Save to backend
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/checklist/${today}/${selectedShift}/task`, {
        task_id: taskId,
        completed: !completedTasks[taskId],
        completed_by: user?.name || 'Onbekend'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Revert on error
      setCompletedTasks(completedTasks);
      toast.error('Fout bij opslaan');
    }
  };

  const currentShift = SHIFTS[selectedShift];
  const allTasks = currentShift.sections.flatMap(s => s.tasks);
  const completedCount = Object.keys(completedTasks).length;
  const progress = allTasks.length > 0 ? Math.round((completedCount / allTasks.length) * 100) : 0;

  // Get previous shift name for overdracht
  const getPreviousShiftName = () => {
    if (selectedShift === 'dagdienst') return 'Nachtdienst';
    if (selectedShift === 'avonddienst') return 'Dagdienst';
    return 'Avonddienst';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30" data-testid="checklist-page">
      <PageHeader 
        title="Dagelijkse Checklist" 
        subtitle={currentShift.name} 
        onBack={onBack}
        icon={CheckSquare}
        iconGradient="from-emerald-500 to-emerald-600"
      />

      <div className="px-4 py-6 pb-32">
        {/* Dienst Selector */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {Object.entries(SHIFTS).map(([key, shift]) => (
            <button
              key={key}
              onClick={() => { setSelectedShift(key); setLoading(true); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                selectedShift === key
                  ? `bg-gradient-to-r ${shift.gradient} text-white shadow-lg`
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`shift-btn-${key}`}
            >
              {shift.name}
            </button>
          ))}
        </div>

        {/* Progress Card */}
        <div className={`mb-6 p-5 rounded-2xl bg-gradient-to-br ${currentShift.gradient} text-white`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-lg">{currentShift.name}</h3>
              <p className="text-sm text-white/80">{currentShift.timeRange}</p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold">{progress}%</span>
              <p className="text-xs text-white/80">{completedCount}/{allTasks.length} taken</p>
            </div>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }} 
              animate={{ width: `${progress}%` }} 
              className="h-full bg-white rounded-full" 
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Previous Overdracht */}
        {previousOverdracht && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200"
          >
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-emerald-800">Overdracht {getPreviousShiftName()}</h3>
            </div>
            <p className="text-sm text-emerald-700 whitespace-pre-wrap">{previousOverdracht.tekst}</p>
            <p className="text-xs text-emerald-500 mt-2">
              Door {previousOverdracht.geschreven_door} • {new Date(previousOverdracht.geschreven_op).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </motion.div>
        )}

        {/* Tasks */}
        {loading ? (
          <LoadingSpinner color="emerald" />
        ) : (
          <div className="space-y-4">
            {currentShift.sections.map((section, sectionIndex) => (
              <motion.div 
                key={section.name} 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: sectionIndex * 0.1 }}
              >
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center justify-between">
                  <span>{section.name}</span>
                </h2>
                <div className="bg-white rounded-2xl border border-slate-200/50 shadow-sm divide-y divide-slate-100">
                  {section.tasks.map((task) => {
                    const isCompleted = !!completedTasks[task.id];
                    const completionInfo = completedTasks[task.id];
                    
                    return (
                      <button 
                        key={task.id} 
                        onClick={() => toggleTask(task.id)} 
                        className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left"
                        data-testid={`task-${task.id}`}
                      >
                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                          isCompleted 
                            ? 'bg-emerald-500 border-emerald-500' 
                            : task.important 
                              ? 'border-amber-400' 
                              : 'border-slate-300'
                        }`}>
                          {isCompleted && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <div className="flex-1">
                          <span className={`font-medium ${isCompleted ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                            {task.title}
                          </span>
                          {task.important && !isCompleted && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Belangrijk</span>
                          )}
                          {isCompleted && completionInfo && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              ✓ {completionInfo.completed_by} • {new Date(completionInfo.completed_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 font-medium">{task.time}</span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// OVERDRACHT PAGE - Aparte pagina voor dagelijkse overdracht
// ============================================
export const OverdrachtPage = ({ onBack, user }) => {
  const [overdrachten, setOverdrachten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overdracht, setOverdracht] = useState('');
  const [geschrevenDoor, setGeschrevenDoor] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const currentHour = new Date().getHours();

  const fetchOverdrachten = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/overdrachten`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverdrachten(response.data.overdrachten || []);
    } catch (error) {
      setOverdrachten([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverdrachten();
  }, []);

  const saveOverdracht = async () => {
    if (!overdracht.trim()) {
      toast.error('Vul eerst de overdracht in');
      return;
    }
    if (!geschrevenDoor.trim()) {
      toast.error('Vul je naam in');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/overdrachten`, {
        tekst: overdracht,
        geschreven_door: geschrevenDoor.trim(),
        datum: today,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Overdracht opgeslagen');
      setOverdracht('');
      setGeschrevenDoor('');
      fetchOverdrachten();
    } catch (error) {
      toast.error('Fout bij opslaan overdracht');
    } finally {
      setSaving(false);
    }
  };

  const deleteOverdracht = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze overdracht wilt verwijderen?')) {
      return;
    }
    setDeleting(id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/overdrachten/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Overdracht verwijderd');
      fetchOverdrachten();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    } finally {
      setDeleting(null);
    }
  };

  // Deadline warnings
  const isAfterOverdrachtDeadline = currentHour >= 20;
  const isAfterZillizDeadline = currentHour >= 23;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-fuchsia-50/30 to-pink-50/30" data-testid="overdracht-page">
      {/* Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-72 h-72 bg-fuchsia-300/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-pink-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-fuchsia-300/15 rounded-full blur-3xl" />
      </div>
      
      <PageHeader title="Overdracht" subtitle="Dagelijkse rapportage" onBack={onBack} icon={FileText} iconGradient="from-fuchsia-500 to-fuchsia-600" />

      <div className="px-4 py-6 pb-32 relative z-10">
        {/* Deadline Warnings */}
        <div className="mb-6 space-y-3">
          <div className={`p-4 rounded-2xl border ${isAfterOverdrachtDeadline ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center gap-3">
              <Clock className={`w-5 h-5 ${isAfterOverdrachtDeadline ? 'text-red-600' : 'text-amber-600'}`} />
              <div>
                <p className={`font-semibold ${isAfterOverdrachtDeadline ? 'text-red-800' : 'text-amber-800'}`}>
                  Overdracht deadline: 20:00
                </p>
                <p className={`text-sm ${isAfterOverdrachtDeadline ? 'text-red-600' : 'text-amber-600'}`}>
                  {isAfterOverdrachtDeadline ? '⚠️ Deadline verstreken!' : 'Plaats je overdracht voor 20:00'}
                </p>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-2xl border ${isAfterZillizDeadline ? 'bg-red-50 border-red-200' : 'bg-fuchsia-50 border-fuchsia-200'}`}>
            <div className="flex items-center gap-3">
              <FileText className={`w-5 h-5 ${isAfterZillizDeadline ? 'text-red-600' : 'text-fuchsia-600'}`} />
              <div>
                <p className={`font-semibold ${isAfterZillizDeadline ? 'text-red-800' : 'text-fuchsia-800'}`}>
                  Zilliz rapportage deadline: 23:00
                </p>
                <p className={`text-sm ${isAfterZillizDeadline ? 'text-red-600' : 'text-fuchsia-600'}`}>
                  {isAfterZillizDeadline ? '⚠️ Deadline verstreken!' : 'Vergeet niet je rapportage in Zilliz te plaatsen'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Schrijf Overdracht */}
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Overdracht Schrijven</h2>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/60 shadow-sm p-4">
            {/* Naam invoerveld */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Geschreven door
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fuchsia-500" />
                <input
                  type="text"
                  value={geschrevenDoor}
                  onChange={(e) => setGeschrevenDoor(e.target.value)}
                  placeholder="Typ je naam..."
                  className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 text-sm bg-white"
                  data-testid="overdracht-naam-input"
                />
              </div>
            </div>
            
            {/* Datum info */}
            <div className="flex items-center gap-2 mb-3 p-2.5 rounded-xl bg-gradient-to-r from-fuchsia-50 to-pink-50 border border-fuchsia-100">
              <Calendar className="w-4 h-4 text-fuchsia-600" />
              <span className="text-sm font-medium text-fuchsia-800">
                {new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
            
            <textarea
              value={overdracht}
              onChange={(e) => setOverdracht(e.target.value)}
              placeholder="Schrijf hier de overdracht voor de volgende dienst...

Denk aan:
• Bijzonderheden per cliënt
• Medicatie afgetekend?
• Afspraken voor morgen
• Aandachtspunten"
              className="w-full h-40 p-3 rounded-xl border border-slate-200 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 resize-none text-sm bg-white"
              data-testid="overdracht-textarea"
            />
            <Button 
              onClick={saveOverdracht} 
              disabled={saving || !overdracht.trim() || !geschrevenDoor.trim()}
              className="mt-3 w-full h-12 rounded-xl bg-gradient-to-r from-fuchsia-500 to-fuchsia-600 text-white shadow-lg shadow-fuchsia-500/20 disabled:opacity-50 disabled:shadow-none"
              data-testid="save-overdracht-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Overdracht Plaatsen
            </Button>
          </div>
        </div>

        {/* Recente Overdrachten */}
        <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Recente Overdrachten</h2>
        
        {loading ? (
          <LoadingSpinner color="fuchsia" />
        ) : overdrachten.length === 0 ? (
          <EmptyState icon={FileText} title="Geen overdrachten" subtitle="Er zijn nog geen overdrachten geplaatst" />
        ) : (
          <div className="space-y-3">
            {overdrachten.map((item, index) => (
              <motion.div 
                key={item.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm"
                data-testid={`overdracht-item-${index}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 flex items-center justify-center">
                      <span className="text-sm font-bold text-white">{item.geschreven_door?.charAt(0) || '?'}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{item.geschreven_door}</p>
                      <p className="text-xs text-slate-500">
                        {item.datum ? new Date(item.datum).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }) : ''}
                        {item.geschreven_op && ` • ${new Date(item.geschreven_op).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`}
                      </p>
                    </div>
                  </div>
                  {item.id && (
                    <button
                      onClick={() => deleteOverdracht(item.id)}
                      disabled={deleting === item.id}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                      data-testid={`delete-overdracht-${index}`}
                    >
                      {deleting === item.id ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.tekst}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// WIFI PAGE - Netwerk gegevens per locatie
// ============================================
export const WifiPage = ({ onBack, user, selectedLocation }) => {
  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
  
  const wifiData = {
    'groep-zwaluw': {
      name: 'Groep Zwaluw',
      address: 'Enschotsestraat 98 & 100',
      network: 'Ziggo5353194',
      password: 'euwzaUfhuby9desr',
      gradient: 'from-emerald-500 to-teal-500',
    },
    'groep-adelaars': {
      name: 'Groep Adelaars',
      address: 'Ardennenlaan 68',
      network: 'KPN516A06',
      password: 'KbsXXXL4LmkWZxRz',
      gradient: 'from-blue-500 to-indigo-500',
    }
  };
  
  const data = wifiData[locationSlug] || wifiData['groep-zwaluw'];
  const [copied, setCopied] = useState(null);
  
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success(`${type} gekopieerd!`);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30" data-testid="wifi-page">
      <PageHeader title="WiFi" subtitle={data.name} onBack={onBack} icon={Wifi} iconGradient="from-blue-600 to-blue-700" />

      <div className="px-4 py-6 pb-32">
        {/* Banner */}
        <div className={`mb-6 p-6 rounded-2xl bg-gradient-to-br ${data.gradient} text-white`}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
              <Wifi className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold">{data.name}</h3>
              <p className="text-sm text-white/80">{data.address}</p>
            </div>
          </div>
        </div>

        {/* Network Info */}
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Netwerknaam</p>
                <p className="text-lg font-bold text-slate-800">{data.network}</p>
              </div>
              <Button 
                onClick={() => copyToClipboard(data.network, 'Netwerknaam')}
                variant="outline"
                className={`rounded-xl ${copied === 'Netwerknaam' ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
              >
                {copied === 'Netwerknaam' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          <div className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Wachtwoord</p>
                <p className="text-lg font-bold text-slate-800 font-mono">{data.password}</p>
              </div>
              <Button 
                onClick={() => copyToClipboard(data.password, 'Wachtwoord')}
                variant="outline"
                className={`rounded-xl ${copied === 'Wachtwoord' ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
              >
                {copied === 'Wachtwoord' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-6 p-4 rounded-2xl bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">Let op</p>
              <p className="text-sm text-amber-700">Deel de WiFi gegevens niet met cliënten of bezoekers. Voor gasten is er een apart gastnetwerk beschikbaar.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// AFVALKALENDER PAGE - Ophaaldata voor restafval
// ============================================
export const AfvalkalenderPage = ({ onBack, user, selectedLocation }) => {
  const [loading, setLoading] = useState(true);
  const [afvalData, setAfvalData] = useState([]);
  const [afvalInfo, setAfvalInfo] = useState({});
  const [showFotoModal, setShowFotoModal] = useState(false);
  const [error, setError] = useState(null);
  
  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
  
  // Fallback data voor als API faalt
  const FALLBACK_DATA = {
    'groep-zwaluw': {
      locatie: 'Enschotsestraat 100, 5014DH Tilburg',
      type: 'Restafval (grote grijze container)',
      frequentie: 'Om de 2 weken',
      instructie: '⚠️ GROTE RESTAFVAL CONTAINER de AVOND ERVOOR aan de straat zetten! Container voor 08:00 uur buiten.',
      plaatsing: 'Container aan de straat bij de lantaarnpaal (zie foto)',
      foto_locatie: 'https://customer-assets.emergentagent.com/job_zvportal-update/artifacts/phca0v2v_PHOTO-2026-02-08-17-36-19%202.jpg',
      ophaaldata_2026: [
        "2026-01-08", "2026-01-22", "2026-02-05", "2026-02-19",
        "2026-03-05", "2026-03-19", "2026-04-02", "2026-04-16", "2026-04-30",
        "2026-05-14", "2026-05-28", "2026-06-11", "2026-06-25",
        "2026-07-09", "2026-07-23", "2026-08-06", "2026-08-20",
        "2026-09-03", "2026-09-17", "2026-10-01", "2026-10-15", "2026-10-29",
        "2026-11-12", "2026-11-26", "2026-12-10", "2026-12-24"
      ]
    },
    'groep-adelaars': {
      locatie: 'Ardennenlaan 68, 5022NM Tilburg',
      type: 'Restafval (grote grijze container)',
      frequentie: 'Om de 2 weken',
      instructie: '⚠️ GROTE RESTAFVAL CONTAINER de AVOND ERVOOR aan de straat zetten! Container voor 08:00 uur buiten.',
      plaatsing: 'Container aan de straat bij de lantaarnpaal',
      foto_locatie: 'https://customer-assets.emergentagent.com/job_zorgvleugel-dash-3/artifacts/5vx2lthd_PHOTO-2026-02-08-16-31-22.jpg',
      ophaaldata_2026: [
        "2026-01-12", "2026-01-26", "2026-02-09", "2026-02-23",
        "2026-03-09", "2026-03-23", "2026-04-06", "2026-04-20",
        "2026-05-04", "2026-05-18", "2026-06-01", "2026-06-15", "2026-06-29",
        "2026-07-13", "2026-07-27", "2026-08-10", "2026-08-24",
        "2026-09-07", "2026-09-21", "2026-10-05", "2026-10-19",
        "2026-11-02", "2026-11-16", "2026-11-30", "2026-12-14", "2026-12-28"
      ]
    }
  };
  
  // Haal afvalkalender data op uit API
  useEffect(() => {
    const fetchAfvalData = async () => {
      try {
        const token = localStorage.getItem('token');
        const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
        const response = await axios.get(`${API_URL}/api/begeleider/overzicht${locationParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const afvalkalender = response.data?.afvalkalender || {};
        
        // Als de API geen data teruggeeft, gebruik fallback
        if (!afvalkalender.ophaaldata_2026 || afvalkalender.ophaaldata_2026.length === 0) {
          console.warn('API returned empty afvalkalender, using fallback data');
          const fallback = FALLBACK_DATA[locationSlug] || FALLBACK_DATA['groep-zwaluw'];
          setAfvalInfo(fallback);
          setAfvalData(fallback.ophaaldata_2026 || []);
        } else {
          setAfvalInfo(afvalkalender);
          setAfvalData(afvalkalender.ophaaldata_2026 || []);
        }
        setError(null);
      } catch (error) {
        console.error('Error fetching afvalkalender:', error);
        // Gebruik fallback data bij fout
        const fallback = FALLBACK_DATA[locationSlug] || FALLBACK_DATA['groep-zwaluw'];
        setAfvalInfo(fallback);
        setAfvalData(fallback.ophaaldata_2026 || []);
        setError('Kon afvalkalender niet laden, lokale data wordt getoond');
      } finally {
        setLoading(false);
      }
    };
    fetchAfvalData();
  }, [selectedLocation, locationSlug]);
  
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  
  // Vind volgende ophaaldatum
  const nextDate = afvalData.find(d => d >= todayStr);
  const nextDateObj = nextDate ? new Date(nextDate) : null;
  
  // Bereken dagen tot volgende ophaal
  const daysUntil = nextDateObj ? Math.ceil((nextDateObj - today) / (1000 * 60 * 60 * 24)) : null;
  
  // Check of morgen ophaaldag is (container moet vandaag buiten)
  const isTomorrow = daysUntil === 1;
  const isToday = daysUntil === 0;
  
  // Komende 6 ophaaldata
  const upcomingDates = afvalData.filter(d => d >= todayStr).slice(0, 6);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-stone-50/30 to-gray-50/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-stone-50/30 to-gray-50/30" data-testid="afvalkalender-page">
      <PageHeader title="Afvalkalender" subtitle={afvalInfo.locatie || "Restafval (zwarte container)"} onBack={onBack} icon={Trash2} iconGradient="from-stone-500 to-stone-600" />

      <div className="px-4 py-6 pb-32">
        {/* Alert Banner */}
        {(isToday || isTomorrow) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-4 rounded-2xl text-white"
            style={{ backgroundColor: isToday ? '#ef4444' : '#78716c' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg">
                  {isToday ? 'Vandaag is ophaaldag!' : 'Morgen is ophaaldag!'}
                </h3>
                <p className="text-sm text-white/90">
                  {isToday ? 'Zorg dat de container aan de straat staat' : 'Zet de container vanavond nog aan de straat'}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Next Pickup Card */}
        <div className="mb-6 p-6 rounded-2xl text-white" style={{ background: 'linear-gradient(to bottom right, #78716c, #57534e)' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/80 mb-1">Volgende ophaaldag</p>
              <h2 className="text-2xl font-bold">
                {nextDateObj ? nextDateObj.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Onbekend'}
              </h2>
              <p className="text-sm text-white/80 mt-1">
                {daysUntil === 0 ? 'Vandaag!' : daysUntil === 1 ? 'Morgen' : `Over ${daysUntil} dagen`}
              </p>
            </div>
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        {/* Reminder */}
        <div className="mb-6 p-4 rounded-2xl border-2" style={{ backgroundColor: '#fafaf9', borderColor: '#d6d3d1' }}>
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#78716c' }} />
            <div className="flex-1">
              <p className="font-semibold" style={{ color: '#44403c' }}>Belangrijk</p>
              <p className="text-sm mb-3" style={{ color: '#57534e' }}>{afvalInfo.instructie || 'De container moet de avond ervoor aan de straat worden gezet. Dit is een taak voor de avonddienst.'}</p>
              
              {/* Foto locatie knop */}
              {afvalInfo.foto_locatie && (
                <button
                  onClick={() => setShowFotoModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-xl text-sm font-medium transition-colors"
                  style={{ backgroundColor: '#78716c' }}
                >
                  <MapPin className="w-4 h-4" />
                  Bekijk waar container moet staan
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Foto Modal */}
        <AnimatePresence>
          {showFotoModal && afvalInfo.foto_locatie && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
              onClick={() => setShowFotoModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative max-w-2xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header met kruisje */}
                <div className="flex items-center justify-between p-4 border-b bg-slate-50 shrink-0">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-amber-500" />
                    <h3 className="font-semibold text-slate-800 text-base">Container plaatsing</h3>
                  </div>
                  <button
                    onClick={() => setShowFotoModal(false)}
                    className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors"
                    data-testid="modal-close-button"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
                
                {/* Foto - groter formaat */}
                <div className="p-4 overflow-auto flex-1">
                  <img
                    src={afvalInfo.foto_locatie}
                    alt="Container locatie"
                    className="w-full h-auto max-h-[60vh] object-contain rounded-xl"
                  />
                  <p className="mt-3 text-sm text-slate-600 text-center font-medium">
                    Zet de container bij de lantaarnpaal aan de straat
                  </p>
                </div>
                
                {/* Sluiten knop */}
                <div className="p-4 border-t bg-slate-50 shrink-0">
                  <button
                    onClick={() => setShowFotoModal(false)}
                    className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors"
                    data-testid="modal-sluiten-button"
                  >
                    Sluiten
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upcoming Dates */}
        <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Komende ophaaldata</h2>
        <div className="space-y-2">
          {upcomingDates.map((date, index) => {
            const dateObj = new Date(date);
            const isNext = index === 0;
            return (
              <motion.div
                key={date}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 rounded-2xl bg-white border ${isNext ? 'border-stone-400 ring-2 ring-stone-100' : 'border-slate-200/50'} shadow-sm`}
                data-testid={`afval-date-${index}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${isNext ? 'bg-stone-500' : 'bg-slate-100'} flex items-center justify-center`}>
                    <Trash2 className={`w-5 h-5 ${isNext ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">
                      {dateObj.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                    <p className="text-xs text-slate-500">
                      Container de avond ervoor buiten zetten
                    </p>
                  </div>
                  {isNext && (
                    <span className="px-2 py-1 text-xs font-semibold bg-stone-100 text-stone-700 rounded-full">
                      Volgende
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================
// ZORGPLAN EVALUATIES PAGE - Alleen voor gedragswetenschapper
// ============================================
export const ZorgplanEvaluatiesPage = ({ onBack, user }) => {
  const [evaluaties, setEvaluaties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [form, setForm] = useState({ client: '', datum: '', evaluatie: '', aanbevelingen: '' });

  // Bepaal de locatie van de gebruiker
  const userLocation = user?.location || user?.locatie || 'groep-zwaluw';

  // Clienten per locatie
  const CLIENTEN_PER_LOCATIE = {
    'groep-adelaars': ['Melvin Hartman', 'Mike Reenalda', 'Rinaldo Bon', 'Joey Goordem', 'Faraah Ibrahim', 'Julian Bovenberg']
  };

  // Haal de juiste clienten op basis van locatie
  const clienten = CLIENTEN_PER_LOCATIE[userLocation] || CLIENTEN_PER_LOCATIE['groep-zwaluw'];
  
  // Fallback evaluaties per locatie
  const FALLBACK_EVALUATIES_PER_LOCATIE = {
    'groep-zwaluw': [
      { id: '1', client: 'Yassin Houba', datum: '2026-01-15', evaluatie: 'Positieve ontwikkeling in dagstructuur', aanbevelingen: 'Doorgaan met huidige aanpak' },
      { id: '2', client: 'Sietse Bouwman', datum: '2026-01-20', evaluatie: 'Medicatie werkt goed', aanbevelingen: 'Voortzetten huidige medicatie' },
    ],
    'groep-adelaars': [
      { id: '3', client: 'Rinaldo Bon', datum: '2026-02-01', evaluatie: 'Stabiel gedrag', aanbevelingen: 'Meer sociale activiteiten' },
      { id: '4', client: 'Melvin Hartman', datum: '2026-02-05', evaluatie: 'Goede vooruitgang', aanbevelingen: 'Focus op zelfredzaamheid' },
    ]
  };

  const FALLBACK_EVALUATIES = FALLBACK_EVALUATIES_PER_LOCATIE[userLocation] || FALLBACK_EVALUATIES_PER_LOCATIE['groep-zwaluw'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/zorgplan-evaluaties`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        });
        if (response.data?.evaluaties && response.data.evaluaties.length > 0) {
          setEvaluaties(response.data.evaluaties);
        } else {
          setEvaluaties(FALLBACK_EVALUATIES);
        }
      } catch (error) {
        console.error('Evaluaties API error:', error);
        setEvaluaties(FALLBACK_EVALUATIES);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const saveEvaluatie = async () => {
    if (!form.client || !form.datum || !form.evaluatie) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/zorgplan-evaluaties`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evaluatie opgeslagen');
      setShowModal(false);
      setForm({ client: '', datum: '', evaluatie: '', aanbevelingen: '' });
      // Refresh data
      const response = await axios.get(`${API_URL}/api/zorgplan-evaluaties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvaluaties(response.data.evaluaties || FALLBACK_EVALUATIES);
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-violet-50/30" data-testid="zorgplan-evaluaties-page">
      <PageHeader 
        title="Zorgplan Evaluaties" 
        subtitle="Gedragswetenschapper" 
        onBack={onBack}
        icon={FileText}
        iconGradient="from-violet-400 to-purple-500"
        rightAction={
          <Button onClick={() => setShowModal(true)} className="h-10 px-4 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg" data-testid="new-zorgplan-btn">
            <Plus className="w-4 h-4 mr-1" />Nieuw
          </Button>
        }
      />

      <div className="px-4 py-6 pb-32">
        {/* Info Banner */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-500 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">Zorgplan Evaluaties</h3>
              <p className="text-sm text-white/80">Periodieke evaluaties van individuele zorgplannen</p>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner color="purple" />
        ) : evaluaties.length === 0 ? (
          <EmptyState 
            icon={FileText} 
            title="Geen evaluaties" 
            subtitle="Voeg een nieuwe zorgplan evaluatie toe"
            action={
              <Button onClick={() => setShowModal(true)} className="rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white">
                <Plus className="w-4 h-4 mr-1" />Evaluatie Toevoegen
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            {evaluaties.map((item, index) => (
              <motion.div key={item.id || index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm" data-testid={`zorgplan-item-${index}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900">{item.client}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{item.evaluatie}</p>
                    <span className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                      <Calendar className="w-3.5 h-3.5" />{item.datum}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuwe Zorgplan Evaluatie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Cliënt *</label>
              <select value={form.client} onChange={(e) => setForm({...form, client: e.target.value})} 
                className="mt-1 w-full p-2 rounded-xl border border-slate-200 focus:border-purple-400" data-testid="zorgplan-client-select">
                <option value="">Selecteer cliënt</option>
                {clienten.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Datum *</label>
              <Input type="date" value={form.datum} onChange={(e) => setForm({...form, datum: e.target.value})} className="mt-1" data-testid="zorgplan-datum-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Evaluatie *</label>
              <textarea value={form.evaluatie} onChange={(e) => setForm({...form, evaluatie: e.target.value})} 
                placeholder="Beschrijf de evaluatie van het zorgplan..." rows={4}
                className="mt-1 w-full p-3 rounded-xl border border-slate-200 focus:border-purple-400 resize-none" data-testid="zorgplan-evaluatie-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Aanbevelingen</label>
              <textarea value={form.aanbevelingen} onChange={(e) => setForm({...form, aanbevelingen: e.target.value})} 
                placeholder="Eventuele aanbevelingen of aanpassingen..." rows={3}
                className="mt-1 w-full p-3 rounded-xl border border-slate-200 focus:border-purple-400 resize-none" data-testid="zorgplan-aanbevelingen-input" />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuleren</Button>
            <Button onClick={saveEvaluatie} className="bg-gradient-to-r from-purple-500 to-violet-500 text-white" data-testid="save-zorgplan-btn">
              <Save className="w-4 h-4 mr-1" />Opslaan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// RI+E EVALUATIES PAGE - Alleen voor gedragswetenschapper
// ============================================
export const RIEEvaluatiesPage = ({ onBack, user }) => {
  const [evaluaties, setEvaluaties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ locatie: '', datum: '', risicos: '', maatregelen: '', status: 'open' });

  const locaties = ['Enschotsestraat 98', 'Enschotsestraat 100', 'Ardennenlaan 68', 'Algemeen'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/rie-evaluaties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEvaluaties(response.data.evaluaties || []);
      } catch (error) {
        setEvaluaties([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const saveEvaluatie = async () => {
    if (!form.locatie || !form.datum || !form.risicos) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/rie-evaluaties`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RI+E Evaluatie opgeslagen');
      setShowModal(false);
      setForm({ locatie: '', datum: '', risicos: '', maatregelen: '', status: 'open' });
      // Refresh data
      const response = await axios.get(`${API_URL}/api/rie-evaluaties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvaluaties(response.data.evaluaties || []);
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50/30 to-rose-50/30" data-testid="rie-evaluaties-page">
      <PageHeader 
        title="RI+E Evaluaties" 
        subtitle="Risico Inventarisatie & Evaluatie" 
        onBack={onBack}
        icon={ClipboardList}
        iconGradient="from-red-400 to-rose-500"
        rightAction={
          <Button onClick={() => setShowModal(true)} className="h-10 px-4 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg" data-testid="new-rie-btn">
            <Plus className="w-4 h-4 mr-1" />Nieuw
          </Button>
        }
      />

      <div className="px-4 py-6 pb-32">
        {/* Info Banner */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-red-500 to-rose-500 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">RI+E Evaluaties</h3>
              <p className="text-sm text-white/80">Risico Inventarisatie & Evaluatie per locatie</p>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner color="red" />
        ) : evaluaties.length === 0 ? (
          <EmptyState 
            icon={ClipboardList} 
            title="Geen RI+E evaluaties" 
            subtitle="Voeg een nieuwe RI+E evaluatie toe"
            action={
              <Button onClick={() => setShowModal(true)} className="rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white">
                <Plus className="w-4 h-4 mr-1" />Evaluatie Toevoegen
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            {evaluaties.map((item, index) => (
              <motion.div key={item.id || index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm" data-testid={`rie-item-${index}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{item.locatie}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${item.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {item.status === 'open' ? 'Open' : 'Afgehandeld'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{item.risicos}</p>
                    <span className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                      <Calendar className="w-3.5 h-3.5" />{item.datum}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuwe RI+E Evaluatie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Locatie *</label>
              <select value={form.locatie} onChange={(e) => setForm({...form, locatie: e.target.value})} 
                className="mt-1 w-full p-2 rounded-xl border border-slate-200 focus:border-red-400" data-testid="rie-locatie-select">
                <option value="">Selecteer locatie</option>
                {locaties.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Datum *</label>
              <Input type="date" value={form.datum} onChange={(e) => setForm({...form, datum: e.target.value})} className="mt-1" data-testid="rie-datum-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Geïdentificeerde risico's *</label>
              <textarea value={form.risicos} onChange={(e) => setForm({...form, risicos: e.target.value})} 
                placeholder="Beschrijf de geïdentificeerde risico's..." rows={4}
                className="mt-1 w-full p-3 rounded-xl border border-slate-200 focus:border-red-400 resize-none" data-testid="rie-risicos-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Voorgestelde maatregelen</label>
              <textarea value={form.maatregelen} onChange={(e) => setForm({...form, maatregelen: e.target.value})} 
                placeholder="Welke maatregelen worden voorgesteld?" rows={3}
                className="mt-1 w-full p-3 rounded-xl border border-slate-200 focus:border-red-400 resize-none" data-testid="rie-maatregelen-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Status</label>
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} 
                className="mt-1 w-full p-2 rounded-xl border border-slate-200 focus:border-red-400" data-testid="rie-status-select">
                <option value="open">Open</option>
                <option value="afgehandeld">Afgehandeld</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuleren</Button>
            <Button onClick={saveEvaluatie} className="bg-gradient-to-r from-red-500 to-rose-500 text-white" data-testid="save-rie-btn">
              <Save className="w-4 h-4 mr-1" />Opslaan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================
// REVALIDATIE PAGE - Met cliënt selectie
// ============================================

export const RevalidatiePage = ({ onBack, user, selectedLocation }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [clienten, setClienten] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cliënten die revalidatie nodig hebben (kan later uit database komen)
  // Ricardo van den Bergh is verwijderd - niet meer in zorg
  const REVALIDATIE_CLIENTEN = {
    // Hier kunnen meer cliënten worden toegevoegd wanneer nodig
  };

  useEffect(() => {
    // Haal cliënten op
    const fetchClienten = async () => {
      try {
        const token = localStorage.getItem('token');
        const locationParam = selectedLocation ? `?location=${selectedLocation}` : '';
        const response = await axios.get(`${API_URL}/api/begeleider/overzicht${locationParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const alleClienten = response.data.alle_clienten || [];
        // Filter alleen cliënten die in REVALIDATIE_CLIENTEN staan
        const revalidatieClienten = alleClienten.filter(c => 
          Object.keys(REVALIDATIE_CLIENTEN).includes(c.voornaam)
        );
        setClienten(revalidatieClienten);
      } catch (error) {
        console.error('Error fetching clienten:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchClienten();
  }, []);

  // Als een cliënt is geselecteerd, toon de detail pagina
  if (selectedClient) {
    return (
      <RevalidatieClientPage 
        client={selectedClient}
        clientInfo={REVALIDATIE_CLIENTEN[selectedClient]}
        onBack={() => setSelectedClient(null)}
        user={user}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-red-50 to-pink-50">
      <PageHeader 
        title="Revalidatie" 
        subtitle="Selecteer een cliënt"
        onBack={onBack}
        icon={Heart}
        iconGradient="from-red-500 to-red-600"
      />

      <div className="p-4 space-y-4 pb-24">
        {/* Info Banner */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl p-4 text-white shadow-lg"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Heart className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Revalidatie Monitoring</h3>
              <p className="text-white/90 text-sm mt-1">
                Dagelijkse metingen voor cliënten met intensieve zorgbehoeften.
                Selecteer een cliënt om metingen in te voeren.
              </p>
            </div>
          </div>
        </motion.div>

        {loading ? (
          <LoadingSpinner color="red" />
        ) : Object.keys(REVALIDATIE_CLIENTEN).length === 0 ? (
          <EmptyState 
            icon={Heart} 
            title="Geen revalidatie cliënten" 
            subtitle="Er zijn momenteel geen cliënten met revalidatie monitoring" 
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Cliënten met revalidatie</h2>
            {Object.entries(REVALIDATIE_CLIENTEN).map(([naam, info], index) => (
              <motion.button
                key={naam}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setSelectedClient(naam)}
                className="w-full p-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-between hover:border-red-300 hover:shadow-md transition-all"
                data-testid={`revalidatie-client-${naam.toLowerCase()}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-${info.kleur}-500 to-${info.kleur}-600 flex items-center justify-center text-2xl shadow-lg`}>
                    {info.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-900">{naam}</h3>
                    <p className="text-sm text-slate-500">{info.volledige_naam}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                        {info.zorg_niveau} zorg
                      </span>
                      <span className="text-xs text-slate-400">{info.diagnose}</span>
                    </div>
                  </div>
                </div>
                <ChevronLeft className="w-5 h-5 text-slate-400 rotate-180" />
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Alias voor backwards compatibility
export const RevalidatieClientPage = ({ client, clientInfo, onBack, user }) => {
  // Dit is de oude RevalidatieRicardoPage, nu met dynamische client
  const [metingen, setMetingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showRichtlijnen, setShowRichtlijnen] = useState(false);
  const [showTrends, setShowTrends] = useState(false);
  const [selectedTrend, setSelectedTrend] = useState('gewicht');
  
  // Formulier voor nieuwe meting
  const [form, setForm] = useState({
    gewicht: '',
    bloeddruk_systolisch: '',
    bloeddruk_diastolisch: '',
    saturatie: '',
    temperatuur: '',
    vochtinname: '',
    benauwdheid: 'geen',
    oedeem: 'geen',
    opmerkingen: ''
  });

  // Check of het maandag is voor wekelijkse metingen herinnering
  const today = new Date();
  const isMonday = today.getDay() === 1; // 0 = zondag, 1 = maandag
  const dayNames = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  const todayName = dayNames[today.getDay()];
  
  // Check of wekelijkse metingen al gedaan zijn vandaag
  const todayStr = today.toISOString().split('T')[0];
  const weeklyMetingDoneToday = metingen.some(m => 
    m.datum === todayStr && 
    (m.bloeddruk_systolisch || m.saturatie || m.temperatuur)
  );

  // Bereken dagen tot volgende maandag
  const daysUntilMonday = isMonday ? 0 : (8 - today.getDay()) % 7;

  // Richtlijnen data met frequenties
  const RICHTLIJNEN = {
    gewicht: {
      label: 'Gewicht',
      icon: Scale,
      frequentie: 'DAGELIJKS',
      frequentieKleur: 'blue',
      normaal: 'Stabiel gewicht',
      alarm: 'Toename ≥ 2-3 kg in 2-3 dagen',
      actie: 'Direct contact hartfalenverpleegkundige',
      color: 'blue'
    },
    vochtinname: {
      label: 'Vochtinname',
      icon: Droplets,
      frequentie: 'DAGELIJKS',
      frequentieKleur: 'blue',
      normaal: 'Max. 1,5 liter per dag',
      alarm: '> 1,5 liter',
      actie: 'Alle dranken + lepelbare voeding tellen',
      color: 'cyan'
    },
    benauwdheid: {
      label: 'Benauwdheid',
      icon: AlertTriangle,
      frequentie: 'DAGELIJKS',
      frequentieKleur: 'blue',
      normaal: 'Geen benauwdheid',
      alarm: 'Benauwdheid in rust of bij lichte inspanning',
      actie: 'Meld bij ernstige klachten',
      color: 'amber'
    },
    oedeem: {
      label: 'Oedeem',
      icon: Droplets,
      frequentie: 'DAGELIJKS',
      frequentieKleur: 'blue',
      normaal: 'Geen oedeem',
      alarm: 'Toenemend oedeem in benen of buik',
      actie: 'Meld bij verergering',
      color: 'blue'
    },
    bloeddruk: {
      label: 'Bloeddruk',
      icon: Activity,
      frequentie: 'WEKELIJKS',
      frequentieKleur: 'purple',
      normaal: '90-150 mmHg (systolisch)',
      alarm: '< 90 mmHg met klachten OF > 150 mmHg',
      actie: 'Bij klachten (duizeligheid, zwakte): bellen',
      color: 'red'
    },
    saturatie: {
      label: 'Zuurstofsaturatie',
      icon: Heart,
      frequentie: 'WEKELIJKS',
      frequentieKleur: 'purple',
      normaal: '> 90%',
      alarm: '< 90%',
      actie: 'Opnieuw meten, zo nodig bellen',
      color: 'purple'
    },
    temperatuur: {
      label: 'Temperatuur',
      icon: Thermometer,
      frequentie: 'WEKELIJKS',
      frequentieKleur: 'purple',
      normaal: '36.0 - 37.5 °C',
      alarm: '> 38.0 °C of < 35.5 °C',
      actie: 'Bij koorts: contact huisarts',
      color: 'orange'
    }
  };

  const fetchMetingen = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/revalidatie/ricardo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetingen(response.data.metingen || []);
    } catch (error) {
      setMetingen([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetingen();
  }, []);

  const saveMeting = async () => {
    if (!form.gewicht && !form.bloeddruk_systolisch && !form.saturatie) {
      toast.error('Vul minimaal één meting in');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/revalidatie/ricardo`, {
        ...form,
        begeleider: user?.name || 'Begeleider',
        datum: new Date().toISOString().split('T')[0],
        tijd: new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Meting opgeslagen!');
      setForm({
        gewicht: '',
        bloeddruk_systolisch: '',
        bloeddruk_diastolisch: '',
        saturatie: '',
        temperatuur: '',
        vochtinname: '',
        benauwdheid: 'geen',
        oedeem: 'geen',
        opmerkingen: ''
      });
      fetchMetingen();
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  // Bepaal status indicator voor een waarde
  const getStatus = (type, value) => {
    if (!value) return null;
    const v = parseFloat(value);
    
    switch(type) {
      case 'saturatie':
        if (v < 90) return { status: 'danger', icon: TrendingDown };
        if (v < 94) return { status: 'warning', icon: Minus };
        return { status: 'ok', icon: TrendingUp };
      case 'bloeddruk':
        if (v < 90 || v > 150) return { status: 'danger', icon: TrendingDown };
        if (v < 100 || v > 140) return { status: 'warning', icon: Minus };
        return { status: 'ok', icon: TrendingUp };
      case 'temperatuur':
        if (v > 38 || v < 35.5) return { status: 'danger', icon: TrendingDown };
        if (v > 37.5 || v < 36) return { status: 'warning', icon: Minus };
        return { status: 'ok', icon: TrendingUp };
      default:
        return { status: 'ok', icon: Minus };
    }
  };

  const statusColors = {
    ok: 'text-green-600 bg-green-100',
    warning: 'text-amber-600 bg-amber-100',
    danger: 'text-red-600 bg-red-100'
  };

  // Trend data voorbereiden (laatste 14 metingen, omgekeerd voor chronologisch)
  const trendData = [...metingen].reverse().slice(-14).map(m => ({
    datum: m.datum ? m.datum.slice(5) : '', // Format: MM-DD
    gewicht: m.gewicht ? parseFloat(m.gewicht) : null,
    systolisch: m.bloeddruk_systolisch ? parseFloat(m.bloeddruk_systolisch) : null,
    diastolisch: m.bloeddruk_diastolisch ? parseFloat(m.bloeddruk_diastolisch) : null,
    saturatie: m.saturatie ? parseFloat(m.saturatie) : null,
    temperatuur: m.temperatuur ? parseFloat(m.temperatuur) : null,
    vochtinname: m.vochtinname ? parseFloat(m.vochtinname) : null,
  }));

  // Bereken trend (stijgend/dalend/stabiel) voor een metric
  const calculateTrend = (metric) => {
    const values = trendData.filter(d => d[metric] !== null).map(d => d[metric]);
    if (values.length < 2) return { trend: 'stabiel', change: 0 };
    
    const recent = values.slice(-3);
    const older = values.slice(-6, -3);
    
    if (recent.length === 0 || older.length === 0) return { trend: 'stabiel', change: 0 };
    
    const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
    const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
    const change = recentAvg - olderAvg;
    const percentChange = ((recentAvg - olderAvg) / olderAvg) * 100;
    
    if (Math.abs(percentChange) < 2) return { trend: 'stabiel', change: 0 };
    return { 
      trend: change > 0 ? 'stijgend' : 'dalend', 
      change: change.toFixed(1),
      percent: percentChange.toFixed(1)
    };
  };

  // Trend configuratie per metric
  const TREND_CONFIG = {
    gewicht: {
      label: 'Gewicht',
      unit: 'kg',
      color: '#3b82f6',
      gradientId: 'gewichtGradient',
      warningHigh: null, // Gewicht: alarm bij snelle toename
      warningLow: null,
      dangerChange: 2, // 2-3 kg toename in 2-3 dagen
    },
    systolisch: {
      label: 'Bloeddruk (systolisch)',
      unit: 'mmHg',
      color: '#ef4444',
      gradientId: 'bpGradient',
      warningHigh: 150,
      warningLow: 90,
      referenceLines: [
        { y: 150, label: 'Max', color: '#ef4444' },
        { y: 90, label: 'Min', color: '#f59e0b' },
      ]
    },
    saturatie: {
      label: 'Saturatie',
      unit: '%',
      color: '#8b5cf6',
      gradientId: 'satGradient',
      warningLow: 90,
      referenceLines: [
        { y: 90, label: 'Min', color: '#ef4444' },
      ]
    },
    temperatuur: {
      label: 'Temperatuur',
      unit: '°C',
      color: '#f97316',
      gradientId: 'tempGradient',
      warningHigh: 38,
      warningLow: 36,
      referenceLines: [
        { y: 38, label: 'Koorts', color: '#ef4444' },
        { y: 36, label: 'Laag', color: '#f59e0b' },
      ]
    },
    vochtinname: {
      label: 'Vochtinname',
      unit: 'ml',
      color: '#06b6d4',
      gradientId: 'vochtGradient',
      warningHigh: 1500,
      referenceLines: [
        { y: 1500, label: 'Max', color: '#ef4444' },
      ]
    },
  };

  // Laatste meting
  const laatsteMeting = metingen[0];
  
  // Vandaag al gemeten?
  const vandaag = new Date().toISOString().split('T')[0];
  const vandaagGemeten = metingen.some(m => m.datum === vandaag);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-red-50 to-pink-50">
      <PageHeader 
        title={`Revalidatie ${client}`} 
        subtitle="Metingen & monitoring"
        onBack={onBack}
        icon={Heart}
        iconGradient="from-red-500 to-red-600"
        rightAction={
          <button 
            onClick={() => setShowRichtlijnen(true)}
            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium"
            data-testid="show-richtlijnen-btn"
          >
            📋 Richtlijnen
          </button>
        }
      />

      <div className="p-4 space-y-4 pb-24">
        {/* Waarschuwing banner */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl p-4 text-white shadow-lg"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Heart className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg">⚠️ 24/7 Zorg Nodig</h3>
              <p className="text-white/90 text-sm mt-1">
                {client} heeft ernstig hartfalen (EF 10-15%). Dagelijks wegen vóór ontbijt. 
                Vochtbeperking MAX 1,5L/dag. Bij alarmsignalen: direct contact!
              </p>
            </div>
          </div>
        </motion.div>

        {/* Status vandaag */}
        {!vandaagGemeten && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800 text-sm font-medium">Vandaag nog niet gemeten - vul de metingen hieronder in</span>
          </motion.div>
        )}

        {/* MAANDAG HERINNERING - Wekelijkse metingen */}
        {isMonday && !weeklyMetingDoneToday && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-purple-500 to-violet-500 rounded-2xl p-4 text-white shadow-lg"
            data-testid="monday-reminder-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  📅 Het is maandag!
                </h3>
                <p className="text-white/90 text-sm mt-1">
                  Vergeet niet de <strong>wekelijkse metingen</strong> te doen:
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium flex items-center gap-1">
                    <Activity className="w-4 h-4" /> Bloeddruk
                  </span>
                  <span className="px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium flex items-center gap-1">
                    <Heart className="w-4 h-4" /> Saturatie
                  </span>
                  <span className="px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium flex items-center gap-1">
                    <Thermometer className="w-4 h-4" /> Temperatuur
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Bevestiging als wekelijkse meting gedaan is op maandag */}
        {isMonday && weeklyMetingDoneToday && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3"
            data-testid="weekly-done-banner"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 text-sm font-medium">✅ Wekelijkse metingen vandaag voltooid!</span>
          </motion.div>
        )}

        {/* Indicator voor niet-maandag dagen */}
        {!isMonday && daysUntilMonday > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-3">
            <Calendar className="w-5 h-5 text-slate-500" />
            <span className="text-slate-600 text-sm">
              Wekelijkse metingen (bloeddruk, saturatie, temp.) over <strong>{daysUntilMonday} {daysUntilMonday === 1 ? 'dag' : 'dagen'}</strong> (maandag)
            </span>
          </div>
        )}

        {vandaagGemeten && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3"
          >
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-800 text-sm font-medium">✓ Vandaag al gemeten</span>
          </motion.div>
        )}

        {/* Laatste meting samenvatting */}
        {laatsteMeting && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800">Laatste Meting</h3>
              <span className="text-xs text-slate-500">{laatsteMeting.datum} om {laatsteMeting.tijd}</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {laatsteMeting.gewicht && (
                <div className="text-center p-2 bg-blue-50 rounded-xl">
                  <Scale className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <div className="text-lg font-bold text-blue-700">{laatsteMeting.gewicht}</div>
                  <div className="text-xs text-blue-600">kg</div>
                </div>
              )}
              {laatsteMeting.bloeddruk_systolisch && (
                <div className="text-center p-2 bg-red-50 rounded-xl">
                  <Activity className="w-5 h-5 text-red-600 mx-auto mb-1" />
                  <div className="text-lg font-bold text-red-700">{laatsteMeting.bloeddruk_systolisch}/{laatsteMeting.bloeddruk_diastolisch || '?'}</div>
                  <div className="text-xs text-red-600">mmHg</div>
                </div>
              )}
              {laatsteMeting.saturatie && (
                <div className={`text-center p-2 rounded-xl ${parseFloat(laatsteMeting.saturatie) < 90 ? 'bg-red-100' : 'bg-purple-50'}`}>
                  <Heart className={`w-5 h-5 mx-auto mb-1 ${parseFloat(laatsteMeting.saturatie) < 90 ? 'text-red-600' : 'text-purple-600'}`} />
                  <div className={`text-lg font-bold ${parseFloat(laatsteMeting.saturatie) < 90 ? 'text-red-700' : 'text-purple-700'}`}>{laatsteMeting.saturatie}%</div>
                  <div className={`text-xs ${parseFloat(laatsteMeting.saturatie) < 90 ? 'text-red-600' : 'text-purple-600'}`}>SpO2</div>
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-2">Door: {laatsteMeting.begeleider}</div>
          </motion.div>
        )}

        {/* Nieuwe meting formulier */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
        >
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-red-500" />
            Nieuwe Meting Invoeren
          </h3>
          
          <div className="space-y-4">
            {/* DAGELIJKSE METINGEN SECTIE */}
            <div className="bg-blue-50 rounded-xl p-3 mb-2">
              <h4 className="text-sm font-bold text-blue-800 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                DAGELIJKS METEN
              </h4>
            </div>

            {/* Gewicht */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                <Scale className="w-4 h-4 text-blue-500" />
                Gewicht (kg) - <span className="text-blue-600 font-normal">Dagelijks vóór ontbijt</span>
              </label>
              <Input 
                type="number" 
                step="0.1"
                placeholder="Bijv. 85.5" 
                value={form.gewicht}
                onChange={(e) => setForm({...form, gewicht: e.target.value})}
                className="text-lg"
                data-testid="ricardo-gewicht-input"
              />
            </div>

            {/* Vochtinname - DAGELIJKS */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                <Droplets className="w-4 h-4 text-cyan-500" />
                Vochtinname (ml) - <span className="text-cyan-600 font-normal">Dagelijks bijhouden, MAX 1500 ml/dag</span>
              </label>
              <Input 
                type="number" 
                placeholder="Bijv. 1200" 
                value={form.vochtinname}
                onChange={(e) => setForm({...form, vochtinname: e.target.value})}
                data-testid="ricardo-vochtinname-input"
              />
              {form.vochtinname && parseFloat(form.vochtinname) > 1500 && (
                <p className="text-red-600 text-sm mt-1 font-medium">⚠️ Boven limiet! Max 1,5L per dag incl. lepelbare voeding</p>
              )}
            </div>

            {/* Benauwdheid - DAGELIJKS */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Benauwdheid - <span className="text-amber-600 font-normal">Dagelijks checken</span>
              </label>
              <select 
                value={form.benauwdheid}
                onChange={(e) => setForm({...form, benauwdheid: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                data-testid="ricardo-benauwdheid-select"
              >
                <option value="geen">Geen benauwdheid</option>
                <option value="licht">Lichte benauwdheid bij inspanning</option>
                <option value="matig">Matige benauwdheid in rust</option>
                <option value="ernstig">Ernstige benauwdheid - ALARM</option>
              </select>
            </div>

            {/* Oedeem - DAGELIJKS */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                <Droplets className="w-4 h-4 text-blue-400" />
                Oedeem (vochtophoping) - <span className="text-blue-600 font-normal">Dagelijks controleren</span>
              </label>
              <select 
                value={form.oedeem}
                onChange={(e) => setForm({...form, oedeem: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                data-testid="ricardo-oedeem-select"
              >
                <option value="geen">Geen oedeem</option>
                <option value="licht">Licht oedeem enkels</option>
                <option value="matig">Matig oedeem onderbenen</option>
                <option value="ernstig">Ernstig oedeem - ALARM</option>
              </select>
            </div>

            {/* WEKELIJKSE METINGEN SECTIE */}
            <div className="bg-purple-50 rounded-xl p-3 mt-6 mb-2">
              <h4 className="text-sm font-bold text-purple-800 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                WEKELIJKS METEN (elke maandag)
              </h4>
            </div>

            {/* Bloeddruk - WEKELIJKS */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                <Activity className="w-4 h-4 text-red-500" />
                Bloeddruk (mmHg) - <span className="text-purple-600 font-normal">Wekelijks meten</span>
              </label>
              <div className="flex gap-2 items-center">
                <Input 
                  type="number" 
                  placeholder="Systolisch" 
                  value={form.bloeddruk_systolisch}
                  onChange={(e) => setForm({...form, bloeddruk_systolisch: e.target.value})}
                  className="text-lg"
                  data-testid="ricardo-systolisch-input"
                />
                <span className="text-slate-400 font-bold">/</span>
                <Input 
                  type="number" 
                  placeholder="Diastolisch" 
                  value={form.bloeddruk_diastolisch}
                  onChange={(e) => setForm({...form, bloeddruk_diastolisch: e.target.value})}
                  className="text-lg"
                  data-testid="ricardo-diastolisch-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Normaal: 90-150 mmHg systolisch</p>
            </div>

            {/* Saturatie - WEKELIJKS */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                <Heart className="w-4 h-4 text-purple-500" />
                Zuurstofsaturatie (%) - <span className="text-purple-600 font-normal">Wekelijks meten</span>
              </label>
              <Input 
                type="number" 
                placeholder="Bijv. 95" 
                value={form.saturatie}
                onChange={(e) => setForm({...form, saturatie: e.target.value})}
                className="text-lg"
                data-testid="ricardo-saturatie-input"
              />
              {form.saturatie && parseFloat(form.saturatie) < 90 && (
                <p className="text-red-600 text-sm mt-1 font-medium">⚠️ ALARM: Saturatie &lt; 90% - Opnieuw meten, zo nodig bellen!</p>
              )}
              <p className="text-xs text-slate-500 mt-1">Normaal: &gt; 90%</p>
            </div>

            {/* Temperatuur - WEKELIJKS */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                <Thermometer className="w-4 h-4 text-orange-500" />
                Temperatuur (°C) - <span className="text-purple-600 font-normal">Wekelijks meten</span>
              </label>
              <Input 
                type="number" 
                step="0.1"
                placeholder="Bijv. 36.8" 
                value={form.temperatuur}
                onChange={(e) => setForm({...form, temperatuur: e.target.value})}
                data-testid="ricardo-temperatuur-input"
              />
              <p className="text-xs text-slate-500 mt-1">Normaal: 36.0-37.5 °C</p>
            </div>

            {/* OVERIG SECTIE */}
            <div className="bg-slate-50 rounded-xl p-3 mt-6 mb-2">
              <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                OPMERKINGEN
              </h4>
            </div>

            {/* Opmerkingen */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Opmerkingen / Bijzonderheden</label>
              <textarea 
                value={form.opmerkingen}
                onChange={(e) => setForm({...form, opmerkingen: e.target.value})}
                placeholder="Eventuele bijzonderheden, klachten, observaties..."
                rows={3}
                className="w-full p-3 rounded-xl border border-slate-200 focus:border-red-400 resize-none"
                data-testid="ricardo-opmerkingen-input"
              />
            </div>

            {/* Opslaan knop */}
            <Button 
              onClick={saveMeting}
              disabled={saving}
              className="w-full bg-gradient-to-r from-red-500 to-rose-500 text-white py-6 text-lg rounded-xl shadow-lg"
              data-testid="save-ricardo-meting-btn"
            >
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
              Meting Opslaan
            </Button>
          </div>
        </motion.div>

        {/* Trends knop */}
        {metingen.length >= 2 && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            onClick={() => setShowTrends(true)}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl p-4 shadow-lg flex items-center justify-between text-white"
            data-testid="show-trends-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-semibold">📊 Trend Grafieken</div>
                <div className="text-sm text-white/80">Bekijk gewicht, bloeddruk & saturatie over tijd</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-white/70 rotate-180" />
          </motion.button>
        )}

        {/* Historiek knop */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          onClick={() => setShowHistory(true)}
          className="w-full bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex items-center justify-between"
          data-testid="show-history-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <History className="w-5 h-5 text-slate-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-slate-800">Meethistoriek</div>
              <div className="text-sm text-slate-500">{metingen.length} metingen opgeslagen</div>
            </div>
          </div>
          <ChevronLeft className="w-5 h-5 text-slate-400 rotate-180" />
        </motion.button>
      </div>

      {/* Richtlijnen Modal */}
      <Dialog open={showRichtlijnen} onOpenChange={setShowRichtlijnen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Richtlijnen {client} - Hartfalen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {Object.entries(RICHTLIJNEN).map(([key, item]) => {
              const Icon = item.icon;
              return (
                <div key={key} className={`p-4 rounded-xl border-l-4 border-${item.color}-500 bg-${item.color}-50`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 text-${item.color}-600`} />
                    <span className="font-semibold text-slate-800">{item.label}</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium text-green-700">✓ Normaal:</span> {item.normaal}</p>
                    <p><span className="font-medium text-red-700">⚠ Alarm:</span> {item.alarm}</p>
                    <p><span className="font-medium text-blue-700">→ Actie:</span> {item.actie}</p>
                  </div>
                </div>
              );
            })}
            
            <div className="p-4 bg-red-100 rounded-xl border border-red-200">
              <h4 className="font-bold text-red-800 mb-2">🚨 Alarmsignalen - Direct Bellen!</h4>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Gewichtstoename ≥ 2-3 kg in 2-3 dagen</li>
                <li>• Bloeddruk &lt; 90 mmHg MET klachten</li>
                <li>• Saturatie &lt; 90%</li>
                <li>• Ernstige benauwdheid</li>
                <li>• Oedeem buik/benen</li>
                <li>• Misselijkheid, verminderde eetlust, extreme vermoeidheid</li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 rounded-xl">
              <h4 className="font-bold text-blue-800 mb-2">📞 Contact Hartfalenverpleegkundige</h4>
              <p className="text-sm text-blue-700">
                Bij alarmsignalen: liever te vroeg dan te laat contact opnemen.
                Meetgegevens wekelijks of voorafgaand aan afspraak doorgeven.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Historiek Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Meethistoriek {client}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {metingen.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Nog geen metingen opgeslagen</p>
            ) : (
              metingen.map((m, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-800">{m.datum}</span>
                    <span className="text-sm text-slate-500">{m.tijd}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    {m.gewicht && (
                      <div className="bg-blue-100 rounded-lg p-2 text-center">
                        <div className="text-xs text-blue-600">Gewicht</div>
                        <div className="font-bold text-blue-800">{m.gewicht} kg</div>
                      </div>
                    )}
                    {m.bloeddruk_systolisch && (
                      <div className={`rounded-lg p-2 text-center ${parseFloat(m.bloeddruk_systolisch) < 90 || parseFloat(m.bloeddruk_systolisch) > 150 ? 'bg-red-100' : 'bg-green-100'}`}>
                        <div className={`text-xs ${parseFloat(m.bloeddruk_systolisch) < 90 || parseFloat(m.bloeddruk_systolisch) > 150 ? 'text-red-600' : 'text-green-600'}`}>Bloeddruk</div>
                        <div className={`font-bold ${parseFloat(m.bloeddruk_systolisch) < 90 || parseFloat(m.bloeddruk_systolisch) > 150 ? 'text-red-800' : 'text-green-800'}`}>{m.bloeddruk_systolisch}/{m.bloeddruk_diastolisch || '?'}</div>
                      </div>
                    )}
                    {m.saturatie && (
                      <div className={`rounded-lg p-2 text-center ${parseFloat(m.saturatie) < 90 ? 'bg-red-100' : 'bg-purple-100'}`}>
                        <div className={`text-xs ${parseFloat(m.saturatie) < 90 ? 'text-red-600' : 'text-purple-600'}`}>SpO2</div>
                        <div className={`font-bold ${parseFloat(m.saturatie) < 90 ? 'text-red-800' : 'text-purple-800'}`}>{m.saturatie}%</div>
                      </div>
                    )}
                  </div>
                  {(m.benauwdheid !== 'geen' || m.oedeem !== 'geen') && (
                    <div className="mt-2 flex gap-2">
                      {m.benauwdheid !== 'geen' && (
                        <span className={`text-xs px-2 py-1 rounded-full ${m.benauwdheid === 'ernstig' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                          Benauwdheid: {m.benauwdheid}
                        </span>
                      )}
                      {m.oedeem !== 'geen' && (
                        <span className={`text-xs px-2 py-1 rounded-full ${m.oedeem === 'buik' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                          Oedeem: {m.oedeem}
                        </span>
                      )}
                    </div>
                  )}
                  {m.opmerkingen && (
                    <p className="text-xs text-slate-600 mt-2 italic">"{m.opmerkingen}"</p>
                  )}
                  <div className="text-xs text-slate-400 mt-2">Door: {m.begeleider}</div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Trends Modal */}
      <Dialog open={showTrends} onOpenChange={setShowTrends}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-500" />
              Trend Grafieken - {client}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Metric selector tabs */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(TREND_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setSelectedTrend(key)}
                  className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                    selectedTrend === key
                      ? 'text-white shadow-lg'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  style={selectedTrend === key ? { backgroundColor: config.color } : {}}
                  data-testid={`trend-tab-${key}`}
                >
                  {config.label}
                </button>
              ))}
            </div>

            {/* Trend indicator */}
            {trendData.length >= 2 && (
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-slate-500">Huidige trend:</span>
                    <div className="flex items-center gap-2 mt-1">
                      {(() => {
                        const trend = calculateTrend(selectedTrend);
                        const config = TREND_CONFIG[selectedTrend];
                        
                        // Bepaal of trend goed of slecht is
                        let isGood = trend.trend === 'stabiel';
                        if (selectedTrend === 'gewicht' && trend.trend === 'stijgend') isGood = false;
                        if (selectedTrend === 'saturatie' && trend.trend === 'dalend') isGood = false;
                        
                        return (
                          <>
                            {trend.trend === 'stijgend' && (
                              <TrendingUp className={`w-5 h-5 ${isGood ? 'text-green-500' : 'text-red-500'}`} />
                            )}
                            {trend.trend === 'dalend' && (
                              <TrendingDown className={`w-5 h-5 ${isGood ? 'text-green-500' : 'text-red-500'}`} />
                            )}
                            {trend.trend === 'stabiel' && (
                              <Minus className="w-5 h-5 text-blue-500" />
                            )}
                            <span className={`font-semibold ${
                              trend.trend === 'stabiel' ? 'text-blue-600' :
                              isGood ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {trend.trend === 'stabiel' ? 'Stabiel' :
                               trend.trend === 'stijgend' ? `Stijgend (+${trend.change} ${config.unit})` :
                               `Dalend (${trend.change} ${config.unit})`}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-slate-500">Laatste waarde:</span>
                    <div className="text-lg font-bold" style={{ color: TREND_CONFIG[selectedTrend].color }}>
                      {(() => {
                        const lastValue = trendData.filter(d => d[selectedTrend] !== null).slice(-1)[0];
                        return lastValue ? `${lastValue[selectedTrend]} ${TREND_CONFIG[selectedTrend].unit}` : '-';
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Grafiek */}
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <h4 className="font-medium text-slate-700 mb-4">
                {TREND_CONFIG[selectedTrend].label} - Laatste 14 metingen
              </h4>
              
              {trendData.length < 2 ? (
                <div className="h-48 flex items-center justify-center text-slate-400">
                  <p>Minimaal 2 metingen nodig voor een grafiek</p>
                </div>
              ) : (
                <div className="h-64" data-testid="trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id={TREND_CONFIG[selectedTrend].gradientId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={TREND_CONFIG[selectedTrend].color} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={TREND_CONFIG[selectedTrend].color} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="datum" 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                        domain={['auto', 'auto']}
                        unit={` ${TREND_CONFIG[selectedTrend].unit}`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value) => [`${value} ${TREND_CONFIG[selectedTrend].unit}`, TREND_CONFIG[selectedTrend].label]}
                        labelFormatter={(label) => `Datum: ${label}`}
                      />
                      {/* Reference lines voor alarm-grenzen */}
                      {TREND_CONFIG[selectedTrend].referenceLines?.map((ref, idx) => (
                        <ReferenceLine 
                          key={idx}
                          y={ref.y} 
                          stroke={ref.color} 
                          strokeDasharray="5 5"
                          label={{ value: ref.label, position: 'right', fontSize: 10, fill: ref.color }}
                        />
                      ))}
                      <Area
                        type="monotone"
                        dataKey={selectedTrend}
                        stroke={TREND_CONFIG[selectedTrend].color}
                        strokeWidth={2}
                        fill={`url(#${TREND_CONFIG[selectedTrend].gradientId})`}
                        dot={{ fill: TREND_CONFIG[selectedTrend].color, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, fill: TREND_CONFIG[selectedTrend].color }}
                        connectNulls={true}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Gewichtstoename waarschuwing */}
            {selectedTrend === 'gewicht' && trendData.length >= 3 && (() => {
              const recent = trendData.filter(d => d.gewicht !== null).slice(-3);
              if (recent.length >= 2) {
                const diff = recent[recent.length - 1].gewicht - recent[0].gewicht;
                if (diff >= 2) {
                  return (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-semibold">⚠️ ALARM: Gewichtstoename {diff.toFixed(1)} kg in laatste {recent.length} metingen!</span>
                      </div>
                      <p className="text-red-600 text-sm mt-1">
                        Toename ≥ 2-3 kg in 2-3 dagen kan duiden op vochtophoping. 
                        Neem contact op met de hartfalenverpleegkundige.
                      </p>
                    </div>
                  );
                }
              }
              return null;
            })()}

            {/* Legenda en tips */}
            <div className="bg-indigo-50 rounded-xl p-4 text-sm">
              <h5 className="font-semibold text-indigo-800 mb-2">💡 Tips voor begeleiders</h5>
              <ul className="text-indigo-700 space-y-1">
                {selectedTrend === 'gewicht' && (
                  <>
                    <li>• Weeg dagelijks <strong>vóór ontbijt</strong>, na plassen</li>
                    <li>• Let op: toename ≥ 2-3 kg in 2-3 dagen = ALARM</li>
                    <li>• Stuur meetgegevens wekelijks naar hartfalenverpleegkundige</li>
                  </>
                )}
                {selectedTrend === 'systolisch' && (
                  <>
                    <li>• Normaal: 90-150 mmHg (systolisch)</li>
                    <li>• Bij bloeddruk &lt; 90 + klachten: direct bellen</li>
                    <li>• Klachten: duizeligheid, zwakte, verwardheid</li>
                  </>
                )}
                {selectedTrend === 'saturatie' && (
                  <>
                    <li>• Normaal: &gt; 90%</li>
                    <li>• Bij saturatie &lt; 90%: opnieuw meten, zo nodig bellen</li>
                    <li>• Combineer met benauwdheidsklachten</li>
                  </>
                )}
                {selectedTrend === 'temperatuur' && (
                  <>
                    <li>• Normaal: 36.0 - 37.5 °C</li>
                    <li>• Bij koorts (&gt; 38°C): contact huisarts</li>
                    <li>• Infectie kan hartfalen verergeren</li>
                  </>
                )}
                {selectedTrend === 'vochtinname' && (
                  <>
                    <li>• MAX 1,5 liter per dag</li>
                    <li>• Tel alle dranken + lepelbare voeding (soep, vla)</li>
                    <li>• IJsblokjes zuigen bij dorst ipv extra drinken</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================
// EVALUATIE OVERZICHT PAGE - Zorgplan, RI+E, SIP per PB'er
// ============================================

export const EvaluatieOverzichtPage = ({ onBack, user }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [editForm, setEditForm] = useState({
    zorgplan_datum: '',
    zorgplan_evaluatie_datum: '',
    zorgplan_aanwezigen: '',
    rie_opgesteld_datum: '',
    rie_evaluatie_datum: '',
    rie_afgevinkt: false,
    sip_opgesteld_datum: '',
    sip_evaluatie_datum: '',
    sip_afgevinkt: false
  });

  // Bepaal de locatie van de gebruiker
  const userLocation = user?.location || user?.locatie || 'groep-zwaluw';

  // FALLBACK DATA per locatie
  const FALLBACK_EVALUATIE_DATA = {
    'groep-zwaluw': [
      { client: 'Ricardo', pb_er: 'Karim', zorgplan_datum: '2025-06-15', zorgplan_evaluatie_datum: '2026-06-15', zorgplan_aanwezigen: 'Ricardo, Karim, Erva', rie_opgesteld_datum: '2025-07-01', rie_evaluatie_datum: '2026-07-01', rie_afgevinkt: true, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Axcel', pb_er: 'Karim', zorgplan_datum: '2025-08-20', zorgplan_evaluatie_datum: '2026-08-20', zorgplan_aanwezigen: 'Axcel, Karim', rie_opgesteld_datum: '2025-09-01', rie_evaluatie_datum: '2026-09-01', rie_afgevinkt: true, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Richard', pb_er: 'Redouan', zorgplan_datum: '2025-05-10', zorgplan_evaluatie_datum: '2026-05-10', zorgplan_aanwezigen: 'Richard, Redouan, Erva', rie_opgesteld_datum: '2025-06-01', rie_evaluatie_datum: '2026-06-01', rie_afgevinkt: true, sip_opgesteld_datum: '2025-06-15', sip_evaluatie_datum: '2026-06-15', sip_afgevinkt: true },
      { client: 'Mahad', pb_er: 'Karim', zorgplan_datum: '2025-10-01', zorgplan_evaluatie_datum: '2026-10-01', zorgplan_aanwezigen: 'Mahad, Karim', rie_opgesteld_datum: '2025-10-15', rie_evaluatie_datum: '2026-10-15', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Sietse', pb_er: 'Karim', zorgplan_datum: '', zorgplan_evaluatie_datum: '', zorgplan_aanwezigen: '', rie_opgesteld_datum: '', rie_evaluatie_datum: '', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Yassin', pb_er: 'Serkan', zorgplan_datum: '', zorgplan_evaluatie_datum: '', zorgplan_aanwezigen: '', rie_opgesteld_datum: '', rie_evaluatie_datum: '', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
    ],
    'groep-adelaars': [
      { client: 'Melvin', pb_er: 'Nassim', zorgplan_datum: '', zorgplan_evaluatie_datum: '', zorgplan_aanwezigen: '', rie_opgesteld_datum: '', rie_evaluatie_datum: '', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Mike', pb_er: 'Ilias en Redouan', zorgplan_datum: '', zorgplan_evaluatie_datum: '', zorgplan_aanwezigen: '', rie_opgesteld_datum: '', rie_evaluatie_datum: '', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Rinaldo', pb_er: 'Serkan en Ilias', zorgplan_datum: '', zorgplan_evaluatie_datum: '', zorgplan_aanwezigen: '', rie_opgesteld_datum: '', rie_evaluatie_datum: '', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Joey', pb_er: 'Redouan', zorgplan_datum: '', zorgplan_evaluatie_datum: '', zorgplan_aanwezigen: '', rie_opgesteld_datum: '', rie_evaluatie_datum: '', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Faraah', pb_er: '', zorgplan_datum: '', zorgplan_evaluatie_datum: '', zorgplan_aanwezigen: '', rie_opgesteld_datum: '', rie_evaluatie_datum: '', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
      { client: 'Julian', pb_er: 'Ilias', zorgplan_datum: '', zorgplan_evaluatie_datum: '', zorgplan_aanwezigen: '', rie_opgesteld_datum: '', rie_evaluatie_datum: '', rie_afgevinkt: false, sip_opgesteld_datum: '', sip_evaluatie_datum: '', sip_afgevinkt: false },
    ]
  };

  // Haal de juiste fallback data op basis van locatie
  const getFallbackData = () => {
    return FALLBACK_EVALUATIE_DATA[userLocation] || FALLBACK_EVALUATIE_DATA['groep-zwaluw'];
  };

  const fetchData = async () => {
    setLoading(true);
    setUseFallback(false);
    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      const response = await axios.get(`${API_URL}/api/evaluatie-overzicht`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const apiData = response.data.overzicht || [];
      if (apiData.length > 0) {
        setData(apiData);
      } else {
        // DEBUG: console.log('API returned empty evaluatie data, using fallback for', userLocation);
        setData(getFallbackData());
        setUseFallback(true);
      }
    } catch (error) {
      console.error('Error fetching evaluatie overzicht:', error);
      // Use fallback for the user's location
      setData(getFallbackData());
      setUseFallback(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userLocation]);

  // Groepeer data per PB'er
  const groupedByPBer = useMemo(() => {
    const groups = {};
    data.forEach(item => {
      const pbEr = item.pb_er || 'Niet toegewezen';
      if (!groups[pbEr]) {
        groups[pbEr] = [];
      }
      groups[pbEr].push(item);
    });
    // Sorteer PB'ers alfabetisch
    return Object.keys(groups).sort().reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {});
  }, [data]);

  const openEdit = (item) => {
    setEditingItem(item);
    setEditForm({
      zorgplan_datum: item.zorgplan_datum || '',
      zorgplan_evaluatie_datum: item.zorgplan_evaluatie_datum || '',
      zorgplan_aanwezigen: item.zorgplan_aanwezigen || '',
      rie_opgesteld_datum: item.rie_opgesteld_datum || '',
      rie_evaluatie_datum: item.rie_evaluatie_datum || '',
      rie_afgevinkt: item.rie_afgevinkt || false,
      sip_opgesteld_datum: item.sip_opgesteld_datum || '',
      sip_evaluatie_datum: item.sip_evaluatie_datum || '',
      sip_afgevinkt: item.sip_afgevinkt || false
    });
    setShowEditModal(true);
  };

  const saveEvaluatie = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/evaluatie-overzicht/${editingItem.client}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evaluatie bijgewerkt');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  // Formatteer datum naar Nederlands formaat
  const formatDatum = (datum) => {
    if (!datum) return '-';
    try {
      const date = new Date(datum);
      return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return datum;
    }
  };

  // Check of evaluatie binnenkort is (binnen 30 dagen)
  const isBinnenkort = (datum) => {
    if (!datum) return false;
    const evalDate = new Date(datum);
    const now = new Date();
    const diffDays = Math.ceil((evalDate - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  };

  // Check of evaluatie verlopen is
  const isVerlopen = (datum) => {
    if (!datum) return false;
    const evalDate = new Date(datum);
    const now = new Date();
    return evalDate < now;
  };

  // Bepaal status kleur
  const getStatusColor = (datum, afgevinkt) => {
    if (afgevinkt) return 'text-green-600';
    if (isVerlopen(datum)) return 'text-red-600 font-semibold';
    if (isBinnenkort(datum)) return 'text-amber-600';
    return 'text-slate-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
      <PageHeader 
        title="Evaluatie Overzicht" 
        subtitle="Per PB'er"
        onBack={onBack}
        icon={ClipboardList}
        iconGradient="from-teal-500 to-teal-600"
      />

      <div className="p-4 space-y-4 pb-24">
        {/* Info Banner */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-4 text-white shadow-lg"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Zorgplan Evaluaties</h3>
              <p className="text-white/90 text-sm mt-1">
                Overzicht per PB'er met zorgplan evaluatie datums van hun cliënten.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Legenda */}
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700">Binnen 30 dagen</span>
          <span className="px-2 py-1 rounded-full bg-red-100 text-red-700">Verlopen</span>
          <span className="px-2 py-1 rounded-full bg-green-100 text-green-700">Afgevinkt</span>
        </div>

        {loading ? (
          <LoadingSpinner color="emerald" />
        ) : data.length === 0 ? (
          <EmptyState 
            icon={ClipboardList} 
            title="Geen evaluaties" 
            subtitle="Er zijn nog geen cliënten gevonden" 
          />
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByPBer).map(([pbEr, clienten], groupIndex) => (
              <motion.div
                key={pbEr}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: groupIndex * 0.1 }}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
                data-testid={`pber-group-${groupIndex}`}
              >
                {/* PB'er Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">{pbEr}</h3>
                      <p className="text-white/80 text-sm">{clienten.length} cliënt{clienten.length !== 1 ? 'en' : ''}</p>
                    </div>
                  </div>
                </div>

                {/* Cliënten Lijst */}
                <div className="divide-y divide-slate-100">
                  {clienten.map((item, clientIndex) => (
                    <div 
                      key={item.client}
                      className="p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`client-row-${groupIndex}-${clientIndex}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {/* Cliënt Naam */}
                          <h4 className="font-semibold text-slate-900 text-base">{item.client}</h4>
                          
                          {/* Zorgplan Info */}
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-slate-500 min-w-[120px]">Zorgplan datum:</span>
                              <span className={getStatusColor(item.zorgplan_evaluatie_datum, item.zorgplan_afgevinkt)}>
                                {formatDatum(item.zorgplan_evaluatie_datum)}
                                {item.zorgplan_afgevinkt && <Check className="w-4 h-4 inline ml-1 text-green-500" />}
                              </span>
                            </div>
                            {item.zorgplan_aanwezigen && (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-slate-500 min-w-[120px]">Aanwezigen:</span>
                                <span className="text-slate-600">{item.zorgplan_aanwezigen}</span>
                              </div>
                            )}
                            
                            {/* RI+E Sectie */}
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs font-semibold text-purple-600 mb-1">RI+E (door Erva)</p>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-slate-500 min-w-[120px]">Opgesteld:</span>
                                <span className="text-slate-600">{formatDatum(item.rie_opgesteld_datum)}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-slate-500 min-w-[120px]">Evaluatie:</span>
                                <span className={getStatusColor(item.rie_evaluatie_datum, item.rie_afgevinkt)}>
                                  {formatDatum(item.rie_evaluatie_datum)}
                                  {item.rie_afgevinkt && <Check className="w-4 h-4 inline ml-1 text-green-500" />}
                                </span>
                              </div>
                            </div>
                            
                            {/* SIP Sectie */}
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs font-semibold text-amber-600 mb-1">SIP (door PB'er)</p>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-slate-500 min-w-[120px]">Opgesteld:</span>
                                <span className="text-slate-600">{formatDatum(item.sip_opgesteld_datum)}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-slate-500 min-w-[120px]">Evaluatie:</span>
                                <span className={getStatusColor(item.sip_evaluatie_datum, item.sip_afgevinkt)}>
                                  {formatDatum(item.sip_evaluatie_datum)}
                                  {item.sip_afgevinkt && <Check className="w-4 h-4 inline ml-1 text-green-500" />}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Edit Knop */}
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-600 transition-colors ml-4"
                          data-testid={`edit-evaluatie-${groupIndex}-${clientIndex}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle>Evaluaties Bewerken - {editingItem?.client}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Zorgplan Sectie */}
            <div className="p-4 bg-teal-50 rounded-xl">
              <h4 className="font-semibold text-teal-800 mb-3">Zorgplan (elke 6 maanden)</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Datum opgesteld</label>
                  <Input 
                    type="date" 
                    value={editForm.zorgplan_datum} 
                    onChange={(e) => setEditForm({...editForm, zorgplan_datum: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Evaluatie datum</label>
                  <Input 
                    type="date" 
                    value={editForm.zorgplan_evaluatie_datum} 
                    onChange={(e) => setEditForm({...editForm, zorgplan_evaluatie_datum: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Aanwezigen bij evaluatie</label>
                  <Input 
                    value={editForm.zorgplan_aanwezigen} 
                    onChange={(e) => setEditForm({...editForm, zorgplan_aanwezigen: e.target.value})}
                    placeholder="bijv. Cliënt, wettelijk vertegenwoordiger, PB'er"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* RI+E Sectie */}
            <div className="p-4 bg-purple-50 rounded-xl">
              <h4 className="font-semibold text-purple-800 mb-1">RI+E (elke 6 maanden)</h4>
              <p className="text-xs text-purple-600 mb-3">Opgesteld en geëvalueerd door: Erva (gedragswetenschapper)</p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Datum opgesteld</label>
                  <Input 
                    type="date" 
                    value={editForm.rie_opgesteld_datum} 
                    onChange={(e) => setEditForm({...editForm, rie_opgesteld_datum: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Evaluatie datum</label>
                  <Input 
                    type="date" 
                    value={editForm.rie_evaluatie_datum} 
                    onChange={(e) => setEditForm({...editForm, rie_evaluatie_datum: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* SIP Sectie */}
            <div className="p-4 bg-amber-50 rounded-xl">
              <h4 className="font-semibold text-amber-800 mb-1">SIP (elke 6 maanden)</h4>
              <p className="text-xs text-amber-600 mb-3">Opgesteld en geëvalueerd door: PB'er ({editingItem?.pb_er || 'Niet toegewezen'})</p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Datum opgesteld</label>
                  <Input 
                    type="date" 
                    value={editForm.sip_opgesteld_datum} 
                    onChange={(e) => setEditForm({...editForm, sip_opgesteld_datum: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Evaluatie datum</label>
                  <Input 
                    type="date" 
                    value={editForm.sip_evaluatie_datum} 
                    onChange={(e) => setEditForm({...editForm, sip_evaluatie_datum: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <Button 
              onClick={saveEvaluatie} 
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
            >
              <Save className="w-4 h-4 mr-2" />Opslaan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ================== ROOSTER PAGE ==================
export const RoosterPage = ({ onBack, user, selectedLocation: propSelectedLocation }) => {
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(propSelectedLocation || user?.selected_location || 'groep-zwaluw');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [originalMedewerker, setOriginalMedewerker] = useState(null); // Track originele naam voor URL
  const [addMode, setAddMode] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'zorgmanager';
  const year = 2026;
  const [month, setMonth] = useState(3); // Maart 2026 als default
  
  // FALLBACK ROOSTER DATA - Februari 2026
  const FALLBACK_ROOSTER_ZWALUW = [
    {"datum": 1, "dag": "Zondag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 1, "dag": "Zondag", "medewerker": "Said", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 2, "dag": "Maandag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 2, "dag": "Maandag", "medewerker": "Said", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "11:00", "shift_type": "day"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Vennon", "shift_start": "11:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Niek", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Niek", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 8, "dag": "Zondag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 8, "dag": "Zondag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 9, "dag": "Maandag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 9, "dag": "Maandag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 10, "dag": "Dinsdag", "medewerker": "Imran", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 10, "dag": "Dinsdag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 11, "dag": "Woensdag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 11, "dag": "Woensdag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 15, "dag": "Zondag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 15, "dag": "Zondag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 16, "dag": "Maandag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 16, "dag": "Maandag", "medewerker": "Said", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "11:00", "shift_type": "day"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Vennon", "shift_start": "11:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Niek", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Niek", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 22, "dag": "Zondag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 22, "dag": "Zondag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 23, "dag": "Maandag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 23, "dag": "Maandag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 24, "dag": "Dinsdag", "medewerker": "Imran", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 24, "dag": "Dinsdag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 25, "dag": "Woensdag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 25, "dag": "Woensdag", "medewerker": "Niek", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Karim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "23:00", "shift_type": "avond"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "23:00", "shift_eind": "08:00", "shift_type": "slaap"},
  ];
  
  const FALLBACK_ROOSTER_ADELAARS = [
    {"datum": 1, "dag": "Zondag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 1, "dag": "Zondag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 2, "dag": "Maandag", "medewerker": "Nassim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 2, "dag": "Maandag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 8, "dag": "Zondag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 8, "dag": "Zondag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 9, "dag": "Maandag", "medewerker": "Nassim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 9, "dag": "Maandag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 10, "dag": "Dinsdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 10, "dag": "Dinsdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 11, "dag": "Woensdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 11, "dag": "Woensdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 15, "dag": "Zondag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 15, "dag": "Zondag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 16, "dag": "Maandag", "medewerker": "Nassim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 16, "dag": "Maandag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 22, "dag": "Zondag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 22, "dag": "Zondag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 23, "dag": "Maandag", "medewerker": "Nassim", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 23, "dag": "Maandag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 24, "dag": "Dinsdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 24, "dag": "Dinsdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 25, "dag": "Woensdag", "medewerker": "Serkan", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 25, "dag": "Woensdag", "medewerker": "Imran", "shift_start": "18:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Redouan", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Imran", "shift_start": "19:00", "shift_eind": "08:00", "shift_type": "oproep"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Ilias", "shift_start": "08:00", "shift_eind": "20:00", "shift_type": "day"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Imran", "shift_start": "20:00", "shift_eind": "08:00", "shift_type": "oproep"},
  ];
  
  // FALLBACK ROOSTER DATA - Maart 2026 - ZWALUW
  const FALLBACK_ROOSTER_ZWALUW_MAART = [
    // Week 1: 1 maart (zondag) - 7 maart (zaterdag)
    {"datum": 1, "dag": "Zondag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 1, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 2, "dag": "Maandag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 2, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Mohammed Amine Belhadj", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    // Week 2
    {"datum": 8, "dag": "Zondag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 8, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 9, "dag": "Maandag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 9, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 10, "dag": "Dinsdag", "medewerker": "Mohammed Amine Belhadj", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 10, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 11, "dag": "Woensdag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 11, "dag": "Woensdag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    // Week 3
    {"datum": 15, "dag": "Zondag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 15, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 16, "dag": "Maandag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 16, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Mohammed Amine Belhadj", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    // Week 4
    {"datum": 22, "dag": "Zondag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 22, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 23, "dag": "Maandag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 23, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 24, "dag": "Dinsdag", "medewerker": "Mohammed Amine Belhadj", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 24, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 25, "dag": "Woensdag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 25, "dag": "Woensdag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    // Week 5
    {"datum": 29, "dag": "Zondag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 29, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 30, "dag": "Maandag", "medewerker": "Karim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 30, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 31, "dag": "Dinsdag", "medewerker": "Mohammed Amine Belhadj", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 31, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
  ];
  
  // FALLBACK ROOSTER DATA - Maart 2026 - ADELAARS
  const FALLBACK_ROOSTER_ADELAARS_MAART = [
    // Week 1: 1 maart (zondag) - 7 maart (zaterdag)
    {"datum": 1, "dag": "Zondag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 1, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 2, "dag": "Maandag", "medewerker": "Nassim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 2, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 3, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 4, "dag": "Woensdag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 5, "dag": "Donderdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 6, "dag": "Vrijdag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 7, "dag": "Zaterdag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    // Week 2
    {"datum": 8, "dag": "Zondag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 8, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 9, "dag": "Maandag", "medewerker": "Nassim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 9, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 10, "dag": "Dinsdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 10, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 11, "dag": "Woensdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 11, "dag": "Woensdag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 12, "dag": "Donderdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 13, "dag": "Vrijdag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 14, "dag": "Zaterdag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    // Week 3
    {"datum": 15, "dag": "Zondag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 15, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 16, "dag": "Maandag", "medewerker": "Nassim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 16, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 17, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 18, "dag": "Woensdag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 19, "dag": "Donderdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 20, "dag": "Vrijdag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 21, "dag": "Zaterdag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    // Week 4
    {"datum": 22, "dag": "Zondag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 22, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 23, "dag": "Maandag", "medewerker": "Nassim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 23, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 24, "dag": "Dinsdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 24, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 25, "dag": "Woensdag", "medewerker": "Serkan Inanc", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 25, "dag": "Woensdag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 26, "dag": "Donderdag", "medewerker": "Imran Agourram", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "19:00", "shift_type": "day"},
    {"datum": 27, "dag": "Vrijdag", "medewerker": "Imran Agourram", "shift_start": "19:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 28, "dag": "Zaterdag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    // Week 5
    {"datum": 29, "dag": "Zondag", "medewerker": "Ilias El Boukhallouki", "shift_start": "08:00", "shift_eind": "19:15", "shift_type": "day"},
    {"datum": 29, "dag": "Zondag", "medewerker": "Imran Agourram", "shift_start": "19:15", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 30, "dag": "Maandag", "medewerker": "Nassim Besbis", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 30, "dag": "Maandag", "medewerker": "Niek Versteegen", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
    {"datum": 31, "dag": "Dinsdag", "medewerker": "Redouan Tegarti", "shift_start": "08:00", "shift_eind": "18:00", "shift_type": "day"},
    {"datum": 31, "dag": "Dinsdag", "medewerker": "Mohammed El Ouasghiri", "shift_start": "18:00", "shift_eind": "07:00", "shift_type": "slaap"},
  ];
  
  // Sync met prop wanneer die verandert
  useEffect(() => {
    if (propSelectedLocation) {
      setSelectedLocation(propSelectedLocation);
    }
  }, [propSelectedLocation]);
  
  const fetchRooster = async () => {
    setLoading(true);
    setUseFallback(false);
    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/rooster/${selectedLocation}?maand=${month}&jaar=${year}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        }
      );
      
      clearTimeout(timeoutId);
      
      const apiShifts = response.data.shifts || [];
      setShifts(apiShifts);
      if (apiShifts.length === 0) {
        // DEBUG: console.log('Geen rooster data gevonden voor deze maand');
      }
    } catch (error) {
      console.error('Error fetching rooster:', error);
      setShifts([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchRooster();
  }, [selectedLocation, month]);
  
  // Auto-scroll naar huidige datum na laden
  useEffect(() => {
    if (!loading && shifts.length > 0) {
      const today = new Date();
      const currentDay = today.getDate();
      // Scroll naar huidige dag element
      setTimeout(() => {
        const todayElement = document.querySelector(`[data-testid="rooster-day-${currentDay}"]`);
        if (todayElement) {
          todayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [loading, shifts]);
  
  const handleSaveShift = async (shiftData) => {
    try {
      const token = localStorage.getItem('token');
      if (addMode) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/rooster/${selectedLocation}`,
          shiftData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Dienst toegevoegd');
      } else {
        // Gebruik originele medewerker naam in URL voor correcte update
        const medewerkerForUrl = originalMedewerker || shiftData.medewerker;
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/rooster/${selectedLocation}/${shiftData.datum}/${encodeURIComponent(medewerkerForUrl)}`,
          shiftData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Dienst bijgewerkt');
      }
      setEditModalOpen(false);
      setOriginalMedewerker(null);
      fetchRooster();
    } catch (error) {
      console.error('Error saving shift:', error);
      toast.error('Kon dienst niet opslaan');
    }
  };
  
  const handleDeleteShift = async (datum, medewerker) => {
    if (!window.confirm('Weet je zeker dat je deze dienst wilt verwijderen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/rooster/${selectedLocation}/${datum}/${medewerker}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Dienst verwijderd');
      fetchRooster();
    } catch (error) {
      console.error('Error deleting shift:', error);
      toast.error('Kon dienst niet verwijderen');
    }
  };
  
  const openAddModal = () => {
    setAddMode(true);
    setEditingShift({
      datum: 1,
      dag: 'Maandag',
      medewerker: '',
      shift_start: '08:00',
      shift_eind: '19:00',
      shift_type: 'day'
    });
    setEditModalOpen(true);
  };
  
  const openEditModal = (shift) => {
    setAddMode(false);
    setEditingShift(shift);
    setOriginalMedewerker(shift.medewerker); // Bewaar originele naam voor update URL
    setEditModalOpen(true);
  };
  
  // Group shifts by day
  const shiftsByDay = shifts.reduce((acc, shift) => {
    if (!acc[shift.datum]) acc[shift.datum] = [];
    acc[shift.datum].push(shift);
    return acc;
  }, {});
  
  const daysInMonth = new Date(year, month, 0).getDate(); // Februari heeft 28 dagen in 2026
  const isZwaluw = selectedLocation === 'groep-zwaluw';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/30 to-blue-50/30" data-testid="rooster-page">
      {/* Header */}
      <div className="sticky top-0 z-20 text-white shadow-lg" style={{ background: 'linear-gradient(to right, #0284c7, #0369a1)' }}>
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={onBack} className="p-2 hover:bg-white/20 rounded-xl transition-colors" data-testid="rooster-back-btn">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Werkrooster
            </h1>
            <div className="flex gap-2">
              <button 
                onClick={() => {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/rooster/export/pdf?maand=${month}&jaar=${year}`;
                  const token = localStorage.getItem('token');
                  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                    .then(res => res.blob())
                    .then(blob => {
                      const link = document.createElement('a');
                      link.href = URL.createObjectURL(blob);
                      link.download = `werkrooster_${month === 2 ? 'februari' : 'maart'}_${year}.pdf`;
                      link.click();
                    });
                }}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                data-testid="rooster-download-pdf-btn"
                title="Download PDF"
              >
                <Download className="w-6 h-6" />
              </button>
              <button 
                onClick={openAddModal}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                data-testid="rooster-add-btn"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          {/* Location Tabs */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setSelectedLocation('groep-zwaluw')}
              className={`flex-1 py-2.5 px-3 rounded-xl font-medium transition-all ${
                selectedLocation === 'groep-zwaluw'
                  ? 'bg-white shadow-md'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              style={selectedLocation === 'groep-zwaluw' ? { color: '#0284c7' } : {}}
              data-testid="rooster-tab-zwaluw"
            >
              <div className="flex flex-col items-center">
                <span>🐦 Groep Zwaluw</span>
                <span className={`text-xs ${selectedLocation === 'groep-zwaluw' ? '' : 'text-white/70'}`} style={selectedLocation === 'groep-zwaluw' ? { color: '#0284c7' } : {}}>
                  Enschotsestraat 98/100
                </span>
              </div>
            </button>
            <button
              onClick={() => setSelectedLocation('groep-adelaars')}
              className={`flex-1 py-2.5 px-3 rounded-xl font-medium transition-all ${
                selectedLocation === 'groep-adelaars'
                  ? 'bg-white shadow-md'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              style={selectedLocation === 'groep-adelaars' ? { color: '#0284c7' } : {}}
              data-testid="rooster-tab-adelaars"
            >
              <div className="flex flex-col items-center">
                <span>🦅 Groep Adelaars</span>
                <span className={`text-xs ${selectedLocation === 'groep-adelaars' ? '' : 'text-white/70'}`} style={selectedLocation === 'groep-adelaars' ? { color: '#0284c7' } : {}}>
                  Ardennenlaan 68
                </span>
              </div>
            </button>
          </div>
          
          {/* Month Header - Selector */}
          <div className="mt-2 flex justify-center gap-2">
            <button
              onClick={() => setMonth(2)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                month === 2 
                  ? 'bg-white text-sky-600 shadow-md' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid="rooster-month-feb"
            >
              Februari 2026
            </button>
            <button
              onClick={() => setMonth(3)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                month === 3 
                  ? 'bg-white text-sky-600 shadow-md' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid="rooster-month-mrt"
            >
              Maart 2026
            </button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0284c7' }} />
          </div>
        ) : shifts.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">Geen diensten gepland</p>
            <p className="text-slate-400 text-sm">voor {month === 2 ? 'Februari' : 'Maart'} {year}</p>
            <button
              onClick={openAddModal}
              className={`mt-4 px-6 py-3 bg-gradient-to-r ${isZwaluw ? 'from-green-500 to-emerald-600' : 'from-blue-500 to-indigo-600'} text-white rounded-xl font-medium`}
            >
              <Plus className="w-5 h-5 inline mr-2" />
              Eerste dienst toevoegen
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const dayShifts = shiftsByDay[day] || [];
              if (dayShifts.length === 0) return null;
              
              const date = new Date(year, month - 1, day); // month-1 omdat JS maanden 0-indexed zijn
              const dayName = date.toLocaleDateString('nl-NL', { weekday: 'long' });
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const today = new Date();
              const isToday = today.getDate() === day && today.getMonth() === (month - 1) && today.getFullYear() === year;
              
              return (
                <motion.div
                  key={day}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${
                    isToday ? 'border-sky-500 ring-2 ring-sky-200' : 'border-slate-200'
                  }`}
                  data-testid={`rooster-day-${day}`}
                >
                  {/* Day Header */}
                  <div className={`px-4 py-3 ${
                    isToday 
                      ? 'bg-gradient-to-r from-sky-500 to-sky-600 text-white'
                      : isWeekend 
                        ? 'bg-slate-100' 
                        : 'bg-slate-50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
                          isToday 
                            ? 'bg-white/20 text-white' 
                            : 'bg-sky-100 text-sky-700'
                        }`}>
                          {day}
                        </div>
                        <div>
                          <p className={`font-semibold capitalize ${isToday ? 'text-white' : 'text-slate-800'}`}>
                            {dayName}
                          </p>
                          {isToday && (
                            <span className="text-xs bg-white/30 px-2 py-0.5 rounded-full">Vandaag</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Shifts - gesorteerd op starttijd (vroegste eerst) */}
                  <div className="divide-y divide-slate-100">
                    {dayShifts
                      .sort((a, b) => {
                        // Sorteer op starttijd (vroegste eerst)
                        const timeA = a.shift_start || '00:00';
                        const timeB = b.shift_start || '00:00';
                        return timeA.localeCompare(timeB);
                      })
                      .map((shift, idx) => (
                      <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            shift.shift_type === 'slaapdienst' || shift.shift_type === 'slaap' || shift.shift_type === 'achterwacht'
                              ? 'bg-indigo-100 text-indigo-600'
                              : shift.shift_type === 'avond_slaap'
                                ? 'bg-violet-100 text-violet-600'
                                : shift.shift_type === 'oproep'
                                  ? 'bg-amber-100 text-amber-600'
                                  : shift.shift_type === 'avond'
                                    ? 'bg-purple-100 text-purple-600'
                                    : 'bg-sky-100 text-sky-600'
                          }`}>
                            {shift.shift_type === 'slaapdienst' || shift.shift_type === 'slaap' || shift.shift_type === 'achterwacht' ? (
                              <Moon className="w-5 h-5" />
                            ) : shift.shift_type === 'avond_slaap' ? (
                              <Moon className="w-5 h-5" />
                            ) : shift.shift_type === 'oproep' ? (
                              <Phone className="w-5 h-5" />
                            ) : shift.shift_type === 'avond' ? (
                              <Moon className="w-5 h-5" />
                            ) : (
                              <Sun className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{shift.medewerker}</p>
                            <p className="text-sm text-slate-500">
                              {shift.shift_start} - {shift.shift_eind}
                              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-100">
                                {shift.shift_type === 'slaapdienst' || shift.shift_type === 'slaap' ? 'Slaapdienst' 
                                  : shift.shift_type === 'achterwacht' ? 'Achterwacht'
                                  : shift.shift_type === 'avond_slaap' ? 'Avond / Slaapdienst'
                                  : shift.shift_type === 'oproep' ? 'Oproep'
                                  : shift.shift_type === 'avond' ? 'Avonddienst'
                                  : 'Dagdienst'}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEditModal(shift)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            data-testid={`edit-shift-${day}-${idx}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteShift(shift.datum, shift.medewerker)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`delete-shift-${day}-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        
        {/* Legend */}
        <div className="mt-6 p-4 bg-white rounded-2xl shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-800 mb-3">Legenda</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-sky-100">
                <Sun className="w-4 h-4 text-sky-600" />
              </div>
              <span className="text-slate-600">Dagdienst</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-violet-100">
                <Moon className="w-4 h-4 text-violet-600" />
              </div>
              <span className="text-slate-600">Avond / Slaapdienst</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-indigo-100">
                <Moon className="w-4 h-4 text-indigo-600" />
              </div>
              <span className="text-slate-600">Achterwacht</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-amber-100">
                <Phone className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-slate-600">Oproep</span>
            </div>
          </div>
        </div>
        
        {isAdmin && (
          <div className="mt-4 p-4 bg-amber-50 rounded-2xl border border-amber-200">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">Beheerdersmodus</p>
                <p className="text-sm text-amber-700">Je kunt diensten toevoegen, bewerken en verwijderen.</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {addMode ? <Plus className="w-5 h-5" /> : <Edit className="w-5 h-5" />}
              {addMode ? 'Dienst toevoegen' : 'Dienst bewerken'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Dag van de maand</label>
              <Input
                type="number"
                min={1}
                max={daysInMonth}
                value={editingShift?.datum || 1}
                onChange={(e) => setEditingShift({...editingShift, datum: parseInt(e.target.value)})}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Medewerker</label>
              <Input
                value={editingShift?.medewerker || ''}
                onChange={(e) => setEditingShift({...editingShift, medewerker: e.target.value})}
                placeholder="Naam medewerker"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Start</label>
                <Input
                  type="time"
                  value={editingShift?.shift_start || '08:00'}
                  onChange={(e) => setEditingShift({...editingShift, shift_start: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Eind</label>
                <Input
                  type="time"
                  value={editingShift?.shift_eind || '19:00'}
                  onChange={(e) => setEditingShift({...editingShift, shift_eind: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Type dienst</label>
              <select
                value={editingShift?.shift_type || 'day'}
                onChange={(e) => setEditingShift({...editingShift, shift_type: e.target.value})}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="day">Dagdienst</option>
                <option value="avond_slaap">Avond / Slaapdienst</option>
                <option value="avond">Avonddienst</option>
                <option value="slaap">Slaapdienst</option>
                <option value="achterwacht">Achterwacht</option>
                <option value="oproep">Oproep</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setEditModalOpen(false)}
                className="flex-1"
              >
                Annuleren
              </Button>
              <Button
                onClick={() => handleSaveShift(editingShift)}
                className={`flex-1 bg-gradient-to-r ${isZwaluw ? 'from-green-500 to-emerald-600' : 'from-blue-500 to-indigo-600'} text-white`}
              >
                {addMode ? 'Toevoegen' : 'Opslaan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ================== CLIENT AFSPRAKEN PAGE ==================
export const AfsprakenPage = ({ onBack, user, selectedLocation }) => {
  const [loading, setLoading] = useState(true);
  const [afspraken, setAfspraken] = useState([]);
  const [algemeneAfspraken, setAlgemeneAfspraken] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAfspraak, setEditingAfspraak] = useState(null);
  const [clienten, setClienten] = useState([]);
  const [useFallback, setUseFallback] = useState(false);
  const [activeTab, setActiveTab] = useState('client'); // 'client' of 'algemeen'
  const [locationFilter, setLocationFilter] = useState('all'); // 'all', 'groep-zwaluw', 'groep-adelaars'
  const [formData, setFormData] = useState({
    client_naam: '',
    datum: '',
    tijd: '',
    type_afspraak: '',
    locatie: '',
    begeleider_meegaat: '',
    notities: '',
    is_algemeen: false,
    titel: ''
  });
  
  // Check of dit een gecombineerd account is
  const isCombinedAccount = user?.locations && user.locations.length > 1;
  
  // FALLBACK DATA voor afspraken - LEEG (gebruik echte data)
  const FALLBACK_AFSPRAKEN = {
    'groep-zwaluw': [],
    'groep-adelaars': []
  };
  
  const FALLBACK_CLIENTEN = {
    'groep-zwaluw': ['Y.G.', 'Y.H.', 'S.B.', 'A.T.'],
    'groep-adelaars': ['M.R.', 'R.B.', 'F.I.', 'M.H.'],
    'combined': ['Y.G.', 'Y.H.', 'S.B.', 'A.T.', 'M.R.', 'R.B.', 'F.I.', 'M.H.']
  };
  
  const afspraakTypes = [
    'Huisarts', 'Tandarts', 'Ziekenhuis', 'Specialist', 'Bewindvoerder', 
    'Psycholoog', 'Fysiotherapie', 'Apotheek', 'Gemeente', 'Anders'
  ];
  
  const algemeneAfspraakTypes = [
    'CV Ketel', 'Onderhoud', 'Inspectie', 'Leverancier', 'Vergadering',
    'Schoonmaak', 'Reparatie', 'Bezorging', 'Afval ophalen', 'Anders'
  ];
  
  const begeleiders = ['Karim B.', 'Ismail I.', 'Ilias E.', 'Dilek I.'];
  
  // Filter afspraken op basis van locatie filter
  const getFilteredAfspraken = (afsprakenList) => {
    if (!isCombinedAccount || locationFilter === 'all') {
      return afsprakenList;
    }
    return afsprakenList.filter(a => a.locatie_bron === locationFilter);
  };
  
  // Groepeer afspraken per locatie
  const getGroupedAfspraken = (afsprakenList) => {
    if (!isCombinedAccount) {
      return { [selectedLocation || 'groep-zwaluw']: afsprakenList };
    }
    
    const filtered = getFilteredAfspraken(afsprakenList);
    const grouped = {};
    
    filtered.forEach(a => {
      const loc = a.locatie_bron || 'groep-zwaluw';
      if (!grouped[loc]) grouped[loc] = [];
      grouped[loc].push(a);
    });
    
    return grouped;
  };
  
  const getLocationLabel = (loc) => {
    if (loc === 'groep-zwaluw') return 'Groep Zwaluw';
    if (loc === 'groep-adelaars') return 'Groep Adelaars';
    return loc;
  };
  
  const getLocationColor = (loc) => {
    if (loc === 'groep-zwaluw') return 'blue';
    if (loc === 'groep-adelaars') return 'orange';
    return 'slate';
  };
  
  const fetchAfspraken = async () => {
    setLoading(true);
    setUseFallback(false);
    const locationSlug = selectedLocation || 'groep-zwaluw';
    
    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      // Voor gecombineerde accounts: haal afspraken van beide locaties op
      let allAfspraken = [];
      const seenIds = new Set(); // Track afspraken die we al hebben
      const locationsToFetch = isCombinedAccount ? user.locations : [selectedLocation || 'groep-zwaluw'];
      
      for (const loc of locationsToFetch) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/afspraken?location=${loc}`,
            { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
          );
          const locAfspraken = response.data.afspraken || [];
          
          // Voeg alleen toe als we deze afspraak nog niet hebben (voorkom dubbelen)
          locAfspraken.forEach(a => {
            if (!seenIds.has(a.id)) {
              seenIds.add(a.id);
              // Gebruik de locatie_groep uit de database als bron, niet de fetch locatie
              allAfspraken.push({
                ...a,
                locatie_bron: a.locatie_groep || loc
              });
            }
          });
        } catch (locError) {
          // DEBUG: console.log(`Kon afspraken niet ophalen voor ${loc}:`, locError);
        }
      }
      
      clearTimeout(timeoutId);
      
      // Splits in client en algemene afspraken
      const clientAfspraken = allAfspraken.filter(a => !a.is_algemeen);
      const algAfspraken = allAfspraken.filter(a => a.is_algemeen);
      
      setAfspraken(clientAfspraken);
      setAlgemeneAfspraken(algAfspraken);
      
      if (clientAfspraken.length === 0 && algAfspraken.length === 0) {
        setUseFallback(true);
      }
      
      // Haal clienten op - voor gecombineerde accounts van beide locaties
      try {
        let alleClienten = [];
        for (const loc of locationsToFetch) {
          const overzichtResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/begeleider/overzicht?location=${loc}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const locClienten = overzichtResponse.data.alle_clienten || [];
          alleClienten = [...alleClienten, ...locClienten];
        }
        
        // Verwijder dubbelen op basis van voornaam
        const seen = new Set();
        const uniqueClienten = alleClienten.filter(c => {
          const naam = c.voornaam || c.volledige_naam?.split(' ')[0] || 'Onbekend';
          if (seen.has(naam)) return false;
          seen.add(naam);
          return true;
        });
        
        if (uniqueClienten.length > 0) {
          // Gebruik altijd de gespecificeerde cliënt initialen
          setClienten(isCombinedAccount ? FALLBACK_CLIENTEN['combined'] : (FALLBACK_CLIENTEN[locationSlug] || FALLBACK_CLIENTEN['groep-zwaluw']));
        } else {
          setClienten(isCombinedAccount ? FALLBACK_CLIENTEN['combined'] : (FALLBACK_CLIENTEN[locationSlug] || FALLBACK_CLIENTEN['groep-zwaluw']));
        }
      } catch {
        setClienten(isCombinedAccount ? FALLBACK_CLIENTEN['combined'] : (FALLBACK_CLIENTEN[locationSlug] || FALLBACK_CLIENTEN['groep-zwaluw']));
      }
    } catch (error) {
      console.error('Error fetching afspraken:', error);
      // Use fallback
      setAfspraken(FALLBACK_AFSPRAKEN[locationSlug] || FALLBACK_AFSPRAKEN['groep-zwaluw']);
      setAlgemeneAfspraken([]);
      setClienten(FALLBACK_CLIENTEN[locationSlug] || FALLBACK_CLIENTEN['groep-zwaluw']);
      setUseFallback(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Ref voor vandaag sectie of de eerstvolgende afspraak
  const todayRef = useRef(null);
  const firstUpcomingRef = useRef(null);
  
  useEffect(() => {
    fetchAfspraken();
  }, [selectedLocation]);
  
  // Auto-scroll naar vandaag of de eerstvolgende afspraak wanneer data is geladen
  useEffect(() => {
    if (!loading) {
      // Kleine delay zodat de DOM volledig gerenderd is
      setTimeout(() => {
        if (todayRef.current) {
          todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (firstUpcomingRef.current) {
          // Als geen afspraak vandaag, scroll naar de eerste toekomstige afspraak
          firstUpcomingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [loading, activeTab]);
  
  const handleSubmit = async () => {
    const isAlgemeen = activeTab === 'algemeen';
    
    if (isAlgemeen) {
      if (!formData.titel || !formData.datum || !formData.tijd || !formData.type_afspraak) {
        toast.error('Vul alle verplichte velden in');
        return;
      }
    } else {
      if (!formData.client_naam || !formData.datum || !formData.tijd || !formData.type_afspraak) {
        toast.error('Vul alle verplichte velden in');
        return;
      }
    }
    
    const submitData = {
      ...formData,
      is_algemeen: isAlgemeen,
      client_naam: isAlgemeen ? formData.titel : formData.client_naam,
      locatie_groep: selectedLocation || user?.selected_location || 'groep-zwaluw'
    };
    
    try {
      const token = localStorage.getItem('token');
      if (editingAfspraak) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/afspraken/${editingAfspraak.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Afspraak bijgewerkt');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/afspraken`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Afspraak toegevoegd');
      }
      setShowAddModal(false);
      setEditingAfspraak(null);
      setFormData({ client_naam: '', datum: '', tijd: '', type_afspraak: '', locatie: '', begeleider_meegaat: '', notities: '', is_algemeen: false, titel: '' });
      fetchAfspraken();
    } catch (error) {
      console.error('Error saving afspraak:', error);
      toast.error('Kon afspraak niet opslaan');
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze afspraak wilt verwijderen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/afspraken/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Afspraak verwijderd');
      fetchAfspraken();
    } catch (error) {
      toast.error('Kon afspraak niet verwijderen');
    }
  };
  
  const openEditModal = (afspraak) => {
    const isAlgemeen = afspraak.is_algemeen;
    setActiveTab(isAlgemeen ? 'algemeen' : 'client');
    setEditingAfspraak(afspraak);
    setFormData({
      client_naam: afspraak.client_naam,
      datum: afspraak.datum,
      tijd: afspraak.tijd,
      type_afspraak: afspraak.type_afspraak,
      locatie: afspraak.locatie || '',
      begeleider_meegaat: afspraak.begeleider_meegaat || '',
      notities: afspraak.notities || '',
      is_algemeen: isAlgemeen,
      titel: isAlgemeen ? afspraak.client_naam : ''
    });
    setShowAddModal(true);
  };
  
  const openAddModal = (isAlgemeen = false) => {
    setActiveTab(isAlgemeen ? 'algemeen' : 'client');
    setEditingAfspraak(null);
    setFormData({ client_naam: '', datum: '', tijd: '', type_afspraak: '', locatie: '', begeleider_meegaat: '', notities: '', is_algemeen: isAlgemeen, titel: '' });
    setShowAddModal(true);
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
  };
  
  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };
  
  const isTomorrow = (dateStr) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return dateStr === tomorrow.toISOString().split('T')[0];
  };
  
  // Get current list based on active tab - apply location filter
  const currentAfspraken = getFilteredAfspraken(activeTab === 'client' ? afspraken : algemeneAfspraken);
  
  // Group by date
  const groupedAfspraken = currentAfspraken.reduce((acc, afspraak) => {
    if (!acc[afspraak.datum]) acc[afspraak.datum] = [];
    acc[afspraak.datum].push(afspraak);
    return acc;
  }, {});
  
  const sortedDates = Object.keys(groupedAfspraken).sort();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-rose-50/30 to-pink-50/30" data-testid="afspraken-page">
      {/* Header */}
      <div className="sticky top-0 z-20 text-white shadow-lg" style={{ background: activeTab === 'client' ? 'linear-gradient(to right, #f43f5e, #e11d48)' : 'linear-gradient(to right, #8b5cf6, #7c3aed)' }}>
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={onBack} className="p-2 hover:bg-white/20 rounded-xl transition-colors" data-testid="afspraken-back-btn">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Afspraken
            </h1>
            <button 
              onClick={() => openAddModal(activeTab === 'algemeen')}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
              data-testid="afspraken-add-btn"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
          
          {/* Locatie Filter - alleen voor gecombineerde accounts */}
          {isCombinedAccount && (
            <div className="mt-3 flex gap-2 justify-center">
              <button
                onClick={() => setLocationFilter('all')}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  locationFilter === 'all'
                    ? 'bg-white text-slate-800 shadow-md'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                Alle locaties
              </button>
              <button
                onClick={() => setLocationFilter('groep-zwaluw')}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  locationFilter === 'groep-zwaluw'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                🔵 Zwaluw
              </button>
              <button
                onClick={() => setLocationFilter('groep-adelaars')}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  locationFilter === 'groep-adelaars'
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                🟠 Adelaars
              </button>
            </div>
          )}
          
          {/* Tabs */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setActiveTab('client')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'client' 
                  ? 'bg-white text-rose-600 shadow-md' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid="tab-client-afspraken"
            >
              👤 Cliënt ({getFilteredAfspraken(afspraken).length})
            </button>
            <button
              onClick={() => setActiveTab('algemeen')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'algemeen' 
                  ? 'bg-white text-purple-600 shadow-md' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid="tab-algemeen-afspraken"
            >
              🏠 Algemeen ({getFilteredAfspraken(algemeneAfspraken).length})
            </button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: activeTab === 'client' ? '#f43f5e' : '#8b5cf6' }} />
          </div>
        ) : currentAfspraken.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">
              {activeTab === 'client' ? 'Geen cliënt afspraken gepland' : 'Geen algemene afspraken gepland'}
            </p>
            <button
              onClick={() => openAddModal(activeTab === 'algemeen')}
              className="mt-4 px-6 py-3 text-white rounded-xl font-medium"
              style={{ background: activeTab === 'client' ? 'linear-gradient(to right, #f43f5e, #e11d48)' : 'linear-gradient(to right, #8b5cf6, #7c3aed)' }}
            >
              <Plus className="w-5 h-5 inline mr-2" />
              {activeTab === 'client' ? 'Cliënt afspraak toevoegen' : 'Algemene afspraak toevoegen'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedDates.map((date, dateIndex) => {
              const today = new Date().toISOString().split('T')[0];
              const isFirstUpcoming = !isToday(date) && date >= today && dateIndex === sortedDates.findIndex(d => d >= today);
              
              return (
              <div key={date} ref={isToday(date) ? todayRef : (isFirstUpcoming ? firstUpcomingRef : null)}>
                {/* Date Header */}
                <div className={`mb-2 flex items-center gap-2 ${isToday(date) ? 'text-rose-700' : isTomorrow(date) ? 'text-rose-600' : 'text-slate-600'}`}>
                  <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    isToday(date) ? 'bg-rose-100 text-rose-700' : 
                    isTomorrow(date) ? 'bg-rose-50 text-rose-600' : 
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {isToday(date) ? '📅 Vandaag' : isTomorrow(date) ? '📆 Morgen' : formatDate(date)}
                  </div>
                </div>
                
                {/* Appointments for this date */}
                <div className="space-y-2">
                  {groupedAfspraken[date].sort((a, b) => a.tijd.localeCompare(b.tijd)).map((afspraak) => (
                    <motion.div
                      key={afspraak.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${
                        isToday(date) ? 'border-rose-300 ring-1 ring-rose-200' : 
                        isTomorrow(date) ? 'border-rose-200 ring-1 ring-rose-100' : 
                        'border-slate-200'
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2.5 rounded-xl ${
                              afspraak.is_algemeen ? 'bg-purple-100 text-purple-600' :
                              isToday(date) ? 'bg-purple-100 text-purple-600' : 
                              isTomorrow(date) ? 'bg-pink-100 text-pink-600' : 
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {afspraak.is_algemeen ? <Home className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-slate-800">{afspraak.client_naam}</p>
                                {afspraak.is_algemeen && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                    Algemeen
                                  </span>
                                )}
                                {/* Locatie badge voor gecombineerde accounts */}
                                {isCombinedAccount && afspraak.locatie_bron && (
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    afspraak.locatie_bron === 'groep-zwaluw' 
                                      ? 'bg-blue-100 text-blue-700' 
                                      : 'bg-orange-100 text-orange-700'
                                  }`}>
                                    {afspraak.locatie_bron === 'groep-zwaluw' ? '🔵 Zwaluw' : '🟠 Adelaars'}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 mt-0.5">
                                <span className="font-semibold">{afspraak.tijd}</span> • {afspraak.type_afspraak}
                              </p>
                              {afspraak.locatie && (
                                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {afspraak.locatie}
                                </p>
                              )}
                              {!afspraak.is_algemeen && (
                                <div className="mt-2">
                                  {afspraak.zelfstandig || !afspraak.begeleider_meegaat ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                      <Check className="w-3 h-3" />
                                      Gaat zelfstandig
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                      <User className="w-3 h-3" />
                                      {afspraak.begeleider_meegaat} gaat mee
                                    </span>
                                  )}
                                </div>
                              )}
                              {afspraak.begeleider_meegaat && afspraak.is_algemeen && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                    <User className="w-3 h-3" />
                                    Verantwoordelijke: {afspraak.begeleider_meegaat}
                                  </span>
                                </div>
                              )}
                              {afspraak.notities && (
                                <p className="text-sm text-slate-500 mt-2 italic">"{afspraak.notities}"</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditModal(afspraak)}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              data-testid={`edit-afspraak-${afspraak.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(afspraak.id)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              data-testid={`delete-afspraak-${afspraak.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingAfspraak ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingAfspraak ? 'Afspraak bewerken' : activeTab === 'algemeen' ? 'Nieuwe algemene afspraak' : 'Nieuwe cliënt afspraak'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Modal Tab Switcher */}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setActiveTab('client')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'client' 
                  ? 'bg-rose-100 text-rose-700 ring-1 ring-rose-200' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              👤 Cliënt
            </button>
            <button
              onClick={() => setActiveTab('algemeen')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'algemeen' 
                  ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-200' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              🏠 Algemeen
            </button>
          </div>
          
          <div className="space-y-4 mt-4">
            {activeTab === 'client' ? (
              <div>
                <label className="text-sm font-medium text-slate-700">Cliënt *</label>
                <select
                  value={formData.client_naam}
                  onChange={(e) => setFormData({...formData, client_naam: e.target.value})}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Selecteer cliënt</option>
                  {clienten.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium text-slate-700">Titel/Omschrijving *</label>
                <Input
                  value={formData.titel}
                  onChange={(e) => setFormData({...formData, titel: e.target.value})}
                  placeholder="bijv. CV ketel onderhoud"
                  className="mt-1"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Datum *</label>
                <Input
                  type="date"
                  value={formData.datum}
                  onChange={(e) => setFormData({...formData, datum: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tijd *</label>
                <Input
                  type="time"
                  value={formData.tijd}
                  onChange={(e) => setFormData({...formData, tijd: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Type afspraak *</label>
              <select
                value={formData.type_afspraak}
                onChange={(e) => setFormData({...formData, type_afspraak: e.target.value})}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Selecteer type</option>
                {(activeTab === 'algemeen' ? algemeneAfspraakTypes : afspraakTypes).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Locatie/Adres</label>
              <Input
                value={formData.locatie}
                onChange={(e) => setFormData({...formData, locatie: e.target.value})}
                placeholder={activeTab === 'algemeen' ? 'bijv. Woonkamer, Keuken' : 'bijv. Ziekenhuis Tilburg'}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">
                {activeTab === 'algemeen' ? 'Verantwoordelijke begeleider' : 'Begeleider gaat mee'}
              </label>
              <select
                value={formData.begeleider_meegaat}
                onChange={(e) => setFormData({...formData, begeleider_meegaat: e.target.value})}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">{activeTab === 'algemeen' ? 'Selecteer begeleider' : 'Gaat zelfstandig'}</option>
                {begeleiders.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Notities</label>
              <textarea
                value={formData.notities}
                onChange={(e) => setFormData({...formData, notities: e.target.value})}
                placeholder="Extra informatie..."
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[80px]"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                Annuleren
              </Button>
              <Button 
                onClick={handleSubmit} 
                className={`flex-1 text-white ${activeTab === 'algemeen' ? 'bg-gradient-to-r from-purple-500 to-violet-600' : 'bg-gradient-to-r from-purple-500 to-pink-600'}`}
              >
                {editingAfspraak ? 'Opslaan' : 'Toevoegen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ================== KLUSJES PAGE ==================
export const KlusjesPage = ({ onBack, user }) => {
  const [loading, setLoading] = useState(true);
  const [klusjes, setKlusjes] = useState([]);
  const [klusjesman, setKlusjesman] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingKlusje, setEditingKlusje] = useState(null);
  const [filter, setFilter] = useState('alle'); // alle, open, in_behandeling, afgerond
  const [useFallback, setUseFallback] = useState(false);
  const [formData, setFormData] = useState({
    titel: '',
    beschrijving: '',
    locatie: 'groep-zwaluw',
    prioriteit: 'normaal'
  });
  
  // FALLBACK DATA voor klusjes
  const FALLBACK_KLUSJES = [
    { id: '1', titel: 'Lamp vervangen', beschrijving: 'Lamp in de gang is kapot', locatie: 'groep-zwaluw', prioriteit: 'normaal', status: 'open', aangemaakt_door: 'Karim', aangemaakt_op: '2026-02-01' },
    { id: '2', titel: 'Lekkende kraan', beschrijving: 'Kraan in badkamer lekt', locatie: 'groep-zwaluw', prioriteit: 'hoog', status: 'in_behandeling', aangemaakt_door: 'Redouan', aangemaakt_op: '2026-02-03' },
    { id: '3', titel: 'Deur piept', beschrijving: 'Voordeur piept bij openen', locatie: 'groep-adelaars', prioriteit: 'laag', status: 'afgerond', aangemaakt_door: 'Ilias', aangemaakt_op: '2026-01-28' },
    { id: '4', titel: 'Verwarming controleren', beschrijving: 'Verwarming in kamer 3 wordt niet warm', locatie: 'groep-zwaluw', prioriteit: 'hoog', status: 'open', aangemaakt_door: 'Niek', aangemaakt_op: '2026-02-05' },
  ];
  
  const FALLBACK_KLUSJESMAN = {
    naam: 'Piet de Klusjesman',
    telefoon: '06-12345678',
    email: 'piet@klusjes.nl'
  };
  
  const fetchKlusjes = async () => {
    setLoading(true);
    setUseFallback(false);
    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/klusjes`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );
      
      clearTimeout(timeoutId);
      
      const apiKlusjes = response.data.klusjes || [];
      if (apiKlusjes.length > 0) {
        setKlusjes(apiKlusjes);
        setKlusjesman(response.data.klusjesman || FALLBACK_KLUSJESMAN);
      } else {
        // DEBUG: console.log('API returned empty klusjes, using fallback');
        setKlusjes(FALLBACK_KLUSJES);
        setKlusjesman(FALLBACK_KLUSJESMAN);
        setUseFallback(true);
      }
    } catch (error) {
      console.error('Error fetching klusjes:', error);
      // Use fallback
      setKlusjes(FALLBACK_KLUSJES);
      setKlusjesman(FALLBACK_KLUSJESMAN);
      setUseFallback(true);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchKlusjes();
  }, []);
  
  const handleSubmit = async () => {
    if (!formData.titel || !formData.beschrijving) {
      toast.error('Vul titel en beschrijving in');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (editingKlusje) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/klusjes/${editingKlusje.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Klusje bijgewerkt');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/klusjes`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Klusje aangemaakt');
      }
      setShowAddModal(false);
      setEditingKlusje(null);
      setFormData({ titel: '', beschrijving: '', locatie: 'groep-zwaluw', prioriteit: 'normaal' });
      fetchKlusjes();
    } catch (error) {
      console.error('Error saving klusje:', error);
      toast.error('Kon klusje niet opslaan');
    }
  };
  
  const handleStatusChange = async (klusje, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/klusjes/${klusje.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status bijgewerkt');
      fetchKlusjes();
    } catch (error) {
      toast.error('Kon status niet bijwerken');
    }
  };
  
  const handleDelete = async (id) => {
    if (!id) {
      toast.error('Kan klusje niet verwijderen: geen ID gevonden');
      return;
    }
    if (!window.confirm('Weet je zeker dat je dit klusje wilt verwijderen?')) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Je bent niet ingelogd');
        return;
      }
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/klusjes/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Klusje verwijderd');
      fetchKlusjes();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
      toast.error(`Fout bij verwijderen: ${errorMsg}`);
    }
  };
  
  const openEditModal = (klusje) => {
    setEditingKlusje(klusje);
    setFormData({
      titel: klusje.titel,
      beschrijving: klusje.beschrijving,
      locatie: klusje.locatie,
      prioriteit: klusje.prioriteit
    });
    setShowAddModal(true);
  };
  
  const filteredKlusjes = klusjes.filter(k => filter === 'alle' || k.status === filter);
  
  const getPriorityColor = (prioriteit) => {
    switch (prioriteit) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200';
      case 'hoog': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'normaal': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'laag': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-amber-100 text-amber-700';
      case 'in_behandeling': return 'bg-blue-100 text-blue-700';
      case 'afgerond': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/30 to-orange-50/30" data-testid="klusjes-page">
      {/* Header */}
      <div className="sticky top-0 z-20 text-white shadow-lg" style={{ background: 'linear-gradient(to right, #d97706, #b45309)' }}>
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={onBack} className="p-2 hover:bg-white/20 rounded-xl transition-colors" data-testid="klusjes-back-btn">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              🔧 Klusjes
            </h1>
            <button 
              onClick={() => { setEditingKlusje(null); setFormData({ titel: '', beschrijving: '', locatie: 'groep-zwaluw', prioriteit: 'normaal' }); setShowAddModal(true); }}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
              data-testid="klusjes-add-btn"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Klusjesman Info */}
      {klusjesman && (
        <div className="mx-4 mt-4 p-4 bg-white rounded-2xl shadow-sm border-2 border-amber-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl" style={{ backgroundColor: '#fef3c7' }}>
                <User className="w-5 h-5" style={{ color: '#d97706' }} />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Klusjesman: {klusjesman.naam}</p>
                <p className="text-sm text-slate-500">Neem contact op voor reparaties</p>
              </div>
            </div>
            <a 
              href={`tel:${klusjesman.telefoon}`}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium"
            >
              <Phone className="w-4 h-4" />
              {klusjesman.telefoon}
            </a>
          </div>
        </div>
      )}
      
      {/* Filter Tabs */}
      <div className="px-4 mt-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { key: 'alle', label: 'Alle', count: klusjes.length },
            { key: 'open', label: 'Open', count: klusjes.filter(k => k.status === 'open').length },
            { key: 'in_behandeling', label: 'In behandeling', count: klusjes.filter(k => k.status === 'in_behandeling').length },
            { key: 'afgerond', label: 'Afgerond', count: klusjes.filter(k => k.status === 'afgerond').length },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                filter === f.key 
                  ? 'text-white shadow-md' 
                  : 'bg-white text-slate-600 hover:bg-amber-50'
              }`}
              style={filter === f.key ? { backgroundColor: '#d97706' } : {}}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 pb-24">
        {/* Fallback Warning Banner */}
        {useFallback && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="klusjes-fallback-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Demo modus actief</p>
                <p className="text-sm text-amber-700 mt-1">
                  Er zijn nog geen klusjes in de database. U ziet nu voorbeelddata. 
                  Voeg een nieuw klusje toe via de + knop om echte klusjes aan te maken die u kunt bewerken en verwijderen.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#d97706' }} />
          </div>
        ) : filteredKlusjes.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔧</div>
            <p className="text-slate-500 text-lg">Geen klusjes gevonden</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 px-6 py-3 text-white rounded-xl font-medium"
              style={{ background: 'linear-gradient(to right, #d97706, #b45309)' }}
            >
              <Plus className="w-5 h-5 inline mr-2" />
              Nieuw klusje melden
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredKlusjes.map((klusje) => (
              <motion.div
                key={klusje.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(klusje.prioriteit)}`}>
                          {klusje.prioriteit === 'urgent' ? '🚨' : klusje.prioriteit === 'hoog' ? '⚠️' : ''} {klusje.prioriteit}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(klusje.status)}`}>
                          {klusje.status === 'open' ? 'Open' : klusje.status === 'in_behandeling' ? 'In behandeling' : 'Afgerond'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          {klusje.locatie === 'groep-zwaluw' ? '🐦 Zwaluw' : '🦅 Adelaars'}
                        </span>
                      </div>
                      <p className="font-bold text-slate-800">{klusje.titel}</p>
                      <p className="text-sm text-slate-600 mt-1">{klusje.beschrijving}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        Gemeld door {klusje.aangemaakt_door || 'Onbekend'} • {klusje.aangemaakt_op ? new Date(klusje.aangemaakt_op).toLocaleDateString('nl-NL') : 'Onbekende datum'}
                      </p>
                    </div>
                    {/* Edit/Delete buttons - only show for real data, not fallback */}
                    {!useFallback && (
                      <div className="flex flex-col gap-2 ml-3">
                        <button
                          onClick={() => openEditModal(klusje)}
                          className="p-2.5 rounded-xl bg-amber-100 hover:bg-amber-200 transition-colors"
                          title="Bewerken"
                          data-testid={`klusje-edit-${klusje.id}`}
                        >
                          <Edit className="w-5 h-5 text-amber-700" />
                        </button>
                        <button
                          onClick={() => handleDelete(klusje.id)}
                          className="p-2.5 rounded-xl bg-red-100 hover:bg-red-200 transition-colors"
                          title="Verwijderen"
                          data-testid={`klusje-delete-${klusje.id}`}
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Status Change Buttons - only show for real data */}
                  {!useFallback && klusje.status !== 'afgerond' && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                      {klusje.status === 'open' && (
                        <button
                          onClick={() => handleStatusChange(klusje, 'in_behandeling')}
                          className="flex-1 py-2 px-3 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                        >
                          In behandeling zetten
                        </button>
                      )}
                      <button
                        onClick={() => handleStatusChange(klusje, 'afgerond')}
                        className="flex-1 py-2 px-3 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
                      >
                        ✓ Markeer als afgerond
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingKlusje ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingKlusje ? 'Klusje bewerken' : 'Nieuw klusje melden'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Titel *</label>
              <Input
                value={formData.titel}
                onChange={(e) => setFormData({...formData, titel: e.target.value})}
                placeholder="bijv. Kapotte deurklink"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Beschrijving *</label>
              <textarea
                value={formData.beschrijving}
                onChange={(e) => setFormData({...formData, beschrijving: e.target.value})}
                placeholder="Beschrijf het probleem..."
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 min-h-[100px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Locatie</label>
                <select
                  value={formData.locatie}
                  onChange={(e) => setFormData({...formData, locatie: e.target.value})}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="groep-zwaluw">🐦 Groep Zwaluw</option>
                  <option value="groep-adelaars">🦅 Groep Adelaars</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Prioriteit</label>
                <select
                  value={formData.prioriteit}
                  onChange={(e) => setFormData({...formData, prioriteit: e.target.value})}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="laag">Laag</option>
                  <option value="normaal">Normaal</option>
                  <option value="hoog">Hoog</option>
                  <option value="urgent">🚨 Urgent</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                Annuleren
              </Button>
              <Button onClick={handleSubmit} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white">
                {editingKlusje ? 'Opslaan' : 'Melden'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default {
  PBGesprekkenPage, WeekmenuPage, BoodschappenPage, CorveePage, HuisafsprakenPage, TeamuitjesPage,
  VergaderingenPage, FichesPage, TakenPage, ClientenPage, TeamoverlegPage, MDOPage, MedicatiePage, ChecklistPage, OverdrachtPage,
  WifiPage, AfvalkalenderPage, ZorgplanEvaluatiesPage, RIEEvaluatiesPage, RevalidatiePage, RevalidatieClientPage, EvaluatieOverzichtPage,
  RoosterPage, AfsprakenPage, KlusjesPage,
};

// Note: DagbestedingPage, EetmomentenPage en VoiceRapportagePage worden apart geëxporteerd via re-export statements bovenaan dit bestand
